/**
 * AutoInvestigateReport — Generates a self-contained HTML report in a new browser tab.
 *
 * Opens a new tab with a complete HTML document (embedded CSS, no Tailwind dependency).
 * This approach:
 *   1. Eliminates dark-mode text color inheritance (own document, own styles)
 *   2. Gives native browser print / "Save as PDF" support
 *   3. Can be bookmarked, shared, or downloaded directly
 *
 * Called from AutoInvestigateCard's "View LE Report" button.
 */
import type { InvestigationSections, InvestigationKey, CirculationResult, CirculationFinding } from '../../hooks/useAutoInvestigation';
import { cleanTokenSymbol } from '../../utils/safeStr';
import { normalizeGates } from '../report/sections/gates';
import {
    coverageStepLabel,
    normalizeCoverage,
    plainStepReason,
    stopReasonText,
    type InvestigationCoverage,
} from '../../utils/investigationCoverage';
import {
    esc,
    fmtNum,
    fmtCryptoVal,
    fmtTs,
    fmtTsCompact,
    truncAddr,
    monoWrap,
    monoSpan,
    cleanEntityName,
    fmt,
    pct,
    normalizeEntityType,
    riskBadgeStyle,
    isHighRiskCounterparty,
    counterpartyTierClass,
    counterpartyRowClass,
    formatNotTestedReason,
    renderProviderHealthBlock,
    renderStampedRowsLine,
    renderStampedCounterparties,
    formatJurisdiction,
    sortForLegalAction,
    sortForNarrativeImpact,
    selectNotableCallouts,
    wrapReportHtml,
    buildReportToolbar,
    buildReportShell,
    buildReportMasthead,
    openReportHtmlInNewTab,
    buildEvidenceTierLegend,
    buildTierTag,
    buildCollapsedCodeBlock,
    buildReportDisclaimer,
    type CounterpartyPrimitive,
    type NotableCallout,
    type ReportTocEntry,
} from '../../utils/reportCommon';
import { stampBadgeText } from '../../utils/stampedRows';
import { OFAC_INDICATOR_LINE } from '../../utils/sanctionsCopy';
import { describeSourcesRan, describeStepsRun, stepsRunSentence, NOTICE_STATUTE, CERTIFICATE_STATUTE, type StepsRun } from '../../utils/reportProvenance';
import { renderFlowDiagramFigure, FLOW_DIAGRAM_CSS } from '../report/flowDiagram';
import type { FlowDiagramIR } from '../report/types';

function openSec(id: string, title: string): string {
    return `<details class="report-section" id="${esc(id)}" open>`
        + `<summary class="report-section-summary"><span class="report-section-title">${title}</span></summary>`
        + `<div class="report-section-body">`;
}
function closeSec(): string {
    return `</div></details>`;
}

/* ── Helpers ── */

function renderRichValue(value: unknown, block = false): string {
    if (value === undefined || value === null) return '<span class="muted">—</span>';
    if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    // Catch strings that are already "[object Object]" from earlier serialization
    if (typeof value === 'string') {
        if (value.includes('[object Object]')) return esc('(complex data omitted)');
        return esc(value);
    }
    if (typeof value === 'object') {
        if (Array.isArray(value)) {
            const items = value.map((item: any) => {
                if (typeof item === 'object' && item !== null) {
                    return Object.entries(item)
                        .filter(([, v]) => v !== null && v !== undefined)
                        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? (Number.isInteger(v as number) ? v : (v as number).toFixed(2)) : typeof v === 'object' ? renderRichValue(v) : v}`)
                        .join(', ');
                }
                return String(item);
            });
            return block
                ? `<ul style="margin:2px 0 2px 12px;padding:0;list-style:disc;">${items.map(i => `<li style="font-size:9px;">${esc(i)}</li>`).join('')}</ul>`
                : esc(items.join('; '));
        }
        // Object: render as compact key-value pairs — recursively expand nested objects
        const entries = Object.entries(value as Record<string, unknown>)
            .filter(([, v]) => v !== null && v !== undefined);
        if (entries.length === 0) return '<span class="muted">—</span>';
        const parts = entries.map(([k, v]) => {
            const label = k.replace(/_/g, ' ');
            if (typeof v === 'number') return `${label}: ${Number.isInteger(v) ? v : v.toFixed(2)}`;
            if (typeof v === 'string') return `${label}: ${v}`;
            if (typeof v === 'boolean') return `${label}: ${v}`;
            // Recursively render nested objects/arrays instead of [object]/[items]
            if (typeof v === 'object') {
                if (Array.isArray(v)) {
                    if (v.length === 0) return `${label}: (none)`;
                    // Render each array item inline
                    const itemStrs = v.map((item: any) => {
                        if (typeof item === 'object' && item !== null) {
                            return Object.entries(item)
                                .filter(([, iv]) => iv !== null && iv !== undefined)
                                .map(([ik, iv]) => `${ik.replace(/_/g, ' ')}: ${typeof iv === 'number' ? (Number.isInteger(iv as number) ? iv : (iv as number).toFixed(2)) : typeof iv === 'object' ? renderRichValue(iv) : iv}`)
                                .join(', ');
                        }
                        return String(item);
                    });
                    if (block) {
                        return `${label}:<ul style="margin:2px 0 2px 12px;padding:0;list-style:disc;">${itemStrs.map(i => `<li style="font-size:9px;">${esc(i)}</li>`).join('')}</ul>`;
                    }
                    return `${label}: ${itemStrs.map(i => `[${i}]`).join('; ')}`;
                }
                // Nested object — recursively expand key-value pairs
                const nested = Object.entries(v as Record<string, unknown>)
                    .filter(([, nv]) => nv !== null && nv !== undefined)
                    .map(([nk, nv]) => {
                        const nLabel = nk.replace(/_/g, ' ');
                        if (typeof nv === 'number') return `${nLabel}: ${Number.isInteger(nv) ? nv : nv.toFixed(2)}`;
                        if (typeof nv === 'object' && nv !== null) return `${nLabel}: ${renderRichValue(nv)}`;
                        return `${nLabel}: ${nv == null ? '—' : String(nv)}`;
                    })
                    .join(', ');
                return `${label}: ${nested || '(empty)'}`;
            }
            return `${label}: ${v}`;
        });
        return block
            ? `<div style="font-size:9px;line-height:1.6;">${parts.map(p => `<div>${p.includes('<ul') ? p : esc(p)}</div>`).join('')}</div>`
            : esc(parts.join(' · '));
    }
    // Safety net: JSON.stringify anything that slipped through
    try { return esc(JSON.stringify(value)); } catch { return esc(String(value)); }
}

// fmt, pct, normalizeEntityType, riskBadgeStyle imported from reportCommon.ts

function isSpamCounterparty(cp: any): boolean {
    if (!cp) return false;
    if (cp.is_spam_token === true) return true;
    const tokenType = String(cp.token_type || '').toLowerCase();
    if (tokenType === 'spam') return true;
    return false;
}

/**
 * Resolve the canonical risk score for the report.
 *
 * The score is the maximum of the triage composite score and the investigation
 * score (signal_assessment). A sanctions hit on the subject floors it at the
 * HIGH band. When the investigation analysed no transactions and nothing else
 * supports a score, returns null so the report shows "INSUFFICIENT DATA"
 * instead of a misleading "0% LOW".
 */
export function resolveCanonicalRisk(
    sections: InvestigationSections,
    triage: any,
): {
    score: number | null;
    level: string;
    source: 'investigation' | 'triage' | 'behavioral_override' | 'sanctions_floor' | 'none';
    override_reasons?: string[];
} {
    const sa = sections?.signal_assessment;
    const saData = sa?.status === 'loaded' ? sa.data : null;
    const bandLevel = (v: number) => (v >= 0.7 ? 'HIGH' : v >= 0.4 ? 'MEDIUM' : 'LOW');

    const triageScore: number | null = typeof triage?.summary?.risk_score === 'number' ? triage.summary.risk_score : null;
    const hasInvScore = !!saData && typeof saData.investigation_risk_score === 'number';
    const invScore: number | null = hasInvScore ? saData.investigation_risk_score : null;
    const subjectSanctioned = triage?.summary?.sanctioned === true;

    // Investigation ran but analysed no transactions: the investigation score is
    // meaningless. Report insufficient data unless a sanctions hit or a non-default
    // triage score (MEDIUM band or above) says otherwise.
    const noTxAnalysed = hasInvScore && (saData.investigation_tx_count ?? 0) === 0 && invScore === 0;
    if (noTxAnalysed && !subjectSanctioned && !(triageScore != null && triageScore >= 0.4)) {
        return { score: null, level: 'INSUFFICIENT DATA', source: 'none' };
    }

    // Report risk = max(triage composite, investigation score).
    let score: number | null = null;
    let source: 'investigation' | 'triage' | 'behavioral_override' | 'sanctions_floor' | 'none' = 'none';
    let level = 'UNKNOWN';
    if (invScore != null && (triageScore == null || invScore >= triageScore)) {
        score = invScore; level = bandLevel(invScore); source = 'investigation';
    } else if (triageScore != null) {
        score = triageScore;
        level = triage?.summary?.risk_level || bandLevel(triageScore);
        source = 'triage';
    }

    // A sanctions hit on the subject floors the report at the HIGH band.
    const SANCTIONS_FLOOR = 0.7;
    if (subjectSanctioned && (score == null || score < SANCTIONS_FLOOR)) {
        return {
            score: SANCTIONS_FLOOR,
            level: 'HIGH',
            source: 'sanctions_floor',
            override_reasons: ['Sanctions match on the subject address: risk floored to the HIGH band regardless of behavioural scores.'],
        };
    }

    // Behavioral override (May-2026 LE audit fix). When the underlying
    // signals say "pass-through relay wallet at scale" but the composite
    // score is LOW because the address has no entity attribution, the LE
    // officer can be misled into deprioritising. Floor the score at MEDIUM
    // (or HIGH for stronger signals) and surface the override reasons.
    const override = computeBehavioralFloor(sections);
    if (override.floor > 0 && (score == null || override.floor > score)) {
        return {
            score: override.floor,
            level: override.floor >= 0.7 ? 'HIGH' : 'MEDIUM',
            source: 'behavioral_override',
            override_reasons: override.reasons,
        };
    }

    return { score, level, source };
}

/** Stablecoin tokens whose units approximate USD 1:1 — used for throughput
 *  thresholding without a price-feed dependency. */
const _STABLECOIN_SYMBOLS = new Set([
    'USDT', 'USDC', 'DAI', 'BUSD', 'TUSD', 'FDUSD', 'USDP', 'USDD',
]);

/** Pass-through-relay behavioral floor.
 *
 *  Triggers when behavioral signals collectively show the wallet is acting as
 *  a relay/launderer, even if the address itself has no entity attribution
 *  (which would otherwise drive composite risk to ~10% LOW). Surfaces the
 *  reasons so the LE officer can see WHY the score was bumped.
 */
function computeBehavioralFloor(
    sections: InvestigationSections,
): { floor: number; reasons: string[] } {
    const reasons: string[] = [];
    const vel = sections?.velocity?.status === 'loaded' ? sections.velocity.data : null;
    if (!vel) return { floor: 0, reasons };

    const ftr = (vel.metrics?.fund_through_rate ?? (vel as any).fund_through_rate) as number | undefined;
    if (typeof ftr !== 'number' || ftr < 0.95) return { floor: 0, reasons };

    const cpSec = sections?.counterparty_triage;
    const cpData = cpSec?.status === 'loaded' ? cpSec.data : null;
    const cps: any[] = Array.isArray(cpData) ? cpData : ((cpData as any)?.counterparties ?? []);
    const uniqueReceivers = cps.length;
    if (uniqueReceivers < 5) return { floor: 0, reasons };

    // Sum stablecoin volume across direct counterparties — units approximate
    // USD 1:1, so we can threshold without a live price feed.
    let stableVolUsd = 0;
    for (const c of cps) {
        const tok = String(c.primary_token || '').toUpperCase();
        const vol = Number(c.primary_volume) || 0;
        if (_STABLECOIN_SYMBOLS.has(tok)) stableVolUsd += vol;
    }
    if (stableVolUsd <= 50_000) return { floor: 0, reasons };

    reasons.push(
        `Behavioral override: pass-through pattern detected — fund-through-rate ${(ftr * 100).toFixed(0)}%, ` +
        `${uniqueReceivers} direct counterparties, ~$${(stableVolUsd / 1000).toFixed(0)}K stablecoin throughput. ` +
        `Composite risk score floored to MEDIUM regardless of root entity attribution.`,
    );

    // Stronger signal → HIGH floor. Many counterparties + high throughput is
    // categorical relay behaviour, not a "monitor" case.
    if (uniqueReceivers >= 10 && stableVolUsd > 100_000) {
        reasons[0] = reasons[0].replace('floored to MEDIUM', 'floored to HIGH');
        return { floor: 0.7, reasons };
    }
    return { floor: 0.5, reasons };
}

/** Lightweight markdown to HTML for AI narrative text */
function mdToHtml(text: string): string {
    return esc(text)
        .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 4px;font-size:13px;font-weight:700;">$1</h4>')
        .replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 6px;font-size:14px;font-weight:700;border-bottom:1px solid #e2e8f0;padding-bottom:4px;">$1</h3>')
        .replace(/^# (.+)$/gm, '<h2 style="margin:20px 0 8px;font-size:16px;font-weight:700;border-bottom:2px solid #cbd5e1;padding-bottom:6px;">$1</h2>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/^- (.+)$/gm, '<li style="margin-left:16px;margin-bottom:2px;">$1</li>')
        .replace(/\n\n/g, '</p><p style="margin:8px 0;">')
        .replace(/\n/g, '<br/>');
}

/* ── Evidence Numbering ── */

/** Sequential evidence counter — generates E-1, E-2, etc. for court-admissible referencing */
class EvidenceCounter {
    private _count = 0;
    next(): string {
        this._count++;
        return `<span class="evidence-id">E-${this._count}</span>`;
    }
    get count(): number {
        return this._count;
    }
    reset(): void {
        this._count = 0;
    }
}

/* ── CSS ── */

/** Investigation-only styles layered on shared REPORT_CSS (do not re-define shell/toolbar/print chrome). */
const INVESTIGATION_EXTRA_CSS = `
${FLOW_DIAGRAM_CSS}
.assessment-metrics { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; margin-top: 8px; }
.assessment-metrics span { font-size: 13px; color: #475569; }
.data-freshness {
    font-size: 10px; color: #64748b; font-weight: 400; text-transform: none;
    letter-spacing: 0; margin-bottom: 6px; font-style: italic;
}
.data-freshness.stale { color: #b45309; font-weight: 600; }
table.data th.center { text-align: center; }
table.data td.center { text-align: center; }
.risk-badge {
    display: inline-block; padding: 2px 8px; border-radius: 3px;
    font-size: 11px; font-weight: 700; text-transform: uppercase;
}
.tier-high { background: #fef2f2; color: #991b1b; }
.tier-medium { background: #fffbeb; color: #92400e; }
.tier-low { background: #f1f5f9; color: #475569; }
.tier-unknown { background: #fef3c7; color: #92400e; font-style: italic; }
.velocity-relay { background: #fef2f2; color: #991b1b; border: 1px solid #fca5a5; }
.velocity-distributor { background: #fffbeb; color: #92400e; border: 1px solid #fcd34d; }
.velocity-accumulator { background: #eff6ff; color: #1e40af; border: 1px solid #93c5fd; }
.velocity-default { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
.narrative {
    background: #f8fafc; border: 1px solid #e2e8f0; border-left: 3px solid #3b82f6;
    border-radius: 4px; padding: 16px; font-size: 13px; line-height: 1.7; color: #334155;
}
.narrative h3, .narrative h4 { color: #1e293b; }
.narrative strong { color: #0f172a; }
.narrative li { color: #334155; }
.error-box {
    background: #fef2f2; border: 1px solid #fecaca; border-radius: 4px;
    padding: 10px 14px; font-size: 12px; color: #991b1b;
}
.path-box {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px;
    padding: 8px; margin-bottom: 6px;
}
.path-addr { font-size: 11px; color: #334155; }
.path-arrow { color: #94a3b8; margin: 0 4px; }
.path-entity { font-size: 12px; font-weight: 600; color: #2563eb; margin-top: 3px; }
.pattern-tag {
    display: inline-block; padding: 2px 8px; border-radius: 3px;
    font-size: 11px; font-weight: 500; background: #fffbeb; color: #92400e;
    border: 1px solid #fcd34d; margin: 2px 2px;
}
/* .disclaimer lives in the shared REPORT_CSS (reportCommon.ts). */
.appendix-divider {
    margin: 40px 0 24px; padding: 12px 0 8px; border-top: 3px double #cbd5e1;
    text-align: center; font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 2px; color: #64748b;
}
.appendix { margin-bottom: 20px; }
.appendix h3 {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 1px; color: #475569; margin-bottom: 6px;
}
.appendix p.note { font-size: 10px; color: #94a3b8; margin-bottom: 6px; font-style: italic; }
table.data-full {
    width: 100%; border-collapse: collapse; font-size: 11px;
    border: 1px solid #cbd5e1; table-layout: fixed;
}
table.data-full th {
    text-align: left; padding: 4px 8px; background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1; font-weight: 600; color: #334155;
    font-size: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
table.data-full td {
    padding: 3px 8px; border-bottom: 1px solid #e2e8f0; color: #334155;
    font-size: 10px; overflow-wrap: anywhere; word-break: normal; overflow: hidden;
}
table.data-full td.mono { font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace; }
table.data-full td.muted { color: #94a3b8; }
table.data-full td.right { text-align: right; }
table.data-full tr:nth-child(even) { background: #f8fafc; }
.json-inline {
    white-space: pre-wrap; font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    font-size: 9px; line-height: 1.35; color: #475569;
}
.json-block {
    margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; word-break: normal;
    font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    font-size: 9px; line-height: 1.35; color: #334155;
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px;
    padding: 4px 6px; max-height: 220px; overflow: auto;
}
.demo-watermark {
    background: #fef3c7; border: 2px solid #f59e0b; border-radius: 6px;
    padding: 10px 24px; margin: 8px 24px; text-align: center;
    font-weight: 700; font-size: 14px; color: #92400e;
    letter-spacing: 1px; text-transform: uppercase;
}
.demo-watermark-page {
    background: #fef3c7; border: 2px dashed #f59e0b; border-radius: 6px;
    padding: 8px 16px; margin-bottom: 16px; text-align: center;
    font-weight: 700; font-size: 13px; color: #92400e;
}
.cert-box {
    border: 2px solid #1e293b; border-radius: 6px; padding: 24px;
    margin-top: 16px; background: #fff;
}
.cert-box h3 { font-size: 13px; font-weight: 700; text-align: center; margin-bottom: 16px; text-transform: uppercase; letter-spacing: 1px; }
.cert-box p { font-size: 11px; line-height: 1.8; color: #334155; margin-bottom: 8px; }
.cert-field {
    display: inline-block; min-width: 200px; border-bottom: 1px solid #94a3b8;
    padding: 2px 4px; margin: 0 4px; font-style: italic; color: #64748b;
}
.cert-signature-block { margin-top: 24px; display: flex; gap: 48px; flex-wrap: wrap; }
.cert-signature-block div { flex: 1; min-width: 200px; }
.cert-signature-block .sig-line { border-bottom: 1px solid #334155; margin-top: 40px; margin-bottom: 4px; }
.cert-signature-block .sig-label { font-size: 10px; color: #64748b; }
@media print {
    .path-box, .assessment-box, .cert-box, .appendix { break-inside: avoid; }
    .appendix-divider { page-break-before: always; }
    .json-block { max-height: none !important; overflow: visible !important; }
    table.data-full { table-layout: auto !important; }
    div[style*="overflow-x:auto"] { overflow: visible !important; }
    div[style*="max-height"] { max-height: none !important; overflow: visible !important; }
    .demo-watermark { display: none !important; }
    .demo-watermark-page { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    @page {
        size: A4; margin: 20mm 15mm 25mm 15mm;
        @bottom-center {
            content: "Page " counter(page); font-size: 9px; color: #94a3b8;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
    }
}
`;

/* ── Section renderers (return HTML strings) ── */

/**
 * The model-written narrative, with its inline tag. The tag sits on the narrative itself (not only in
 * Methodology and the 65B text), because the text is the one place in the report a reader meets
 * model-generated prose next to computed facts. A narrative the server rejected arrives as no text.
 */
export function renderAiNarrative(analysis: unknown, style = ''): string {
    if (typeof analysis !== 'string' || !analysis.trim()) return '';
    return `<div class="narrative" data-testid="ai-narrative"${style ? ` style="${style}"` : ''}>`
        + `<p style="margin-bottom:6px;" data-testid="ai-narrative-tag">${buildTierTag(3)} `
        + `<strong>AI-generated.</strong> <span style="font-size:11px;color:#64748b;">Wallet references were put back by the platform; `
        + `the text was checked against the evidence sent, and it is a hypothesis for the officer to verify, not a finding.</span></p>`
        + `${mdToHtml(analysis)}</div>`;
}

function renderSignalAssessment(data: any, ev?: EvidenceCounter): string {
    if (!data) return '';

    // Check for structured signal families first
    const families = data.signal_families as Record<string, any> | undefined;

    if (!families) {
        // AI narrative text (Theory A / Theory B format from /explain)
        if (data.analysis) {
            return renderAiNarrative(data.analysis);
        }
        return '<p style="color:#94a3b8;font-size:12px;">Assessment data not structured.</p>';
    }

    let html = '';

    // Summary line
    const ft = data.families_triggered ?? '?';
    const tt = data.families_tested ?? (data.signal_families ? Object.keys(data.signal_families).length : 8);
    const esc_rec = data.escalation_recommendation;
    html += `<div style="margin-bottom:10px;display:flex;align-items:center;gap:12px;">`;
    html += `<span><strong>${ft}</strong> of <strong>${tt}</strong> signal families triggered</span>`;
    if (esc_rec) {
        const cls = esc_rec === 'ESCALATE' ? 'tier-high'
            : esc_rec === 'INVESTIGATE' ? 'tier-medium'
            : esc_rec === 'INSUFFICIENT_DATA' ? 'tier-unknown'
            : 'tier-low';
        html += `<span class="risk-badge ${cls}">${esc(esc_rec.replace(/_/g, ' '))}</span>`;
    }
    html += `</div>`;

    // Families table
    html += `<table class="data"><thead><tr>
        <th>Signal Family</th><th class="center" style="width:90px;">Status</th><th>Key Signals</th>
    </tr></thead><tbody>`;
    for (const [name, info] of Object.entries(families) as [string, any][]) {
        const statusCls = info.triggered === true ? 'tier-high' : info.triggered === false ? 'tier-low' : 'tier-unknown';
        const statusText = info.triggered === true ? 'TRIGGERED' : info.triggered === false ? 'CLEAR' : 'NOT TESTED';
        const signals = info.triggered === true && info.signals?.length > 0
            ? esc(info.signals.slice(0, 4).map((s: any) => typeof s === 'string' ? s : JSON.stringify(s)).join(', '))
            : '—';
        // Add evidence ID for sanctions-related triggered families
        const sanctionsEid = (ev && info.triggered === true && /sanction/i.test(name)) ? ev.next() : '';
        html += `<tr>
            <td style="text-transform:capitalize;">${sanctionsEid}${esc(name.replace(/_/g, ' '))}</td>
            <td class="center"><span class="risk-badge ${statusCls}">${statusText}</span></td>
            <td style="color:#64748b;">${signals}</td>
        </tr>`;
    }
    html += `</tbody></table>`;

    // Contradictions
    if (data.score_contradictions?.length > 0) {
        const text = data.score_contradictions
            .map((c: any) => esc(c?.description || (typeof c === 'string' ? c : JSON.stringify(c))))
            .join('; ');
        html += `<div style="margin-top:8px;padding:8px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;font-size:12px;color:#92400e;">
            <strong>Contradictions:</strong> ${text}</div>`;
    }

    // AI narrative (Theory A / Theory B) — render alongside signal families
    if (data.analysis) {
        html += renderAiNarrative(data.analysis, 'margin-top:12px;');
    }

    return html;
}

function renderVelocity(data: any): string {
    if (!data) return '';

    // Metrics are nested under data.metrics (backend velocity_analyzer.py line 128-135)
    const m = data.metrics || {};
    const metrics = [
        { label: 'Fund-Through Rate', desc: 'Outflow / inflow ratio — 1.0 = pass-through, >1 = distributing', value: m.fund_through_rate ?? data.fund_through_rate },
        { label: 'Consolidation Ratio', desc: 'Fraction of txs with 5+ inputs — high = aggregation behavior', value: m.consolidation_ratio ?? data.consolidation_ratio },
        { label: 'Value Amplification', desc: 'Largest tx / median tx value — high = whale-scale outliers', value: m.value_amplification ?? data.value_amplification },
        { label: 'Dwell Time (hours)', desc: 'Median hours funds stay before moving out — low = rapid relay', value: m.dwell_time_hours ?? data.dwell_time_hours },
        { label: 'Regularity Score', desc: 'Daily tx count consistency (CoV) — low = erratic/manual', value: m.regularity_score ?? data.regularity_score },
        { label: 'Burst Score', desc: 'Peak day / average day — high = sudden activity spikes', value: m.burst_score ?? data.burst_score },
    ];

    let html = '<div style="display:flex;gap:10px;align-items:center;margin-bottom:10px;">';

    if (data.velocity_profile) {
        const cls = data.velocity_profile === 'Relay' ? 'velocity-relay'
            : data.velocity_profile === 'Distributor' ? 'velocity-distributor'
            : data.velocity_profile === 'Accumulator' ? 'velocity-accumulator'
            : 'velocity-default';
        html += `<span class="risk-badge ${cls}">${esc(data.velocity_profile)}</span>`;
    }
    if (typeof data.velocity_risk_score === 'number') {
        html += `<span class="risk-badge" style="${riskBadgeStyle(data.velocity_risk_score)}">Risk: ${pct(data.velocity_risk_score)}</span>`;
    }
    html += '</div>';

    html += `<table class="data"><thead><tr>
        <th>Metric</th><th style="width:280px;">Description</th><th class="right" style="width:100px;">Value</th>
    </tr></thead><tbody>`;
    for (const met of metrics) {
        html += `<tr><td>${esc(met.label)}</td><td style="font-size:10px;color:#64748b;">${esc(met.desc)}</td><td class="right mono">${typeof met.value === 'number' ? fmtNum(met.value) : '—'}</td></tr>`;
    }
    html += '</tbody></table>';

    // Explain why regularity/burst are unavailable (V4-M3 + 2026-05-05 refactor).
    // The backend now ships analysis_window metadata (distinct_days,
    // regularity_computable, dwell_computable, notes). When present, use it
    // to compose precise copy that names the actual constraint instead of
    // guessing. Falls back to the legacy single-day message if the metadata
    // is absent (older payloads).
    const regNull = (m.regularity_score ?? data.regularity_score) == null;
    const burstNull = (m.burst_score ?? data.burst_score) == null;
    const dwellNull = (m.dwell_time_hours ?? data.dwell_time_hours) == null;
    const win = data.analysis_window;
    if (win) {
        const issues: string[] = [];
        if ((regNull || burstNull) && !win.regularity_computable) {
            const missing = [regNull && 'Regularity Score', burstNull && 'Burst Score'].filter(Boolean).join(' and ');
            issues.push(
                `${missing} ${regNull && burstNull ? 'require' : 'requires'} activity across at least 2 distinct calendar days. ` +
                `This address's analyzed activity (${win.total_txs} tx${win.total_txs === 1 ? '' : 's'}) ` +
                `falls within ${win.distinct_days || 0} calendar day${win.distinct_days === 1 ? '' : 's'}` +
                `${win.span_days > 0 ? ` over a span of ${win.span_days} day${win.span_days === 1 ? '' : 's'}` : ''}, ` +
                `so coefficient-of-variation is mathematically null (not a missing measurement).`
            );
        }
        if (dwellNull && !win.dwell_computable) {
            issues.push(
                `Dwell Time requires both incoming and outgoing transactions in the analyzed set. ` +
                `Without paired receive→send events, median gap is uncomputable (not missing).`
            );
        }
        if (issues.length > 0) {
            html += `<p style="font-size:10px;color:#94a3b8;margin-top:4px;font-style:italic;">${esc(issues.join(' '))}</p>`;
        }
    } else if (regNull || burstNull) {
        // Legacy fallback for payloads without analysis_window
        const missing = [regNull && 'Regularity Score', burstNull && 'Burst Score'].filter(Boolean).join(' and ');
        html += `<p style="font-size:10px;color:#94a3b8;margin-top:4px;font-style:italic;">${missing} require${regNull && burstNull ? '' : 's'} activity across multiple days to compute. This address's analyzed transactions fall within a single day.</p>`;
    }

    return html;
}

export function renderCounterparties(data: any, ev?: EvidenceCounter): string {
    const rawList = (data?.counterparties || data?.ranked || []) as any[];
    const spamCount = rawList.filter((c: any) => isSpamCounterparty(c)).length;
    const list = rawList.filter((c: any) => !isSpamCounterparty(c));
    if (list.length === 0) {
        if (spamCount > 0) {
            return `<p style="color:#92400e;font-size:12px;">All detected counterparties were classified as spam-token airdrops (${spamCount}) and excluded.</p>`;
        }
        return '<p style="color:#94a3b8;font-size:12px;">No counterparties found.</p>';
    }

    const highRisk = list.filter(isHighRiskCounterparty).length;
    let html = `<p style="margin-bottom:8px;"><strong>${list.length}</strong> counterparties ranked by transaction volume`;
    if (highRisk > 0) html += ` — <span style="color:#991b1b;font-weight:600;">${highRisk} high-risk</span>`;
    html += '</p>';

    html += `<table class="data"><thead><tr>
        <th style="width:55px;">Ref</th><th>Address</th><th style="width:140px;">Entity</th><th class="right" style="width:110px;">Volume</th><th class="center" style="width:90px;">Risk Tier</th>
    </tr></thead><tbody>`;
    const mainBodyLimit = 25;
    for (let i = 0; i < Math.min(list.length, mainBodyLimit); i++) {
        const cp = list[i];
        const rowCls = counterpartyRowClass(cp);
        const tierCls = counterpartyTierClass(cp.risk_tier);
        const vol = cp.primary_volume != null && cp.primary_token
            ? `${fmtCryptoVal(cp.primary_volume)} ${esc(cp.primary_token)}`
            : typeof cp.total_volume === 'number' ? fmtCryptoVal(cp.total_volume) : typeof cp.volume === 'number' ? fmtCryptoVal(cp.volume) : esc(String(cp.volume ?? '—'));
        const entityName = cp.entity_name && cp.entity_name !== 'Unknown' ? cp.entity_name : (cp.entity && cp.entity !== 'Unknown' ? cp.entity : (cp.name && cp.name !== 'Unknown' ? cp.name : ''));
        const eid = ev ? ev.next() : `<span class="muted">${i + 1}</span>`;
        html += `<tr${rowCls}>
            <td>${eid}</td>
            <td class="mono" style="font-size:11px;">${esc(cp.address || '?')}${stampBadgeText(cp) ? ` <strong style="color:#92400e;">${esc(stampBadgeText(cp))}</strong>` : ''}</td>
            <td style="font-size:11px;">${entityName ? esc(cleanEntityName(entityName)) : '<span class="muted">—</span>'}</td>
            <td class="right mono">${vol}</td>
            <td class="center"><span class="risk-badge ${tierCls}">${esc(cp.risk_tier || cp.entity_type || 'unknown')}</span></td>
        </tr>`;
    }
    html += '</tbody></table>';
    html += renderStampedCounterparties(data?.stamped_counterparties);

    if (list.length > mainBodyLimit) {
        html += `<p style="font-size:11px;color:#94a3b8;margin-top:4px;">Showing top ${mainBodyLimit} of ${list.length} counterparties — see appendix for full list.</p>`;
    }
    if (spamCount > 0) {
        html += `<p style="font-size:11px;color:#92400e;margin-top:4px;">${spamCount} spam-token airdrop counterpart${spamCount > 1 ? 'ies were' : 'y was'} excluded from this table.</p>`;
    }

    return html;
}

const HOP_BADGE_COLORS: Record<string, string> = {
    relay: '#f59e0b', service: '#22c55e', storage: '#3b82f6',
    intermediary: '#94a3b8', collector: '#a855f7', distributor: '#f97316',
};
const HOP_BADGE_ICONS: Record<string, string> = {
    relay: '⚡', service: '🏦', storage: '💾',
    intermediary: '🔄', collector: '📥', distributor: '📤',
};

function formatDwellHtml(seconds: number | null | undefined): string {
    if (seconds == null) return '';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
    return `${(seconds / 86400).toFixed(1)}d`;
}

function renderHopBadgesHtml(hopDetails: any[]): string {
    if (!hopDetails || hopDetails.length === 0) return '';
    let html = '<div style="display:flex;gap:4px;flex-wrap:wrap;margin-top:4px;">';
    for (const hd of hopDetails) {
        const cls = hd.classification || 'intermediary';
        const color = HOP_BADGE_COLORS[cls] || '#94a3b8';
        const icon = HOP_BADGE_ICONS[cls] || '🔄';
        const dwell = formatDwellHtml(hd.dwell_time_seconds);
        const label = hd.entity_name ? esc(cleanEntityName(hd.entity_name)) : `${esc(hd.address?.slice(0, 6))}...`;
        html += `<span style="display:inline-flex;align-items:center;gap:2px;padding:1px 6px;border-radius:4px;font-size:10px;background:${color}15;color:${color};border:1px solid ${color}40;">`;
        html += `${icon} ${label}`;
        if (dwell) html += ` <span style="opacity:0.7">(${dwell})</span>`;
        html += '</span>';
    }
    html += '</div>';
    return html;
}

function renderTraceExplorationSummary(expSummary: any, elapsed?: number): string {
    if (!expSummary) return '';
    const parts: string[] = [];
    if (expSummary.total_nodes_explored > 0) parts.push(`${expSummary.total_nodes_explored} addresses`);
    if (expSummary.bfs_depth_reached > 0) parts.push(`across ${expSummary.bfs_depth_reached} hops`);
    if (elapsed != null && elapsed > 0) parts.push(`in ${elapsed}s`);
    if (parts.length === 0) return '';
    let html = `<p style="font-size:11px;color:#64748b;margin-bottom:6px;">Exploration: traced ${parts.join(', ')}.`;
    if (expSummary.entities_found > 0) {
        html += ` <strong>${expSummary.entities_found}</strong> entity/entities identified.`;
    }
    const stopLabels: Record<string, string> = {
        time_budget: 'Time budget reached',
        limit_reached: 'Address limit (200) reached',
        no_entity_coverage: 'Entity DB coverage insufficient',
        stop_entity: 'Target entity found',
        no_inbound_transactions: 'No inbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
        no_outbound_transactions: 'No outbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
        root_page_unavailable: 'The transactions of the subject could not be read, so the trace did not run (not a finding of no activity)',
        depth: 'Max hop depth reached',
        complete: 'Complete',
    };
    if (expSummary.stop_reason && expSummary.stop_reason !== 'complete' && expSummary.stop_reason !== 'stop_entity') {
        html += ` <span style="color:#d97706;font-style:italic;">(${stopLabels[expSummary.stop_reason] || expSummary.stop_reason})</span>`;
    }
    html += '</p>';
    return html;
}

// Q5: Shared trace renderer — eliminates ~90% duplication between backward/forward trace
function renderTrace(data: any, direction: 'backward' | 'forward', ev?: EvidenceCounter): string {
    const isBackward = direction === 'backward';
    const dirLabel = isBackward ? 'Backward' : 'Forward';
    const entityLabel = isBackward ? 'Source' : 'Destination';
    const pathLabel = isBackward ? 'fund-source' : 'destination';
    const entitiesKey = isBackward ? 'source_entities' : 'target_entities';
    const noEntityExplanation = isBackward
        ? 'This may indicate the funds originated from a personal wallet, P2P transfer, or an entity not in the database. '
        : 'Funds may have been sent to a personal wallet, cold storage, or an entity not in the database. ';

    const paths = (data?.paths || data?.traces || []) as any[];
    const expSummary = data?.exploration_summary;
    const elapsed = data?.elapsed_seconds ?? data?.progress?.elapsed_seconds;

    // Scope summary — total nodes, edges, hops traced
    const totalNodes = data?.total_nodes ?? (data?.nodes || []).length;
    const totalEdges = data?.total_edges ?? (data?.edges || []).length;
    const maxHops = data?.max_hops_reached ?? data?.depth_searched ?? null;
    let scopeHtml = '';
    if (totalNodes > 0 || totalEdges > 0) {
        const parts: string[] = [];
        if (totalNodes > 0) parts.push(`${totalNodes} addresses traced`);
        if (totalEdges > 0) parts.push(`${totalEdges} transactions`);
        if (maxHops != null) parts.push(`up to ${maxHops} hops`);
        scopeHtml = `<p style="font-size:11px;color:#64748b;margin-bottom:8px;font-style:italic;">Scope: ${parts.join(', ')}</p>`;
    }

    // Exploration summary (from BFS metadata)
    scopeHtml += renderTraceExplorationSummary(expSummary, elapsed);

    if (paths.length === 0) {
        let html = scopeHtml;
        // No paths and no record of any exploration: the trace did not run (or
        // returned nothing to judge by). That is not a finding of "no paths".
        const traceRan = !!expSummary
            && (expSummary.total_nodes_explored > 0 || !!expSummary.stop_reason || totalNodes > 0);
        if (!traceRan && !(data?.[entitiesKey]?.length > 0)) {
            return html
                + `<p style="color:#92400e;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;padding:8px 12px;font-size:12px;">`
                + `${dirLabel} trace did not run, or returned no exploration record. No addresses were explored, so this is not a finding that no ${pathLabel} paths exist.</p>`;
        }
        // Distinguish: exhausted budget with partial data vs truly empty
        if (expSummary && expSummary.total_nodes_explored > 0) {
            const reason = expSummary.stop_reason;
            if (reason === 'time_budget' || reason === 'limit_reached' || reason === 'no_entity_coverage') {
                html += '<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;padding:8px 12px;margin-bottom:8px;">';
                html += `<p style="color:#92400e;font-size:12px;font-weight:600;margin:0 0 4px;">Exploration Exhausted — No ${entityLabel} Entity Found</p>`;
                html += `<p style="color:#78350f;font-size:11px;margin:0;">${dirLabel} trace explored ${expSummary.total_nodes_explored} addresses across ${expSummary.bfs_depth_reached} hops`;
                if (elapsed != null) html += ` in ${elapsed}s`;
                html += ' but did not reach a known exchange or VASP within the search budget. ';
                html += noEntityExplanation;
                html += 'Re-running after cache warm-up or with increased hop depth may yield results.</p>';
                html += '</div>';
            } else {
                html += `<p style="color:#64748b;font-size:12px;">No ${pathLabel} paths found within hop limit.</p>`;
            }
        } else {
            html += `<p style="color:#64748b;font-size:12px;">No ${pathLabel} paths found within hop limit.</p>`;
        }
        const entities = data?.[entitiesKey];
        if (entities?.length > 0) {
            html += `<p style="font-size:11px;color:#059669;margin-top:4px;">BFS discovered ${entities.length} entity/entities (Neo4j path extraction pending):</p>`;
            html += '<ul style="font-size:11px;color:#059669;margin:2px 0 0 16px;">';
            for (const ent of entities.slice(0, 10)) {
                html += `<li class="mono">${esc(cleanEntityName(ent.name || ent.address || '—'))} (${esc(ent.type || 'unknown')})</li>`;
            }
            html += '</ul>';
        }
        return html;
    }

    let html = scopeHtml;
    html += `<p style="margin-bottom:8px;"><strong>${paths.length}</strong> ${pathLabel} path${paths.length !== 1 ? 's' : ''} identified</p>`;

    for (let i = 0; i < Math.min(paths.length, 8); i++) {
        const path = paths[i];
        const addrs = path.addresses || path.path || [];
        // For backward trace: reverse path so arrows show fund-flow direction
        // (Source Entity → ... → Target Address) instead of trace direction.
        // Edge data stays unchanged — only the visual path order is reversed.
        const displayAddrs = isBackward ? [...addrs].reverse() : addrs;
        const arrow = isBackward ? '&rarr;' : '&rarr;';
        const dirNote = isBackward ? '<span style="font-size:9px;color:#94a3b8;margin-left:6px;">(fund-flow direction)</span>' : '';
        const eid = ev ? ev.next() : '';
        html += `<div class="path-box">${eid}<div class="mono path-addr">`;
        for (let ai = 0; ai < displayAddrs.length; ai++) {
            if (ai > 0) html += `<span class="path-arrow">${arrow}</span>`;
            const addr = displayAddrs[ai];
            const display = addr.length > 16 ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : addr;
            html += `<span title="${esc(addr)}">${esc(display)}</span>`;
        }
        html += `${dirNote}</div>`;
        html += renderHopBadgesHtml(path.hop_details);
        const ent = path.target_entity || {};
        const entName = ent.name || ent.entity_name || path.entity_name;
        const entType = ent.type || ent.entity_type || path.entity_type || 'unknown';
        if (entName) {
            html += `<div class="path-entity">${esc(cleanEntityName(entName))} (${esc(entType)})</div>`;
        }
        // V4-M1: Show origin value (first edge from root) vs terminal value
        // to prevent investigators from confusing intermediate entity's own
        // activity with the actual fund flow from the target address
        const pathEdges = path.edges || [];
        if (pathEdges.length >= 2) {
            const firstVal = Number(pathEdges[0]?.value || 0);
            const lastVal = Number(pathEdges[pathEdges.length - 1]?.value || 0);
            if (firstVal > 0 && lastVal > 0 && lastVal > firstVal * 5) {
                html += `<div style="font-size:10px;color:#d97706;margin-top:2px;padding:2px 8px;background:#fffbeb;border-radius:3px;">`;
                html += `Note: Origin edge ${fmtCryptoVal(firstVal)} from target address; terminal edge ${fmtCryptoVal(lastVal)} — terminal value likely reflects independent activity of an intermediate address, not fund flow from the target.`;
                html += `</div>`;
            }
        }
        html += '</div>';
    }

    if (paths.length > 8) {
        html += `<p style="font-size:11px;color:#94a3b8;">+${paths.length - 8} more paths (not shown).</p>`;
    }

    return html;
}

function renderBackwardTrace(data: any, ev?: EvidenceCounter): string {
    return renderTrace(data, 'backward', ev);
}

function renderForwardTrace(data: any, ev?: EvidenceCounter): string {
    return renderTrace(data, 'forward', ev);
}

function renderMultiHopExposure(data: any, traceEntitiesOrEv?: { name: string; type: string; direction: string; hops?: number }[] | EvidenceCounter): string {
    // SECTIONS array passes EvidenceCounter but the special call site at L2500 passes traceEntities[]
    const traceEntities = Array.isArray(traceEntitiesOrEv) ? traceEntitiesOrEv : undefined;
    if (!data) return '';

    let html = '';

    // NOT TESTED handling — when the upstream signal-assembler exposure family
    // returned triggered=null (e.g. 15s timeout from /explain), the section
    // payload may carry the structured reason. Surface it as the headline so
    // the officer sees "exceeded budget" not just an empty section.
    if (data.not_tested_reason) {
        html += `<div style="padding:8px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;margin-bottom:10px;font-size:11px;color:#92400e;">
            ${esc(formatNotTestedReason(data.not_tested_reason, 'Multi-Hop Exposure'))}
        </div>`;
    }

    // V3-H1: Show partial/timed-out banner when multi-hop was interrupted
    if (data.timed_out || data.status === 'partial') {
        const hopsCompleted = data.hops_completed ?? '?';
        const timeoutSec = data.timeout_after_s ? ` after ${Math.round(data.timeout_after_s)}s` : '';
        html += `<div style="padding:8px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;margin-bottom:10px;font-size:11px;color:#92400e;">
            <strong>Partial Results</strong> — Analysis timed out${timeoutSec}. Completed ${hopsCompleted} of requested hops. Direct exposure data (hop 1) is shown below; deeper hops may be incomplete.
        </div>`;
    }

    const hops = data.unknown_pct_by_hop || {};
    const direct = data.direct_exposure || {};
    const indirect = data.indirect_exposure || {};

    if (typeof data.combined_risk_score === 'number') {
        html += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <span>Combined Risk Score:</span>
            <span class="risk-badge" style="${riskBadgeStyle(data.combined_risk_score)}">${pct(data.combined_risk_score)}</span>
        </div>`;
    }

    const hopKeys = Object.keys(hops).sort((a, b) => +a - +b);
    if (hopKeys.length > 0) {
        html += `<table class="data" style="margin-bottom:12px;"><thead><tr>
            <th>Hop</th><th class="right" style="width:120px;">Unattributed %</th><th class="right" style="width:130px;">Entity Coverage</th>
        </tr></thead><tbody>`;

        // Extract counterparty count and entity count from direct/indirect exposure
        const directSrc = (direct.source || []) as any[];
        const directDst = (direct.destination || []) as any[];
        const allDirect = [...directSrc, ...directDst];
        const hop1Total = allDirect.reduce((sum: number, e: any) => sum + (e.address_count || 0), 0);
        const hop1Unknown = allDirect.filter((e: any) => (e.category || '').toLowerCase() === 'unknown')
            .reduce((sum: number, e: any) => sum + (e.address_count || 0), 0);
        const hop1Identified = Math.max(hop1Total - hop1Unknown, 0);

        for (const hop of hopKeys) {
            const val = hops[hop];
            const display = typeof val === 'number' ? `${val.toFixed(1)}%` : esc(String(val));
            // For hop 1, show entity coverage count
            let coverageStr = '—';
            if (hop === '1' && hop1Total > 0) {
                coverageStr = `${hop1Identified} of ${hop1Total} identified`;
            } else if (hop !== '1' && Array.isArray(indirect)) {
                const hopEntries = (indirect as any[]).filter((e: any) => String(e.hop) === hop);
                const hopKnown = hopEntries.filter((e: any) => (e.category || '').toLowerCase() !== 'unknown').length;
                if (hopEntries.length > 0) {
                    coverageStr = `${hopKnown} attributed entries`;
                }
            }
            html += `<tr><td>Hop ${esc(hop)}</td><td class="right mono">${display}</td><td class="right" style="font-size:10px;color:#64748b;">${coverageStr}</td></tr>`;
        }
        html += '</tbody></table>';
        html += '<p style="font-size:9px;color:#94a3b8;margin-top:2px;">Unattributed % = proportion of <em>transaction volume</em> sent to/from addresses not in the entity database. Entity Coverage = count of unique counterparty addresses identified. A low unattributed % with low entity coverage means a small number of identified entities account for the majority of volume.</p>';

        // Coverage warning when hop 1 has very high unattributed %
        const hop1Pct = typeof hops['1'] === 'number' ? hops['1'] : null;
        if (hop1Pct !== null && hop1Pct > 80) {
            html += `<div style="margin-top:8px;padding:8px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;font-size:11px;color:#92400e;">
                <strong>Low Entity Coverage:</strong> ${hop1Pct.toFixed(0)}% of direct counterparties (hop 1) are unattributed.
                Risk score reflects limited data visibility, not confirmed low risk. Unattributed addresses may include
                exchanges, services, or other entities not yet in the database.
            </div>`;
        }
    }

    if (typeof direct.high_risk_pct === 'number' || typeof indirect.high_risk_pct === 'number') {
        html += '<p style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:#475569;margin-bottom:4px;">High-Risk Exposure</p>';
        if (typeof direct.high_risk_pct === 'number') {
            html += `<p>Direct: <span class="mono" style="font-weight:600;">${direct.high_risk_pct.toFixed(1)}%</span></p>`;
        }
        if (typeof indirect.high_risk_pct === 'number') {
            html += `<p>Indirect: <span class="mono" style="font-weight:600;">${indirect.high_risk_pct.toFixed(1)}%</span></p>`;
        }
    }

    // P2-6: Surface trace-discovered entities that multi-hop missed (due to pipeline ordering)
    if (traceEntities && traceEntities.length > 0) {
        // Collect entity names already in indirect exposure to avoid duplicates
        const indirectNames = new Set<string>();
        if (Array.isArray(indirect)) {
            for (const entry of indirect as any[]) {
                for (const ent of (entry.entities || [])) {
                    if (ent.name) indirectNames.add(ent.name.toLowerCase());
                }
            }
        }
        const novel = traceEntities.filter(e => !indirectNames.has(e.name.toLowerCase()));
        if (novel.length > 0) {
            html += `<div style="margin-top:12px;padding:10px 14px;background:#f0fdf4;border:1px solid #86efac;border-radius:6px;">`;
            html += `<p style="font-size:11px;font-weight:700;color:#166534;margin-bottom:6px;">Additional Entities from Fund-Flow Traces</p>`;
            html += `<p style="font-size:10px;color:#15803d;margin-bottom:6px;">These entities were discovered by backward/forward BFS tracing (steps 7-8) after multi-hop exposure analysis completed. They supplement the exposure data above.</p>`;
            html += `<table class="data" style="font-size:11px;"><thead><tr><th>Entity</th><th>Type</th><th>Direction</th></tr></thead><tbody>`;
            for (const ent of novel.slice(0, 15)) {
                html += `<tr><td><strong>${esc(cleanEntityName(ent.name))}</strong></td><td>${esc(ent.type)}</td><td>${esc(ent.direction)}${ent.hops ? ` (${ent.hops} hops)` : ''}</td></tr>`;
            }
            html += `</tbody></table>`;
            if (novel.length > 15) {
                html += `<p style="font-size:9px;color:#94a3b8;">+${novel.length - 15} more entities not shown.</p>`;
            }
            html += `</div>`;
        }
    }

    return html;
}

function renderAdaptiveTransactions(data: any): string {
    if (!data) return '';

    const stopReasonLabels: Record<string, string> = {
        all_fetched: 'All available transactions retrieved from API',
        sufficient_coverage: 'Sufficient coverage reached (60%+ of volume)',
        concentrated_activity: 'Activity concentrated in recent window',
        max_rounds: 'Maximum fetch rounds reached',
    };

    const rows: { label: string; value: string }[] = [];
    if (data.total_fetched !== undefined) rows.push({ label: 'Adaptive Fetch', value: String(data.total_fetched) });
    if (data.total !== undefined && data.total_fetched === undefined) rows.push({ label: 'Total', value: String(data.total) });
    if (data.fetch_rounds !== undefined) rows.push({ label: 'Fetch Rounds', value: String(data.fetch_rounds) });
    if (data.stop_reason === 'all_fetched') {
        const fetched = data.total_fetched ?? data.total ?? '?';
        rows.push({ label: 'Coverage', value: `${fetched} transactions fetched — all available from API at time of analysis` });
    } else if (data.stop_reason) {
        rows.push({ label: 'Stop Reason', value: stopReasonLabels[data.stop_reason] || String(data.stop_reason) });
    }

    // Transaction summary from orchestrator — total across all fetch mechanisms
    const txSummary = data._transaction_summary;
    if (txSummary) {
        const total = txSummary.total_transactions_analyzed;
        if (total && total !== data.total_fetched) {
            const parts: string[] = [];
            if (txSummary.adaptive_fetched) parts.push(`${txSummary.adaptive_fetched} from adaptive fetch`);
            if (txSummary.root_transactions && txSummary.root_transactions !== txSummary.adaptive_fetched) {
                parts.push(`${txSummary.root_transactions} root transactions from prefetch/cache`);
            }
            if (txSummary.backward_trace_edges) parts.push(`${txSummary.backward_trace_edges} from backward trace`);
            if (txSummary.forward_trace_edges) parts.push(`${txSummary.forward_trace_edges} from forward trace`);
            const breakdown = parts.length > 0 ? ` (${parts.join(' + ')})` : '';
            rows.push({ label: 'Total Analyzed', value: `${total} transactions across all investigation steps${breakdown}` });
        }
    }

    let html = '';

    if (rows.length > 0) {
        html += '<table class="data" style="margin-bottom:12px;"><tbody>';
        for (const r of rows) {
            html += `<tr><td style="font-weight:600;width:140px;">${esc(r.label)}</td><td class="mono">${esc(r.value)}</td></tr>`;
        }
        html += '</tbody></table>';
    }

    html += renderStampedRowsLine(data.transactions);

    if (data.pattern_triggers?.length > 0) {
        html += '<p style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:#475569;margin-bottom:6px;">Triggered Patterns</p><div>';
        for (const p of data.pattern_triggers) {
            const isStableSwap = String(p).toLowerCase().includes('stablecoin_swap') || String(p).toLowerCase().includes('stablecoin swap');
            const tagStyle = isStableSwap
                ? 'background:#f0fdfa;color:#0f766e;border:1px solid #99f6e4;'
                : '';
            html += `<span class="pattern-tag"${tagStyle ? ` style="${tagStyle}"` : ''}>${esc(p)}</span>`;
        }
        html += '</div>';
    }

    // Stablecoin-swap aggregation detail (STABLECOIN-SWAP-1 follow-on)
    const swapHits = Array.isArray(data.patterns)
        ? data.patterns.filter((p: any) => p?.pattern === 'stablecoin_swap')
        : [];
    if (swapHits.length > 0) {
        html += '<p style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:#0f766e;margin:10px 0 6px;">Stablecoin Swaps (same-chain)</p>';
        html += '<table class="data" style="margin-bottom:12px;"><tbody>';
        for (const p of swapHits) {
            html += `<tr><td style="font-weight:600;width:140px;">Count</td><td class="mono">${esc(String(p.swap_count ?? '?'))}</td></tr>`;
            if (p.top_protocol) {
                html += `<tr><td style="font-weight:600;">Protocol</td><td class="mono">${esc(String(p.top_protocol))}</td></tr>`;
            }
            if (Array.isArray(p.pair_hints) && p.pair_hints.length) {
                html += `<tr><td style="font-weight:600;">Pairs</td><td class="mono">${esc(p.pair_hints.join(', '))}</td></tr>`;
            }
            if (p.description) {
                html += `<tr><td style="font-weight:600;">Detail</td><td>${esc(String(p.description))}</td></tr>`;
            }
            const edgeType = p.canvas_intent?.edge_type;
            if (edgeType) {
                html += `<tr><td style="font-weight:600;">Canvas intent</td><td class="mono">${esc(String(edgeType))} (teal stroke)</td></tr>`;
            }
        }
        html += '</tbody></table>';
    }

    return html;
}

/* ── Appendix: full raw data tables ── */

function renderAppendices(sections: InvestigationSections): string {
    let html = '';
    let appendixNum = 0;

    // A. Signal Assessment — full families + raw AI narrative
    const sa = sections.signal_assessment;
    if (sa.status === 'loaded' && sa.data) {
        appendixNum++;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Signal Assessment — Full Data</h3>`;

        const families = sa.data.signal_families as Record<string, any> | undefined;
        if (families) {
            html += `<table class="data-full" style="table-layout:fixed;width:100%;"><thead><tr>
                <th style="width:100px;">Family</th><th style="width:55px;">Status</th><th style="width:28%;">Signals</th><th>Details</th>
            </tr></thead><tbody>`;
            for (const [name, info] of Object.entries(families) as [string, any][]) {
                const status = info.triggered === true ? 'YES' : info.triggered === false ? 'NO' : 'NOT TESTED';
                const statusStyle = info.triggered === true
                    ? 'color:#991b1b;font-weight:700;'
                    : info.triggered === false ? 'color:#065f46;' : 'color:#b45309;font-style:italic;';
                const signals = info.signals?.length ? esc(info.signals.join(', ')) : '—';

                // Format details intelligently per data type.
                // Family objects are {triggered, signals, details: {...}}.  Flatten
                // the `details` sub-object so its keys get the same skip/render logic
                // (fixes _velocity_raw leaking through renderRichValue).
                let detailHtml = '';
                const flatEntries: [string, unknown][] = [];
                for (const [k, v] of Object.entries(info)) {
                    if (k === 'triggered' || k === 'signals') continue;
                    if (k === 'details' && typeof v === 'object' && v !== null && !Array.isArray(v)) {
                        // Spread details entries so their keys get individual skip/render
                        for (const [dk, dv] of Object.entries(v as Record<string, unknown>)) {
                            flatEntries.push([dk, dv]);
                        }
                    } else {
                        flatEntries.push([k, v]);
                    }
                }
                for (const [k, v] of flatEntries) {
                    if (v === undefined || v === null) continue;
                    if (typeof v === 'object' && Object.keys(v as object).length === 0) continue;
                    // Skip internal/raw keys — velocity raw data shown in Velocity appendix, others are internal
                    if (k.startsWith('_') || k === 'velocity_raw' || k.endsWith('_raw') || k.endsWith('_raw_data')) continue;

                    if (k === 'patterns' && Array.isArray(v)) {
                        // Render patterns as compact list
                        detailHtml += '<div style="margin-bottom:4px;"><strong style="font-size:9px;text-transform:uppercase;color:#475569;">Patterns:</strong>';
                        for (const p of v as any[]) {
                            const conf = typeof p.confidence === 'number' ? ` (${(p.confidence * 100).toFixed(0)}%)` : '';
                            detailHtml += `<div style="margin-left:6px;margin-top:1px;">• <strong>${esc(p.pattern || '?')}</strong>${conf}`;
                            if (p.description) detailHtml += ` — ${esc(p.description)}`;
                            detailHtml += '</div>';
                        }
                        detailHtml += '</div>';
                    } else if (k === 'velocity' && typeof v === 'object' && !Array.isArray(v)) {
                        // Render velocity as compact key-value
                        const vel = v as Record<string, any>;
                        detailHtml += '<div style="margin-bottom:4px;"><strong style="font-size:9px;text-transform:uppercase;color:#475569;">Velocity:</strong>';
                        const velParts: string[] = [];
                        if (vel.profile) velParts.push(`Profile: <strong>${esc(vel.profile)}</strong>`);
                        if (typeof vel.risk_score === 'number') velParts.push(`Risk: ${(vel.risk_score * 100).toFixed(0)}%`);
                        if (typeof vel.fund_through_rate === 'number') velParts.push(`FTR: ${vel.fund_through_rate.toFixed(2)}`);
                        if (typeof vel.dwell_time_hours === 'number') velParts.push(`Dwell: ${vel.dwell_time_hours.toFixed(1)}h`);
                        if (typeof vel.burst_score === 'number') velParts.push(`Burst: ${vel.burst_score.toFixed(2)}`);
                        detailHtml += ` ${velParts.join(' · ')}</div>`;
                    } else if (typeof v === 'object') {
                        detailHtml += `<div style="margin:2px 0;"><strong style="font-size:9px;text-transform:uppercase;color:#475569;">${esc(k)}:</strong> ${renderRichValue(v, true)}</div>`;
                    } else if (typeof v === 'number') {
                        detailHtml += `<div style="margin-bottom:1px;">${esc(k.replace(/_/g, ' '))}: <strong>${v.toFixed(3)}</strong></div>`;
                    } else if (typeof v === 'string' || typeof v === 'boolean') {
                        detailHtml += `<div style="margin-bottom:1px;">${esc(k.replace(/_/g, ' '))}: ${esc(String(v))}</div>`;
                    }
                }

                html += `<tr>
                    <td style="text-transform:capitalize;font-weight:600;">${esc(name.replace(/_/g, ' '))}</td>
                    <td style="${statusStyle}">${status}</td>
                    <td style="word-break:break-word;">${signals}</td>
                    <td style="font-size:9px;line-height:1.5;">${detailHtml || '<span class="muted">—</span>'}</td>
                </tr>`;
            }
            html += '</tbody></table>';
        }

        if (sa.data.escalation_recommendation) {
            html += `<p style="margin-top:6px;font-size:11px;"><strong>Escalation:</strong> ${esc(sa.data.escalation_recommendation)}</p>`;
        }
        if (sa.data.families_triggered != null) {
            html += `<p style="font-size:11px;">${sa.data.families_triggered} of ${sa.data.families_tested ?? (sa.data.signal_families ? Object.keys(sa.data.signal_families).length : 8)} families triggered</p>`;
        }
        if (sa.data.score_contradictions?.length > 0) {
            html += '<p style="margin-top:4px;font-size:11px;font-weight:600;">Score Contradictions:</p><ul style="margin-left:16px;">';
            for (const c of sa.data.score_contradictions) {
                html += `<li style="font-size:10px;">${esc(c.description || String(c))}</li>`;
            }
            html += '</ul>';
        }

        // AI narrative is already rendered in the main Signal Assessment section — not duplicated here.

        html += '</div>';
    }

    // B. Velocity — full metrics + daily breakdown
    const vel = sections.velocity;
    if (vel.status === 'loaded' && vel.data) {
        appendixNum++;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Velocity Analysis — Full Data</h3>`;

        const d = vel.data;
        const m = d.metrics || {};

        // Profile and top-level scores
        html += '<table class="data-full"><thead><tr><th>Field</th><th>Value</th></tr></thead><tbody>';
        html += `<tr><td class="font-weight:600;">Velocity Profile</td><td>${esc(d.velocity_profile || '—')}</td></tr>`;
        html += `<tr><td>Velocity Risk Score</td><td class="mono">${fmt(d.velocity_risk_score, 4)}</td></tr>`;

        // All metrics
        const allMetrics: Record<string, any> = { ...m };
        // Also capture top-level fields that may be metrics
        for (const k of ['fund_through_rate', 'consolidation_ratio', 'value_amplification', 'dwell_time_hours', 'regularity_score', 'burst_score']) {
            if (d[k] !== undefined && allMetrics[k] === undefined) allMetrics[k] = d[k];
        }
        for (const [k, v] of Object.entries(allMetrics)) {
            const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const display = typeof v === 'number' ? v.toFixed(4) : typeof v === 'object' ? esc(JSON.stringify(v)) : esc(String(v ?? '—'));
            html += `<tr><td>${esc(label)}</td><td class="mono">${display}</td></tr>`;
        }
        html += '</tbody></table>';

        // Daily breakdown if present
        const daily = d.daily_breakdown || d.daily_activity;
        if (daily && typeof daily === 'object') {
            const entries = Array.isArray(daily) ? daily : Object.entries(daily);
            if (entries.length > 0) {
                html += '<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:1px;color:#475569;">Daily Activity Breakdown</p>';
                html += '<table class="data-full"><thead><tr><th>Date</th><th class="right">Tx Count</th><th class="right">Inflow</th><th class="right">Outflow</th></tr></thead><tbody>';
                const rows = Array.isArray(daily) ? daily : Object.entries(daily).map(([date, info]: [string, any]) => ({
                    date, tx_count: info?.tx_count ?? info?.count ?? info, inflow: info?.inflow, outflow: info?.outflow, volume: info?.volume,
                }));
                for (const row of rows.slice(0, 100)) {
                    const date = row.date || '—';
                    const txCount = typeof (row.tx_count ?? row.count) === 'number' ? String(row.tx_count ?? row.count) : esc(String(row.tx_count ?? row.count ?? '—'));
                    const inflow = typeof row.inflow === 'number' ? row.inflow.toFixed(4) : (row.volume != null ? (typeof row.volume === 'number' ? row.volume.toFixed(4) : esc(String(row.volume))) : '—');
                    const outflow = typeof row.outflow === 'number' ? row.outflow.toFixed(4) : '—';
                    html += `<tr><td class="mono">${esc(String(date))}</td><td class="right mono">${txCount}</td><td class="right mono">${inflow}</td><td class="right mono">${outflow}</td></tr>`;
                }
                if (rows.length > 100) {
                    html += `<tr><td colspan="3" class="muted">... ${rows.length - 100} more rows not shown</td></tr>`;
                }
                html += '</tbody></table>';
            }
        }

        html += '</div>';
    }

    // C. Counterparty Triage — full returned list
    const cp = sections.counterparty_triage;
    if (cp.status === 'loaded' && cp.data) {
        appendixNum++;
        const rawList = (cp.data.counterparties || cp.data.ranked || []) as any[];
        const spamCount = rawList.filter((item: any) => isSpamCounterparty(item)).length;
        const list = rawList.filter((item: any) => !isSpamCounterparty(item));
        const totalCps = cp.data.total_counterparties ?? list.length;
        const analysisTxCount = cp.data.analysis_tx_count;
        let subtitle = `${list.length} entries`;
        if (totalCps > list.length) subtitle += ` (top ${list.length} of ${totalCps} unique counterparties)`;
        if (analysisTxCount) subtitle += ` · analysed from ${analysisTxCount} transactions`;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Counterparty Triage — ${subtitle}</h3>`;
        if (spamCount > 0) {
            html += `<p class="note">Excluded ${spamCount} spam-token airdrop counterpart${spamCount > 1 ? 'ies' : 'y'} (classified as <code>token_type: spam</code>).</p>`;
        }

        if (list.length > 0) {
            // Curated columns — show what's investigatively useful, skip internal fields
            const columns: Array<{ key: string; label: string; align?: string; fmt?: (v: any, item: any) => string }> = [
                { key: 'address', label: 'Address', fmt: (v) => `<span class="mono">${monoWrap(String(v ?? ''))}</span>` },
                { key: 'entity_name', label: 'Entity', fmt: (v) => v && v !== 'Unknown' ? esc(cleanEntityName(String(v))) : '<span class="muted">\u2014</span>' },
                { key: 'entity_type', label: 'Type', fmt: (v, item) => esc(normalizeEntityType(String(v || 'unknown'), item.entity_name)) },
                { key: 'primary_volume', label: 'Volume', align: 'right', fmt: (v, item) => {
                    const val = Number(v || item.total_volume || 0);
                    const tok = item.primary_token || '';
                    return `${fmtCryptoVal(val)} ${esc(tok)}`;
                }},
                { key: 'tx_count', label: 'Txs', align: 'right', fmt: (v) => String(v || 0) },
                { key: 'risk_tier', label: 'Risk', fmt: (v) => {
                    const tier = String(v || 'low').toLowerCase();
                    const color = tier === 'critical' ? '#dc2626' : tier === 'high' || tier === 'severe' ? '#ea580c' : tier === 'medium' ? '#d97706' : '#64748b';
                    return `<span style="color:${color};font-weight:600;">${tier.toUpperCase()}</span>`;
                }},
                { key: 'sanctioned', label: 'Sanctions', fmt: (v) => v ? '<span style="color:#dc2626;font-weight:700;">YES</span>' : 'No' },
            ];

            html += '<table class="data-full"><thead><tr><th>#</th>';
            for (const col of columns) html += `<th${col.align ? ` class="${col.align}"` : ''}>${col.label}</th>`;
            html += '</tr></thead><tbody>';
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                html += `<tr><td class="muted">${i + 1}</td>`;
                for (const col of columns) {
                    const v = item[col.key];
                    html += `<td${col.align ? ` class="${col.align}"` : ''}>${col.fmt ? col.fmt(v, item) : (v ?? '<span class="muted">\u2014</span>')}</td>`;
                }
                html += '</tr>';
            }
            html += '</tbody></table>';

            // Token volume details as a compact sub-table (only if data exists)
            const hasTokenVolumes = list.some((item: any) => item.token_volumes && Object.keys(item.token_volumes).length > 1);
            if (hasTokenVolumes) {
                html += '<details style="margin-top:8px;"><summary style="font-size:11px;color:#64748b;cursor:pointer;">Token Volume Breakdown (per-counterparty)</summary>';
                html += '<table class="data-full" style="font-size:10px;margin-top:4px;"><thead><tr><th>Address</th><th>Token</th><th class="right">Volume In</th><th class="right">Volume Out</th><th class="right">Txs</th></tr></thead><tbody>';
                for (const item of list) {
                    const tvol = item.token_volumes;
                    if (!tvol || typeof tvol !== 'object') continue;
                    for (const [tok, data] of Object.entries(tvol as Record<string, any>)) {
                        html += `<tr><td class="mono">${monoWrap(String(item.address || ''))}</td>`;
                        html += `<td>${esc(tok)}</td>`;
                        html += `<td class="right">${fmtCryptoVal(Number(data?.volume_in || data?.['volume in'] || 0))}</td>`;
                        html += `<td class="right">${fmtCryptoVal(Number(data?.volume_out || data?.['volume out'] || 0))}</td>`;
                        html += `<td class="right">${data?.tx_count || data?.['tx count'] || 0}</td></tr>`;
                    }
                }
                html += '</tbody></table></details>';
            }
        } else {
            html += '<p class="note">No counterparty data returned.</p>';
        }

        html += '</div>';
    }

    // D. Backward Trace — full nodes + edges tables
    const bt = sections.backward_trace;
    if (bt.status === 'loaded' && bt.data) {
        appendixNum++;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Backward Trace — Full Data</h3>`;

        const nodes = (bt.data.nodes || []) as any[];
        const edges = (bt.data.edges || []) as any[];
        const paths = (bt.data.paths || bt.data.traces || []) as any[];

        // Metadata
        const hopsReached = bt.data.max_hops_reached ?? bt.data.depth_searched;
        const expSummary = bt.data.exploration_summary;
        html += '<table class="data-full" style="margin-bottom:10px;"><tbody>';
        if (expSummary) {
            // Show exploration scope vs what's in the report
            html += `<tr><td style="font-weight:600;width:180px;">Addresses Explored (BFS)</td><td class="mono">${expSummary.total_nodes_explored}</td></tr>`;
            html += `<tr><td style="font-weight:600;">Transactions Explored</td><td class="mono">${expSummary.total_edges_explored}</td></tr>`;
            html += `<tr><td style="font-weight:600;">BFS Depth Reached</td><td class="mono">${expSummary.bfs_depth_reached} hops</td></tr>`;
            html += `<tr><td style="font-weight:600;">Entities Found</td><td class="mono">${expSummary.entities_found}</td></tr>`;
            html += `<tr><td style="font-weight:600;">Paths Found</td><td class="mono">${expSummary.paths_found}</td></tr>`;
            if (expSummary.stop_reason) {
                const stopLabels: Record<string, string> = {
                    no_entity_coverage: 'Entity DB coverage insufficient for this neighborhood',
                    time_budget: 'Time budget reached (2 min) — re-run uses cached data',
                    limit_reached: 'Address limit reached (200)',
                    depth: 'Max hop depth reached',
                    stop_entity: 'Target entity found',
                    no_inbound_transactions: 'No inbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
                    no_outbound_transactions: 'No outbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
                    root_page_unavailable: 'The transactions of the subject could not be read, so the trace did not run (not a finding of no activity)',
                };
                html += `<tr><td style="font-weight:600;">Stop Reason</td><td>${esc(stopLabels[expSummary.stop_reason] || expSummary.stop_reason)}</td></tr>`;
            }
            html += `<tr><td style="font-weight:600;">Nodes in Report</td><td class="mono">${nodes.length} (gold paths + 1-hop context)</td></tr>`;
            html += `<tr><td style="font-weight:600;">Edges in Report</td><td class="mono">${edges.length}</td></tr>`;
        } else {
            if (bt.data.total_nodes != null) html += `<tr><td style="font-weight:600;width:140px;">Total Nodes</td><td class="mono">${bt.data.total_nodes}</td></tr>`;
            if (bt.data.total_edges != null) html += `<tr><td style="font-weight:600;">Total Edges</td><td class="mono">${bt.data.total_edges}</td></tr>`;
            if (hopsReached != null) html += `<tr><td style="font-weight:600;">Depth Searched</td><td class="mono">${hopsReached} hops</td></tr>`;
        }
        if (bt.data.root_address) html += `<tr><td style="font-weight:600;">Root Address</td><td class="mono">${monoWrap(bt.data.root_address)}</td></tr>`;
        if (bt.data.status) html += `<tr><td style="font-weight:600;">Status</td><td>${esc(bt.data.status)}</td></tr>`;
        if (bt.data.source_entities?.length > 0) html += `<tr><td style="font-weight:600;">Source Entities Found</td><td class="mono">${bt.data.source_entities.length}</td></tr>`;
        html += '</tbody></table>';

        // Nodes table — cap at 100 to prevent browser OOM on large traces
        const NODE_CAP = 100;
        if (nodes.length > 0) {
            const cappedNodes = nodes.length > NODE_CAP;
            // Prioritize nodes with entity info, then by risk score
            const sortedNodes = [...nodes].sort((a: any, b: any) => {
                const aHasEntity = a.entity_name || a.entity_type ? 1 : 0;
                const bHasEntity = b.entity_name || b.entity_type ? 1 : 0;
                if (aHasEntity !== bHasEntity) return bHasEntity - aHasEntity;
                return (b.risk_score || 0) - (a.risk_score || 0);
            });
            const displayNodes = sortedNodes.slice(0, NODE_CAP);
            html += `<p style="font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Traced Nodes (${nodes.length}${cappedNodes ? `, showing top ${NODE_CAP}` : ''})</p>`;
            html += '<table class="data-full"><thead><tr><th>Address</th><th>Entity</th><th>Type</th><th class="right">Risk</th><th>Sanctioned</th></tr></thead><tbody>';
            for (const n of displayNodes) {
                const addr = n.id || '';
                const shortAddr = monoWrap(addr);
                const entityName = n.entity_name ? cleanEntityName(n.entity_name) : '';
                const riskPct = n.risk_score != null ? `${Math.round(Number(n.risk_score) * 100)}%` : '\u2014';
                html += `<tr>
                    <td class="mono">${shortAddr}</td>
                    <td>${entityName ? esc(entityName) : '<span class="muted">\u2014</span>'}</td>
                    <td>${esc(normalizeEntityType(n.entity_type || 'unknown', n.entity_name))}</td>
                    <td class="right mono">${riskPct}</td>
                    <td>${n.sanctioned ? '<span style="color:#dc2626;font-weight:700;">YES</span>' : n.sanctioned === false ? 'No' : '\u2014'}</td>
                </tr>`;
            }
            if (cappedNodes) {
                html += `<tr><td colspan="5" class="muted" style="text-align:center;font-style:italic;">… ${nodes.length - NODE_CAP} additional nodes omitted for report size</td></tr>`;
            }
            html += '</tbody></table>';
        }

        // Edges table — cap at 200 to prevent browser OOM on large traces
        const EDGE_CAP = 200;
        if (edges.length > 0) {
            const cappedEdges = edges.length > EDGE_CAP;
            // Prioritize in-path edges and high-value edges
            const sortedEdges = [...edges].sort((a: any, b: any) => {
                const aInPath = a.in_path ? 1 : 0;
                const bInPath = b.in_path ? 1 : 0;
                if (aInPath !== bInPath) return bInPath - aInPath;
                return (Number(b.value) || 0) - (Number(a.value) || 0);
            });
            const displayEdges = sortedEdges.slice(0, EDGE_CAP);
            html += `<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Traced Edges (${edges.length}${cappedEdges ? `, showing top ${EDGE_CAP}` : ''})</p>`;
            // Tx Hash column added for Section-91/94 evidence \u2014 exchanges
            // need the on-chain hash to pinpoint the deposit event in their
            // KYC logs.
            html += '<table class="data-full"><thead><tr><th>From</th><th>To</th><th class="right">Value</th><th>Token</th><th>Date</th><th>Tx Hash</th></tr></thead><tbody>';
            for (const e of displayEdges) {
                const fromAddr = e.from || '';
                const toAddr = e.to || '';
                const shortFrom = monoWrap(fromAddr);
                const shortTo = monoWrap(toAddr);
                const txHash = e.tx_hash || '';
                const shortHash = monoWrap(txHash);
                html += `<tr>
                    <td class="mono">${shortFrom}</td>
                    <td class="mono">${shortTo}</td>
                    <td class="right mono">${e.value != null ? fmtCryptoVal(Number(e.value)) : '\u2014'}</td>
                    <td>${esc(cleanTokenSymbol(e.token_symbol) || '\u2014')}</td>
                    <td class="mono">${esc(fmtTs(e.timestamp))}</td>
                    <td class="mono">${txHash ? shortHash : '\u2014'}</td>
                </tr>`;
            }
            if (cappedEdges) {
                html += `<tr><td colspan="6" class="muted" style="text-align:center;font-style:italic;">… ${edges.length - EDGE_CAP} additional edges omitted for report size</td></tr>`;
            }
            html += '</tbody></table>';
        }

        // Full paths — compact card layout instead of wide table
        if (paths.length > 0) {
            html += `<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Fund-Source Paths (${paths.length})</p>`;
            for (let i = 0; i < paths.length; i++) {
                const p = paths[i];
                const addrs = (p.addresses || p.path || []) as string[];
                const pEdges = (p.edges || []) as any[];
                const te = p.target_entity || {};
                const targetName = cleanEntityName(te.name || p.entity_name || 'Unknown');
                const targetType = normalizeEntityType(te.type || p.entity_type || 'unknown', targetName);
                html += `<div style="border:1px solid #e2e8f0;border-radius:6px;padding:8px 10px;margin-bottom:6px;font-size:11px;">`;
                html += `<div style="display:flex;justify-content:space-between;margin-bottom:4px;"><strong>Path ${i + 1}</strong><span style="color:#64748b;">${addrs.length - 1} hops \u2192 ${esc(targetName)} (${esc(targetType)})</span></div>`;
                html += `<div class="mono" style="font-size:10px;line-height:1.6;">`;
                addrs.forEach((addr, j) => {
                    const short = monoWrap(addr);
                    const isFirst = j === 0;
                    const isLast = j === addrs.length - 1;
                    const style = isFirst ? 'color:#1d4ed8;font-weight:600;' : isLast ? 'color:#dc2626;font-weight:600;' : '';
                    html += `<span style="${style}">${short}</span>`;
                    if (!isLast) {
                        const edge = pEdges[j];
                        const val = edge?.total_value ?? edge?.value;
                        const tok = edge?.token_symbol || '';
                        const valStr = val != null ? ` ${fmtCryptoVal(Number(val))} ${esc(tok)}` : '';
                        html += ` <span style="color:#f59e0b;">\u2192${valStr} \u2192</span> `;
                    }
                });
                html += `</div></div>`;
            }
        }

        html += '</div>';
    }

    // E. Forward Trace — full nodes + edges tables (mirrors backward trace appendix)
    const ft = sections.forward_trace;
    if (ft.status === 'loaded' && ft.data) {
        appendixNum++;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Forward Trace — Full Data</h3>`;

        const nodes = (ft.data.nodes || []) as any[];
        const edges = (ft.data.edges || []) as any[];
        const paths = (ft.data.paths || ft.data.traces || []) as any[];

        // Metadata
        const hopsReached = ft.data.max_hops_reached ?? ft.data.depth_searched;
        const expSummary = ft.data.exploration_summary;
        html += '<table class="data-full" style="margin-bottom:10px;"><tbody>';
        if (expSummary) {
            html += `<tr><td style="font-weight:600;width:180px;">Addresses Explored (BFS)</td><td class="mono">${expSummary.total_nodes_explored}</td></tr>`;
            html += `<tr><td style="font-weight:600;">Transactions Explored</td><td class="mono">${expSummary.total_edges_explored}</td></tr>`;
            html += `<tr><td style="font-weight:600;">BFS Depth Reached</td><td class="mono">${expSummary.bfs_depth_reached} hops</td></tr>`;
            html += `<tr><td style="font-weight:600;">Entities Found</td><td class="mono">${expSummary.entities_found}</td></tr>`;
            html += `<tr><td style="font-weight:600;">Paths Found</td><td class="mono">${expSummary.paths_found}</td></tr>`;
            if (expSummary.stop_reason) {
                const stopLabels: Record<string, string> = {
                    no_entity_coverage: 'Entity DB coverage insufficient for this neighborhood',
                    time_budget: 'Time budget reached (2 min) — re-run uses cached data',
                    limit_reached: 'Address limit reached (200)',
                    depth: 'Max hop depth reached',
                    stop_entity: 'Target entity found',
                    no_inbound_transactions: 'No inbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
                    no_outbound_transactions: 'No outbound transactions in the latest 100 (the check reads the newest 100 rows only, not the whole history)',
                    root_page_unavailable: 'The transactions of the subject could not be read, so the trace did not run (not a finding of no activity)',
                };
                html += `<tr><td style="font-weight:600;">Stop Reason</td><td>${esc(stopLabels[expSummary.stop_reason] || expSummary.stop_reason)}</td></tr>`;
            }
            html += `<tr><td style="font-weight:600;">Nodes in Report</td><td class="mono">${nodes.length} (gold paths + 1-hop context)</td></tr>`;
            html += `<tr><td style="font-weight:600;">Edges in Report</td><td class="mono">${edges.length}</td></tr>`;
        } else {
            if (ft.data.total_nodes != null) html += `<tr><td style="font-weight:600;width:140px;">Total Nodes</td><td class="mono">${ft.data.total_nodes}</td></tr>`;
            if (ft.data.total_edges != null) html += `<tr><td style="font-weight:600;">Total Edges</td><td class="mono">${ft.data.total_edges}</td></tr>`;
            if (hopsReached != null) html += `<tr><td style="font-weight:600;">Depth Searched</td><td class="mono">${hopsReached} hops</td></tr>`;
        }
        if (ft.data.root_address) html += `<tr><td style="font-weight:600;">Root Address</td><td class="mono">${monoWrap(ft.data.root_address)}</td></tr>`;
        if (ft.data.status) html += `<tr><td style="font-weight:600;">Status</td><td>${esc(ft.data.status)}</td></tr>`;
        if (ft.data.destination_entities?.length > 0) html += `<tr><td style="font-weight:600;">Destination Entities Found</td><td class="mono">${ft.data.destination_entities.length}</td></tr>`;
        html += '</tbody></table>';

        // Nodes table — cap at 100
        const FT_NODE_CAP = 100;
        if (nodes.length > 0) {
            const cappedNodes = nodes.length > FT_NODE_CAP;
            const sortedNodes = [...nodes].sort((a: any, b: any) => {
                const aHasEntity = a.entity_name || a.entity_type ? 1 : 0;
                const bHasEntity = b.entity_name || b.entity_type ? 1 : 0;
                if (aHasEntity !== bHasEntity) return bHasEntity - aHasEntity;
                return (b.risk_score || 0) - (a.risk_score || 0);
            });
            const displayNodes = sortedNodes.slice(0, FT_NODE_CAP);
            html += `<p style="font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Traced Nodes (${nodes.length}${cappedNodes ? `, showing top ${FT_NODE_CAP}` : ''})</p>`;
            html += '<table class="data-full"><thead><tr><th>Address</th><th>Entity</th><th>Type</th><th class="right">Risk Score</th><th>Sanctioned</th></tr></thead><tbody>';
            for (const n of displayNodes) {
                html += `<tr>
                    <td class="mono">${monoWrap(n.id || '—')}</td>
                    <td>${esc(n.entity_name ? cleanEntityName(n.entity_name) : '—')}</td>
                    <td>${esc(n.entity_type || '—')}</td>
                    <td class="right mono">${n.risk_score != null ? Number(n.risk_score).toFixed(2) : '—'}</td>
                    <td>${n.sanctioned ? 'YES' : n.sanctioned === false ? 'No' : '—'}</td>
                </tr>`;
            }
            if (cappedNodes) {
                html += `<tr><td colspan="5" class="muted" style="text-align:center;font-style:italic;">… ${nodes.length - FT_NODE_CAP} additional nodes omitted for report size</td></tr>`;
            }
            html += '</tbody></table>';
        }

        // Edges table — cap at 200
        const FT_EDGE_CAP = 200;
        if (edges.length > 0) {
            const cappedEdges = edges.length > FT_EDGE_CAP;
            const sortedEdges = [...edges].sort((a: any, b: any) => {
                const aInPath = a.in_path ? 1 : 0;
                const bInPath = b.in_path ? 1 : 0;
                if (aInPath !== bInPath) return bInPath - aInPath;
                return (Number(b.value) || 0) - (Number(a.value) || 0);
            });
            const displayEdges = sortedEdges.slice(0, FT_EDGE_CAP);
            html += `<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Traced Edges (${edges.length}${cappedEdges ? `, showing top ${FT_EDGE_CAP}` : ''})</p>`;
            // Tx Hash column added for Section-91/94 evidence \u2014 exchanges
            // need the on-chain hash to pinpoint the deposit event in their
            // KYC logs.
            html += '<table class="data-full"><thead><tr><th>From</th><th>To</th><th class="right">Value</th><th>Token</th><th>Date</th><th>Tx Hash</th></tr></thead><tbody>';
            for (const e of displayEdges) {
                const fromAddr = e.from || '';
                const toAddr = e.to || '';
                const shortFrom = monoWrap(fromAddr);
                const shortTo = monoWrap(toAddr);
                const txHash = e.tx_hash || '';
                const shortHash = monoWrap(txHash);
                html += `<tr>
                    <td class="mono">${shortFrom}</td>
                    <td class="mono">${shortTo}</td>
                    <td class="right mono">${e.value != null ? fmtCryptoVal(Number(e.value)) : '\u2014'}</td>
                    <td>${esc(cleanTokenSymbol(e.token_symbol) || '\u2014')}</td>
                    <td class="mono">${esc(fmtTs(e.timestamp))}</td>
                    <td class="mono">${txHash ? shortHash : '\u2014'}</td>
                </tr>`;
            }
            if (cappedEdges) {
                html += `<tr><td colspan="6" class="muted" style="text-align:center;font-style:italic;">… ${edges.length - FT_EDGE_CAP} additional edges omitted for report size</td></tr>`;
            }
            html += '</tbody></table>';
        }

        // Destination paths with complete addresses
        if (paths.length > 0) {
            html += `<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Destination Paths (${paths.length})</p>`;
            for (let i = 0; i < paths.length; i++) {
                const p = paths[i];
                const addrs = (p.addresses || p.path || []) as string[];
                const pEdges = (p.edges || []) as any[];
                const te = p.target_entity || {};
                const targetName = cleanEntityName(te.name || p.entity_name || 'Unknown');
                const targetType = normalizeEntityType(te.type || p.entity_type || 'unknown', targetName);
                html += `<div style="border:1px solid #e2e8f0;border-radius:6px;padding:8px 10px;margin-bottom:6px;font-size:11px;">`;
                html += `<div style="display:flex;justify-content:space-between;margin-bottom:4px;"><strong>Path ${i + 1}</strong><span style="color:#64748b;">${addrs.length - 1} hops \u2192 ${esc(targetName)} (${esc(targetType)})</span></div>`;
                html += `<div class="mono" style="font-size:10px;line-height:1.6;">`;
                addrs.forEach((addr, j) => {
                    const short = monoWrap(addr);
                    const isFirst = j === 0;
                    const isLast = j === addrs.length - 1;
                    const style = isFirst ? 'color:#1d4ed8;font-weight:600;' : isLast ? 'color:#dc2626;font-weight:600;' : '';
                    html += `<span style="${style}">${short}</span>`;
                    if (!isLast) {
                        const edge = pEdges[j];
                        const val = edge?.total_value ?? edge?.value;
                        const tok = edge?.token_symbol || '';
                        const valStr = val != null ? ` ${fmtCryptoVal(Number(val))} ${esc(tok)}` : '';
                        html += ` <span style="color:#f59e0b;">\u2192${valStr} \u2192</span> `;
                    }
                });
                html += `</div></div>`;
            }
        }

        html += '</div>';
    }

    // F. Multi-Hop Exposure — full breakdown
    const mh = sections.multi_hop_exposure;
    if (mh.status === 'loaded' && mh.data) {
        appendixNum++;
        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Multi-Hop Exposure — Full Data</h3>`;

        const d = mh.data;
        html += '<table class="data-full"><thead><tr><th>Field</th><th>Value</th></tr></thead><tbody>';
        if (d.combined_risk_score != null) html += `<tr><td style="font-weight:600;">Combined Risk Score</td><td class="mono">${Number(d.combined_risk_score).toFixed(4)}</td></tr>`;

        // Unattributed % by hop (counterparties not in entity DB)
        const hops = d.unknown_pct_by_hop || {};
        for (const [hop, val] of Object.entries(hops)) {
            html += `<tr><td>Hop ${esc(hop)} — Unattributed %</td><td class="mono">${typeof val === 'number' ? val.toFixed(2) + '%' : esc(String(val))}</td></tr>`;
        }

        // Direct/indirect exposure — render as compact tables
        const direct = d.direct_exposure || {};
        const indirect = d.indirect_exposure || {};
        const HIGH_RISK_CATS = ['scam', 'phishing', 'fraud', 'ransomware', 'mixer', 'sanctioned', 'hacker', 'darknet'];
        const isHighRiskExposure = (item: any) => {
            const cat = ((item?.category || item?.risk_tier) ?? '').toLowerCase();
            const name = ((item?.entity_name || '') + ' ' + ((item?.entities || []).map((e: any) => e?.name || '').join(' '))).toLowerCase();
            return HIGH_RISK_CATS.some(c => cat.includes(c) || name.includes(c))
                || ['severe', 'high', 'critical'].includes((item?.risk_tier || '').toLowerCase());
        };
        const truncAddrExp = (addr: string) => addr || '—'; // full address: a hover title does not survive print

        for (const [label, exp] of [['Direct Exposure', direct], ['Indirect Exposure', indirect]] as [string, any][]) {
            if (exp && typeof exp === 'object') {
                if (Array.isArray(exp)) {
                    const EXPOSURE_CAP = 25;
                    const topItems = exp.slice(0, EXPOSURE_CAP);
                    const extraHighRisk = exp.slice(EXPOSURE_CAP).filter(isHighRiskExposure);
                    const displayItems = [...topItems, ...extraHighRisk];
                    const skippedCount = exp.length - displayItems.length;

                    html += `<p style="font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin:10px 0 4px;">${esc(label)} (${exp.length} groups)</p>`;
                    html += '<table class="data-full" style="width:100%;"><thead><tr>';
                    html += '<th style="width:30px;">#</th>';
                    html += '<th style="width:80px;">Category</th>';
                    html += '<th style="width:90px;">Volume</th>';
                    html += '<th style="width:35px;">Hop</th>';
                    html += '<th style="width:50px;">Risk</th>';
                    html += '<th>Key Entities (top 3)</th>';
                    html += '</tr></thead><tbody>';

                    for (let idx = 0; idx < displayItems.length; idx++) {
                        const item = displayItems[idx];
                        if (!item || typeof item !== 'object') continue;
                        const cat = item.category || 'Unknown';
                        const vol = typeof item.volume === 'number' ? fmtNum(item.volume) : '—';
                        const hop = item.hop ?? '—';
                        const risk = item.risk_tier || 'low';
                        const isHR = isHighRiskExposure(item);
                        const riskStyle = isHR ? 'color:#dc2626;font-weight:600;' : '';

                        // Extract top 3 entity names from entities array
                        const entities = item.entities || [];
                        let entityText = '—';
                        if (Array.isArray(entities) && entities.length > 0) {
                            const top3 = entities.slice(0, 3).map((e: any) => {
                                const n = e?.name || '';
                                const a = e?.address || '';
                                const v = typeof e?.volume === 'number' && e.volume > 0 ? ` (${fmtNum(e.volume)})` : '';
                                return n.length > 20 ? `${truncAddrExp(a)}${v}` : `${n}${v}`;
                            });
                            entityText = top3.join(', ');
                            if (entities.length > 3) entityText += ` +${entities.length - 3} more`;
                        }

                        html += `<tr${isHR ? ' style="background:#fef2f2;"' : ''}>`;
                        html += `<td class="mono">${idx + 1}</td>`;
                        html += `<td style="font-size:9px;">${esc(cat)}</td>`;
                        html += `<td class="right mono" style="white-space:nowrap;">${vol}</td>`;
                        html += `<td class="mono">${hop}</td>`;
                        html += `<td style="${riskStyle}font-size:9px;">${esc(risk)}</td>`;
                        html += `<td style="font-size:9px;">${entityText}</td>`;
                        html += '</tr>';
                    }
                    if (skippedCount > 0) {
                        html += `<tr><td colspan="6" style="color:#64748b;font-style:italic;text-align:center;">Showing ${displayItems.length} of ${exp.length} entries${extraHighRisk.length > 0 ? ` (includes ${extraHighRisk.length} high-risk beyond top ${EXPOSURE_CAP})` : ''}. Full dataset available via JSON export.</td></tr>`;
                    }
                    html += '</tbody></table>';
                } else {
                    for (const [k, v] of Object.entries(exp)) {
                        const kLabel = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        // Flatten nested objects (e.g. source: {address, entity_name, ...})
                        if (v && typeof v === 'object' && !Array.isArray(v)) {
                            for (const [sk, sv] of Object.entries(v as Record<string, unknown>)) {
                                const skLabel = sk.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                const rendered = typeof sv === 'number' ? fmtNum(sv) : esc(String(sv ?? '—'));
                                html += `<tr><td>${esc(label)} — ${esc(kLabel)} — ${esc(skLabel)}</td><td class="mono" style="word-break:break-word;">${rendered}</td></tr>`;
                            }
                        } else if (Array.isArray(v)) {
                            for (let ai = 0; ai < v.length; ai++) {
                                const arrItem = v[ai];
                                if (arrItem && typeof arrItem === 'object') {
                                    for (const [aik, aiv] of Object.entries(arrItem)) {
                                        const aikLabel = aik.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                        let rendered: string;
                                        if (typeof aiv === 'number') {
                                            rendered = fmtNum(aiv);
                                        } else if (Array.isArray(aiv)) {
                                            // Nested array of objects (e.g. entities: [{name, address, volume}, ...])
                                            rendered = aiv.map((e: any) => {
                                                if (e && typeof e === 'object') {
                                                    const parts: string[] = [];
                                                    if (e.name) parts.push(esc(cleanEntityName(String(e.name))));
                                                    if (e.address) parts.push(monoWrap(String(e.address)));
                                                    if (typeof e.volume === 'number') parts.push(fmtNum(e.volume));
                                                    if (parts.length > 0) return parts.join(' · ');
                                                    // Fallback: render all string/number fields
                                                    return Object.entries(e)
                                                        .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
                                                        .map(([k, v]) => `${k}: ${typeof v === 'number' ? (v as number).toFixed(4) : v}`)
                                                        .join(' · ') || '—';
                                                }
                                                return esc(String(e));
                                            }).join('<br>');
                                        } else {
                                            rendered = esc(String(aiv ?? '—'));
                                        }
                                        html += `<tr><td>${esc(label)} — ${esc(kLabel)} [${ai}] — ${esc(aikLabel)}</td><td class="mono" style="word-break:break-word;">${rendered}</td></tr>`;
                                    }
                                } else {
                                    html += `<tr><td>${esc(label)} — ${esc(kLabel)} [${ai}]</td><td class="mono">${typeof arrItem === 'number' ? fmtNum(arrItem) : esc(String(arrItem ?? '—'))}</td></tr>`;
                                }
                            }
                        } else {
                            html += `<tr><td>${esc(label)} — ${esc(kLabel)}</td><td class="mono">${typeof v === 'number' ? fmtNum(v) : esc(String(v ?? '—'))}</td></tr>`;
                        }
                    }
                }
            }
        }

        // Any per-hop entity breakdown
        const hopBreakdown = d.per_hop_breakdown || d.hop_breakdown;
        if (hopBreakdown && typeof hopBreakdown === 'object') {
            for (const [hop, breakdown] of Object.entries(hopBreakdown) as [string, any][]) {
                if (breakdown && typeof breakdown === 'object') {
                    for (const [entity, pctVal] of Object.entries(breakdown)) {
                        html += `<tr><td>Hop ${esc(hop)} — ${esc(entity)}</td><td class="mono">${typeof pctVal === 'number' ? pctVal.toFixed(2) + '%' : esc(String(pctVal))}</td></tr>`;
                    }
                }
            }
        }
        html += '</tbody></table>';
        html += '</div>';
    }

    // G. Adaptive Transactions — full transaction list
    const at = sections.adaptive_transactions;
    if (at.status === 'loaded' && at.data) {
        appendixNum++;
        const d = at.data;
        const txs = (d.transactions || []) as any[];

        html += `<div class="appendix"><h3>Appendix ${String.fromCharCode(64 + appendixNum)}: Transaction Fetch — Full Data</h3>`;

        // Summary fields
        html += '<table class="data-full" style="margin-bottom:10px;"><tbody>';
        if (d.total_fetched != null) html += `<tr><td style="font-weight:600;width:140px;">Total Fetched</td><td class="mono">${d.total_fetched}</td></tr>`;
        if (d.total != null && d.total_fetched == null) html += `<tr><td style="font-weight:600;">Total</td><td class="mono">${d.total}</td></tr>`;
        if (d.fetch_rounds != null) html += `<tr><td style="font-weight:600;">Fetch Rounds</td><td class="mono">${d.fetch_rounds}</td></tr>`;
        if (d.stop_reason) html += `<tr><td style="font-weight:600;">Stop Reason</td><td>${esc(d.stop_reason)}</td></tr>`;
        if (d.pattern_triggers?.length) html += `<tr><td style="font-weight:600;">Patterns Triggered</td><td>${esc(d.pattern_triggers.join(', '))}</td></tr>`;
        html += '</tbody></table>';

        // Signal analysis if present
        if (d.signal_analysis && typeof d.signal_analysis === 'object') {
            html += '<p style="font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin:8px 0 4px;">Signal Analysis</p>';
            html += '<table class="data-full"><thead><tr><th>Signal</th><th>Value</th></tr></thead><tbody>';
            for (const [k, v] of Object.entries(d.signal_analysis)) {
                const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                html += `<tr><td>${esc(label)}</td><td class="mono">${typeof v === 'object' ? renderRichValue(v, true) : esc(String(v ?? '—'))}</td></tr>`;
            }
            html += '</tbody></table>';
        }

        // Full transaction list — cap at 100 to prevent browser OOM
        const TX_CAP = 100;
        if (txs.length > 0) {
            const cappedTxs = txs.length > TX_CAP;
            html += `<p style="margin-top:10px;font-size:10px;font-weight:600;text-transform:uppercase;color:#475569;margin-bottom:4px;">Transactions (${txs.length}${cappedTxs ? `, showing first ${TX_CAP}` : ''})</p>`;

            // Curated columns for LEA readability (8 cols instead of 16+)
            // Removed: fee (always 0), successful (always yes), token_name (redundant with symbol),
            // data_source (internal), block_height (internal), token_address (contract, not useful),
            // other_address_risk_score (numeric, replaced by tier in entity type)
            const displayCols = [
                { key: 'tx_hash', label: 'Transaction Hash', width: '130px' },
                { key: 'from_address', label: 'From', width: '110px' },
                { key: 'to_address', label: 'To', width: '110px' },
                { key: 'value', label: 'Value', width: '80px' },
                { key: 'token_symbol', label: 'Token', width: '50px' },
                { key: 'timestamp', label: 'Date/Time', width: '100px' },
                { key: 'other_address_entity_name', label: 'Counterparty Entity', width: '100px' },
                { key: 'other_address_entity_type', label: 'Entity Type', width: '70px' },
            ];

            const truncAddr = (addr: string) => addr ? monoWrap(addr) : '—'; // full value, wrapped; print has no hover
            const truncHash = (hash: string) => hash ? monoWrap(hash) : '—';
            const fmtTs = (ts: any) => {
                if (!ts) return '—';
                const n = Number(ts);
                const d = !isNaN(n) ? new Date(n > 1e12 ? n : n * 1000) : new Date(String(ts));
                return isNaN(d.getTime()) ? String(ts).slice(0, 19) : d.toISOString().slice(0, 16).replace('T', ' ');
            };

            html += '<div style="overflow-x:auto;max-width:100%;"><table class="data-full" style="width:100%;"><thead><tr>';
            for (const col of displayCols) {
                html += `<th style="width:${col.width};white-space:nowrap;">${esc(col.label)}</th>`;
            }
            html += '</tr></thead><tbody>';

            const displayTxs = txs.slice(0, TX_CAP);
            for (const tx of displayTxs) {
                const hash = tx.tx_hash || tx.hash || '';
                const from = tx.from_address || tx.from || '';
                const to = tx.to_address || tx.to || '';
                const val = tx.value ?? tx.amount ?? 0;
                const valStr = typeof val === 'number' ? (val % 1 === 0 ? String(val) : val.toFixed(6)) : String(val);
                const token = tx.token_symbol || '';
                const ts = fmtTs(tx.timestamp);
                const entityName = tx.other_address_entity_name || '—';
                const entityType = tx.other_address_entity_type || '—';

                html += '<tr>';
                html += `<td class="mono" style="font-size:9px;">${truncHash(hash)}</td>`;
                html += `<td class="mono" style="font-size:9px;">${truncAddr(from)}</td>`;
                html += `<td class="mono" style="font-size:9px;">${truncAddr(to)}</td>`;
                html += `<td class="right mono" style="white-space:nowrap;">${esc(valStr)}</td>`;
                html += `<td>${esc(token)}</td>`;
                html += `<td style="font-size:9px;white-space:nowrap;">${esc(ts)}</td>`;
                html += `<td style="font-size:9px;">${esc(entityName)}</td>`;
                html += `<td style="font-size:9px;">${esc(entityType)}</td>`;
                html += '</tr>';
            }
            if (cappedTxs) {
                html += `<tr><td colspan="${displayCols.length}" class="muted" style="text-align:center;font-style:italic;">… ${txs.length - TX_CAP} additional transactions omitted. Full dataset available via JSON export.</td></tr>`;
            }
            html += '</tbody></table></div>';
        }

        html += '</div>';
    }

    return html;
}

/* ── Exchange Detection section render ── */

function renderExchangeDetection(d: any, ev?: EvidenceCounter): string {
    const isExchange = d.is_exchange;
    const eid = ev ? ev.next() : '';
    const verdict = isExchange
        ? `<span style="color:#1d4ed8;font-weight:600;">${esc(d.exchange_name || 'Exchange Wallet')} — ${esc((d.exchange_type || 'hot_wallet').replace('_', ' '))}</span>`
        : '<span style="color:#6b7280;">Not identified as exchange</span>';

    let html = `<p>${eid}<strong>Verdict:</strong> ${verdict}</p>`;
    const confidence = typeof d.confidence === 'number' ? `${(d.confidence * 100).toFixed(1)}%` : '—';
    html += `<p><strong>Confidence:</strong> ${confidence} &nbsp;·&nbsp; <strong>Source:</strong> ${esc(d.source || '—')}</p>`;
    // M7: warn when exchange verdict has very low confidence
    if (isExchange && typeof d.confidence === 'number' && d.confidence < 0.2) {
        html += `<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:4px;padding:6px 10px;margin:6px 0;font-size:10px;color:#92400e;">
            &#9888; Low confidence (${(d.confidence * 100).toFixed(0)}%) — this identification may be unreliable. Verify with independent sources before citing in reports.</div>`;
    }

    if (d.evidence) {
        html += `<p style="color:#374151;font-size:11px;margin-top:4px;">${esc(d.evidence)}</p>`;
    }

    const signalKeys = ['high_fan_in', 'high_fan_out', 'pass_through_rate', 'consolidation', 'gas_station', 'volume_throughput'];
    const signals = d.signals || {};
    const hasSignals = signalKeys.some(k => typeof signals[k] === 'number');

    if (d.source === 'heuristic' && hasSignals) {
        html += '<h4 style="margin:10px 0 4px;font-size:11px;color:#374151;">Behavioral Signal Scores</h4>';
        html += '<table class="data-full"><thead><tr><th>Signal</th><th>Score</th><th>Assessment</th></tr></thead><tbody>';
        for (const k of signalKeys) {
            if (typeof signals[k] !== 'number') continue;
            const pct = (signals[k] * 100).toFixed(0);
            const strength = signals[k] >= 0.7 ? 'Strong' : signals[k] >= 0.4 ? 'Moderate' : 'Low';
            html += `<tr><td>${esc(k.replace(/_/g, ' '))}</td><td class="mono right">${pct}%</td><td>${strength}</td></tr>`;
        }
        html += '</tbody></table>';

        // Metrics table
        const metricKeys = ['total_txs', 'unique_senders', 'unique_receivers', 'fan_in_ratio', 'fan_out_ratio'];
        const hasMetrics = metricKeys.some(k => signals[k] !== undefined);
        if (hasMetrics) {
            html += '<h4 style="margin:10px 0 4px;font-size:11px;color:#374151;">Transaction Metrics</h4>';
            html += '<table class="data-full"><thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>';
            for (const k of metricKeys) {
                if (signals[k] === undefined) continue;
                const v = typeof signals[k] === 'number' && !Number.isInteger(signals[k])
                    ? signals[k].toFixed(3)
                    : typeof signals[k] === 'object' ? JSON.stringify(signals[k]) : String(signals[k]);
                html += `<tr><td>${esc(k.replace(/_/g, ' '))}</td><td class="mono right">${esc(v)}</td></tr>`;
            }
            html += '</tbody></table>';
        }
    }

    return html;
}

/* ── Section config ── */

const SECTIONS: { key: InvestigationKey; title: string; render: (d: any, ev?: EvidenceCounter) => string }[] = [
    { key: 'exchange_detection', title: 'Exchange Detection', render: renderExchangeDetection },
    { key: 'signal_assessment', title: 'Signal Convergence Assessment', render: renderSignalAssessment },
    { key: 'velocity', title: 'Velocity Analysis', render: renderVelocity },
    { key: 'counterparty_triage', title: 'Counterparty Analysis', render: renderCounterparties },
    { key: 'backward_trace', title: 'Backward Fund-Source Trace', render: renderBackwardTrace },
    { key: 'forward_trace', title: 'Forward Destination Trace', render: renderForwardTrace },
    { key: 'multi_hop_exposure', title: 'Multi-Hop Exposure', render: renderMultiHopExposure },
    { key: 'adaptive_transactions', title: 'Transaction Fetch Summary', render: renderAdaptiveTransactions },
];

/* ── Executive Summary (template-based, deterministic, Tier 2) ── */

/* ── Coverage and Limitations (NS-34) ── */

export interface CoverageIssue {
    step: string;
    label: string;
    status: string;
    reason: string;
}

export interface CoverageIssues {
    /** Backend coverage record for this run (null when the run did not record one). */
    recorded: InvestigationCoverage | null;
    missing: CoverageIssue[];
    degraded: CoverageIssue[];
}

const TRACE_PARTIAL_REASONS = new Set(['time_budget', 'limit_reached']);

/**
 * Steps that did not finish normally, from data only: the backend coverage record
 * (carried on the signal assessment) first, then what each section itself says
 * (failed, degraded, or a trace that stopped on a limit). One entry per step.
 */
export function collectCoverageIssues(sections: InvestigationSections): CoverageIssues {
    const recorded = normalizeCoverage(sections?.signal_assessment?.data?.coverage);
    const seen = new Set<string>();
    const missing: CoverageIssue[] = [];
    const degraded: CoverageIssue[] = [];
    const add = (list: CoverageIssue[], step: string, status: string, reason: string) => {
        if (seen.has(step)) return;
        seen.add(step);
        list.push({ step, label: step === 'stream' ? 'Investigation stream' : coverageStepLabel(step), status, reason });
    };

    for (const m of recorded?.missing_steps || []) {
        add(missing, m.step, 'skipped', plainStepReason('skipped', m.reason));
    }
    for (const d of recorded?.degraded_steps || []) {
        add(degraded, d.step, d.status || 'degraded', plainStepReason(d.status, d.reason));
    }

    const anyStarted = (Object.keys(sections || {}) as InvestigationKey[])
        .some((k) => k !== 'signal_assessment' && sections[k]?.status && sections[k].status !== 'idle');
    for (const key of Object.keys(sections || {}) as InvestigationKey[]) {
        const sec = sections[key];
        if (!sec) continue;
        if (sec.status === 'idle') {
            if (key === 'signal_assessment' && anyStarted) {
                add(missing, key, 'skipped', 'Did not run: the signal assessment never started');
            }
            continue;
        }
        if (sec.status === 'error') {
            add(degraded, key, 'error', sec.degradedReason || plainStepReason('error', sec.error));
            continue;
        }
        if (sec.status !== 'loaded') continue;
        if (sec.health === 'degraded') {
            add(degraded, key, 'degraded', sec.degradedReason || 'Partial data');
            continue;
        }
        // Saved or hydrated sections may lack the health flag: read the data itself.
        const d = sec.data;
        const stop = d?.exploration_summary?.stop_reason;
        if (d && (d.status === 'partial' || TRACE_PARTIAL_REASONS.has(stop))) {
            const n = d.exploration_summary?.addresses_fetched ?? d.exploration_summary?.total_nodes_explored;
            add(degraded, key, 'partial',
                `Partial result: stopped because ${stopReasonText(stop || 'partial')}${typeof n === 'number' ? ` after ${n} addresses` : ''}`);
        } else if (d && (d.tested === false || d.transactions_unavailable === true)) {
            add(degraded, key, 'unavailable', plainStepReason('unavailable', String(d.not_tested_reason || 'transactions unavailable')));
        }
    }
    return { recorded, missing, degraded };
}

function covTable(rows: string[][], head: string[]): string {
    return `<table class="data" style="font-size:11px;"><thead><tr>${head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead><tbody>`
        + rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')
        + '</tbody></table>';
}

/**
 * Recheck-gate verdicts (WB09): the backend's conservation and per-field-source
 * gates, and the rest of the gate set, as a verdict table. A gate that checked
 * nothing prints NOT TESTED, never PASS (R10); a failed gate is listed, not hidden.
 */
export function renderRecheckGateVerdicts(gates: unknown): string {
    const g = normalizeGates(gates);
    const h = '<h3 style="margin:14px 0 6px;font-size:13px;color:#1e293b;">Recheck gates</h3>';
    if (!g || g.rows.length === 0) {
        return h + '<p style="font-size:12px;color:#64748b;margin:0;">The recheck gates (value conservation, per-field source) did not run for this investigation, so neither the fund-flow figures nor the sourcing of stated fields was machine-checked. This is not a clean result.</p>';
    }
    const label = (st: string) => st === 'passed' ? 'Passed' : st === 'failed' ? 'FAILED' : st === 'not_tested' ? 'Not tested' : 'Not available';
    const order = ['conservation', 'per_field_source'];
    const rows = [...g.rows].sort((a, b) => {
        const ia = order.indexOf(a.name), ib = order.indexOf(b.name);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    const body = rows.map((r) => {
        const shown = r.failures.slice(0, 5);
        const more = Math.max(0, r.failureCount - shown.length);
        const detail = shown.length
            ? shown.map((f) => esc(f)).join('<br/>') + (more ? `<br/>and ${more} more` : '')
            : (r.status === 'failed' ? 'No detail recorded' : '');
        return [esc(r.name.replace(/_/g, ' ')), esc(label(r.status)), typeof r.checked === 'number' ? String(r.checked) : 'n/a', detail];
    });
    const intro = g.overall === 'failed'
        ? 'One or more gates failed. The figures they cover should not be relied on until the failures are resolved or explained.'
        : g.overall === 'passed'
            ? 'Every gate that had something to check passed. A gate marked Not tested had nothing to check.'
            : 'No gate had anything to check in this run. That is not a clean result.';
    return h + `<p style="font-size:12px;color:#475569;margin:0 0 6px;">${esc(intro)}</p>`
        + covTable(body, ['Gate', 'Result', 'Checks', 'Detail']);
}

function renderCoverageAndLimitations(sections: InvestigationSections, triage: any, gates?: unknown): string {
    const sa = sections.signal_assessment;
    const saData = sa?.status === 'loaded' ? sa.data : null;
    const issues = collectCoverageIssues(sections);
    const h = (t: string) => `<h3 style="margin:14px 0 6px;font-size:13px;color:#1e293b;">${esc(t)}</h3>`;
    const none = (t: string) => `<p style="font-size:12px;color:#64748b;margin:0;">${esc(t)}</p>`;
    let html = '<p style="font-size:12px;color:#475569;margin:0 0 4px;">This section lists, from the investigation data only, what the report did not or could not cover. A check that did not run is not a clean result.</p>';

    // 1. Steps missing or degraded
    html += h('Investigation steps');
    const all = [...issues.missing, ...issues.degraded];
    if (all.length > 0) {
        html += covTable(
            all.map((i) => [esc(i.label), esc(i.status === 'skipped' ? 'Missing' : i.status.charAt(0).toUpperCase() + i.status.slice(1)), esc(i.reason)]),
            ['Step', 'Status', 'Reason'],
        );
    } else if (issues.recorded) {
        html += none(`No step was recorded as missing or degraded${issues.recorded.steps_completed !== null ? ` (${issues.recorded.steps_completed} backend steps produced a result)` : ''}.`);
    } else {
        html += none('No step coverage record was received for this run, and no section reports a failure.');
    }

    // 2. NOT TESTED signal families
    html += h('Signal families not tested');
    const families = saData?.signal_families as Record<string, any> | undefined;
    if (!saData) {
        html += none('The signal assessment did not load, so no signal family was evaluated.');
    } else if (!families || typeof families !== 'object') {
        html += none('The signal assessment did not return per-family results, so which families were tested is not recorded.');
    } else {
        const untested = Object.entries(families).filter(([, f]) => f && f.triggered === null);
        if (untested.length === 0) {
            html += none('Every signal family was evaluated (none is marked not tested).');
        } else {
            html += covTable(
                untested.map(([k, f]) => [
                    esc(k.replace(/_/g, ' ')),
                    esc(formatNotTestedReason(f.not_tested_reason, '') .replace(/^:\s*/, '')),
                ]),
                ['Family', 'Why it was not tested'],
            );
        }
    }

    // 3. Partial traces
    html += h('Trace coverage');
    const traceRows: string[][] = [];
    for (const [key, label] of [['backward_trace', 'Backward trace'], ['forward_trace', 'Forward trace']] as const) {
        const sec = sections[key];
        if (!sec || sec.status === 'idle') continue;
        if (sec.status !== 'loaded' || !sec.data) {
            traceRows.push([label, 'Did not run', esc(sec.degradedReason || sec.error || 'No trace data')]);
            continue;
        }
        const exp = sec.data.exploration_summary;
        const stop = exp?.stop_reason;
        const explored = exp?.total_nodes_explored ?? exp?.addresses_fetched;
        if (!exp) {
            traceRows.push([label, 'No exploration record', 'The trace returned no exploration summary; treat it as not run.']);
        } else if (sec.data.status === 'partial' || TRACE_PARTIAL_REASONS.has(stop)) {
            traceRows.push([label, 'Partial', `Stopped because ${esc(stopReasonText(stop || 'partial'))}; ${explored ?? '?'} addresses explored. Paths beyond this point were not searched.`]);
        } else {
            traceRows.push([label, 'Complete', `Ended because ${esc(stopReasonText(stop || 'complete'))}; ${explored ?? '?'} addresses explored.`]);
        }
    }
    html += traceRows.length ? covTable(traceRows, ['Trace', 'State', 'Detail']) : none('No trace section was run.');

    // 4. Sanctions screening
    html += h('Sanctions screening');
    const cp = sections.counterparty_triage?.status === 'loaded' ? sections.counterparty_triage.data : null;
    if (!cp) {
        html += none('Counterparty triage did not load, so counterparties were not screened against sanctions lists.');
    } else {
        const notChecked = cp.sanctions_not_checked_count;
        const failedN = cp.sanctions_check_failed_count;
        const beyond = cp.sanctions_beyond_top_n_count;
        const limit = cp.sanctions_screen_limit;
        if (typeof notChecked !== 'number') {
            html += none('The count of counterparties that were not sanctions-screened was not recorded for this run.');
        } else {
            html += `<p style="font-size:12px;color:#334155;margin:0;"><strong>${notChecked}</strong> of ${cp.total_counterparties ?? '?'} counterparties were not sanctions-screened`
                + ` (${typeof failedN === 'number' ? failedN : '?'} screen failures, ${typeof beyond === 'number' ? beyond : '?'} ranked below the screening limit).`
                + ` The screen covers the top ${typeof limit === 'number' ? limit : '(limit not recorded)'} counterparties by volume.`
                + (notChecked > 0 ? ' An unscreened counterparty is not shown as clear.' : '') + '</p>';
        }
    }

    // 5. Transaction windows
    html += h('Transaction windows by section');
    const vel = sections.velocity?.status === 'loaded' ? sections.velocity.data : null;
    const ad = sections.adaptive_transactions?.status === 'loaded' ? sections.adaptive_transactions.data : null;
    const mh = sections.multi_hop_exposure?.status === 'loaded' ? sections.multi_hop_exposure.data : null;
    const txSummary = saData?.transaction_summary;
    const winRows: string[][] = [];
    const num = (v: any): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const velN = num(vel?.transaction_count);
    const trN = num(cp?.analysis_tx_count);
    const adN = num(ad?.total_fetched);
    const period = vel?.analysis_period;
    winRows.push(['Velocity Profile', velN !== null ? String(velN) : 'not recorded',
        period?.start && period?.end ? `${esc(fmtTs(period.start))} to ${esc(fmtTs(period.end))}` : 'period not recorded']);
    winRows.push(['Top Counterparties', trN !== null ? String(trN) : 'not recorded', 'transactions read for triage']);
    winRows.push(['Adaptive Transactions', adN !== null ? String(adN) : 'not recorded',
        ad?.fetch_rounds ? `${ad.fetch_rounds} fetch round(s)${ad.stop_reason ? `, stopped: ${esc(String(ad.stop_reason))}` : ''}` : '']);
    const mhN = num(mh?.transaction_count) ?? num(mh?.transactions_analyzed);
    winRows.push(['Multi-Hop Exposure', mhN !== null ? String(mhN) : 'not recorded', mh ? 'per-hop transaction limits are not recorded in the result' : 'section not loaded']);
    for (const [key, label] of [['backward_trace', 'Backward Trace'], ['forward_trace', 'Forward Trace']] as const) {
        const d = sections[key]?.status === 'loaded' ? sections[key].data : null;
        const e = d?.exploration_summary;
        winRows.push([label, d ? String(num(e?.total_edges_explored) ?? num(d.total_edges) ?? 'not recorded') : 'not run',
            d ? `${num(e?.total_nodes_explored) ?? '?'} addresses explored` : '']);
    }
    html += covTable(winRows, ['Section', 'Transactions', 'Window']);
    const counts = [velN, trN, adN].filter((n): n is number => n !== null);
    if (new Set(counts).size > 1) {
        html += '<p style="font-size:11px;color:#92400e;margin:6px 0 0;">The sections above read different numbers of transactions. Figures from different sections are not computed over the same window and should not be compared directly.</p>';
    }
    if (txSummary && typeof txSummary === 'object') {
        html += `<p style="font-size:11px;color:#64748b;margin:6px 0 0;">Backend transaction summary: ${Object.entries(txSummary).filter(([, v]) => typeof v === 'number' || typeof v === 'string').map(([k, v]) => `${esc(k.replace(/_/g, ' '))} ${esc(String(v))}`).join(', ')}.</p>`;
    }

    // 6. Provider health
    html += h('Data provider health');
    html += renderProviderHealthBlock(saData?.provider_health);

    // 7. Recheck gates (WB09)
    html += renderRecheckGateVerdicts(gates);
    return html;
}

export function buildExecutiveSummary(
    chain: string | null,
    address: string | null,
    triage: any,
    escalation: string | null,
    familiesTriggered: number | null,
    familiesTested: number | null,
    velocityProfile: string | null,
    velocityRisk: number | null,
    sections: InvestigationSections,
): string {
    const parts: string[] = [];

    // What is this address?
    const entity = triage?.summary?.entity_name;
    const canonicalRisk = resolveCanonicalRisk(sections, triage);
    const velData = sections?.velocity?.data;
    const velMetrics = velData?.data?.metrics || velData?.metrics || velData;
    // Prefer analyzed count (what we actually processed) over on-chain total
    // (which may differ due to pagination, date filters, or API limits)
    const txCount = sections?.adaptive_transactions?.data?.total_fetched
        ?? velMetrics?.transaction_count
        ?? velMetrics?.total_transactions
        ?? sections?.adaptive_transactions?.data?.transactions?.length
        ?? sections?.counterparty_triage?.data?.analysis_tx_count
        ?? triage?.summary?.tx_count
        ?? triage?.details?.address_info?.tx_count;
    const balance = triage?.summary?.balance;
    const sanctioned = triage?.summary?.sanctioned;

    // V4-H3: Confidence-gate entity display in executive summary
    const execEntityConf = triage?.details?.entity?.confidence ?? triage?.summary?.entity_confidence ?? 1.0;
    if (entity && entity !== 'Unknown' && execEntityConf >= 0.3) {
        const entitySource = triage?.details?.entity?.source || '';
        if (entitySource === 'heuristic') {
            parts.push(`The target address is heuristically classified as <strong>${esc(entity)}</strong> on the ${esc(chain || 'unknown')} blockchain (behavioral pattern match, not verified attribution).`);
        } else {
            parts.push(`The target address is identified as <strong>${esc(entity)}</strong> on the ${esc(chain || 'unknown')} blockchain.`);
        }
    } else if (entity && entity !== 'Unknown' && execEntityConf < 0.3) {
        parts.push(`The target address on the ${esc(chain || 'unknown')} blockchain has a possible entity match (<em>${esc(entity)}</em>, confidence ${(execEntityConf * 100).toFixed(0)}%) — unverified, not used for classification.`);
    } else {
        parts.push(`The target address on the ${esc(chain || 'unknown')} blockchain is <strong>not attributed to a known entity</strong>.`);
    }

    if (sanctioned) {
        parts.push(`The address is <strong>flagged on the OFAC SDN sanctions list</strong> (${esc(triage?.summary?.sanctions_entity || 'details available below')}).`);
    }

    // Risk
    if (canonicalRisk.score !== null) {
        const sourceLabel =
            canonicalRisk.source === 'investigation' ? 'Investigation analysis' :
            canonicalRisk.source === 'behavioral_override' ? 'Behavioral analysis (overrides triage)' :
            canonicalRisk.source === 'sanctions_floor' ? 'Sanctions match (risk floored to HIGH)' :
            'Automated triage';
        parts.push(`${sourceLabel} assigns a composite risk score of <strong>${(canonicalRisk.score * 100).toFixed(0)}%</strong> (${esc(canonicalRisk.level)}).`);
        if ((canonicalRisk.source === 'behavioral_override' || canonicalRisk.source === 'sanctions_floor') && canonicalRisk.override_reasons?.length) {
            // Surface WHY the score was bumped, so the officer doesn't see a
            // higher score than triage and wonder where it came from.
            for (const r of canonicalRisk.override_reasons) parts.push(esc(r));
        }
    } else if (canonicalRisk.level === 'INSUFFICIENT DATA') {
        parts.push(`Risk assessment: <strong>INSUFFICIENT DATA</strong> — the investigation could not retrieve transaction data from the blockchain API.`);
    }

    // Activity — V4-M4: suppress "0 transactions" when txCount fell through to 0;
    // use counterparty count as a fallback indicator of activity
    const activityParts: string[] = [];
    const effectiveTxCount = (typeof txCount === 'number' && txCount > 0)
        ? txCount
        : (sections?.counterparty_triage?.status === 'loaded'
            ? (sections.counterparty_triage.data?.counterparties?.length || 0)
            : null);
    if (typeof effectiveTxCount === 'number' && effectiveTxCount > 0) {
        activityParts.push(`${effectiveTxCount.toLocaleString()} transactions`);
    }
    if (typeof balance === 'number') activityParts.push(`current balance ${balance.toFixed(4)} ${esc(chain || '')}`);
    if (activityParts.length > 0) {
        parts.push(`The address shows ${activityParts.join(' with ')}.`);
    }

    // Analysis period (m3/m12 — critical temporal context)
    // Primary: velocity data.  Fallback: derive from adaptive_transactions or counterparty timestamps.
    const velSection = sections.velocity;
    let analysisPeriod = velSection?.status === 'loaded'
        ? velSection.data?.analysis_period : null;
    if (!analysisPeriod?.start || !analysisPeriod?.end) {
        // Fallback: try to extract min/max timestamps from adaptive_transactions or counterparty data
        const atSection = sections.adaptive_transactions;
        const txList = atSection?.status === 'loaded' ? (atSection.data?.transactions || []) : [];
        if (txList.length > 0) {
            const timestamps = (txList as any[])
                .map((tx: any) => tx.timestamp || tx.block_timestamp || tx.time)
                .filter(Boolean)
                .map((ts: any) => typeof ts === 'number' ? (ts > 1e12 ? ts : ts * 1000) : new Date(String(ts)).getTime())
                .filter((t: number) => !isNaN(t));
            if (timestamps.length > 0) {
                analysisPeriod = {
                    start: new Date(Math.min(...timestamps)).toISOString(),
                    end: new Date(Math.max(...timestamps)).toISOString(),
                    span_days: Math.ceil((Math.max(...timestamps) - Math.min(...timestamps)) / 86400000),
                };
            }
        }
    }
    if (analysisPeriod?.start && analysisPeriod?.end) {
        // V4-M4: "0 days" → "1 day (single-day activity)" for same-day spans
        let spanText = '';
        if (typeof analysisPeriod.span_days === 'number') {
            spanText = analysisPeriod.span_days === 0
                ? ' spanning 1 day (single-day activity window)'
                : ` spanning ${analysisPeriod.span_days} days`;
        }
        parts.push(`Analysis covers on-chain activity from ${fmtTs(analysisPeriod.start)} to ${fmtTs(analysisPeriod.end)}${spanText}.`);
    }

    // Velocity (m6: context-aware classification when exchange counterparties exist)
    if (velocityProfile) {
        const cpSection = sections.counterparty_triage;
        const hasExchangeCounterparty = cpSection.status === 'loaded' && (cpSection.data?.counterparties as any[] || []).some((c: any) => {
            if (c.category === 'exchange' || c.entity_type === 'exchange' || c.category === 'high_risk_exchange' || c.entity_type === 'high_risk_exchange') return true;
            const name = (c.entity_name || '').toLowerCase();
            return /deposit|withdraw|exchange|binance|coinbase|kraken|hitbtc|bybit|okx|changenow|changehero/i.test(name);
        });
        const profileDesc: Record<string, string> = {
            'Relay': hasExchangeCounterparty
                ? 'Funds pass through rapidly with minimal dwell time. Known exchange counterparties suggest this address functions as an intermediary transacting with exchanges.'
                : 'Funds pass through rapidly with minimal dwell time, consistent with a relay or intermediary pattern.',
            'Distributor': 'Outbound value significantly exceeds inbound from immediate sources, indicating distribution activity.',
            'Accumulator': 'Inbound flows dominate with limited outbound, indicating accumulation behavior.',
            'Mixed': 'No single dominant flow pattern detected.',
            'Dormant': 'Minimal transaction activity observed.',
        };
        const article = /^[aeiou]/i.test(velocityProfile) ? 'an' : 'a';
        parts.push(`Velocity analysis classifies this address as ${article} <strong>${esc(velocityProfile)}</strong>. ${profileDesc[velocityProfile] || ''}`);
    }

    // Signal convergence
    if (familiesTriggered !== null && familiesTested !== null) {
        parts.push(`Signal convergence analysis identified triggers in <strong>${familiesTriggered} of ${familiesTested}</strong> signal families tested.`);
    }

    // Escalation recommendation
    if (escalation === 'ESCALATE') {
        parts.push('Based on multi-signal convergence, this address is <strong>recommended for escalated investigation</strong>.');
    } else if (escalation === 'INVESTIGATE') {
        parts.push('Further investigation is recommended to clarify the risk profile.');
    } else if (escalation === 'INSUFFICIENT_DATA') {
        parts.push('Risk assessment: <strong>INSUFFICIENT DATA</strong> — the investigation could not retrieve transaction data from the blockchain API. Re-run after confirming API connectivity.');
    } else if (escalation === 'MONITOR') {
        parts.push('The current evidence supports monitoring rather than immediate escalation.');
    }

    // Notable Findings — surface SEVERE/CRITICAL counterparties + signal-emitted
    // callouts (Tether-blacklisted, sanctions, high-risk-category exposure)
    // *regardless of magnitude*. The 2026-05-05 v6 report buried a $145k SEVERE
    // Tether-blacklisted counterparty in Appendix C because the exec-summary
    // builder only sorted by volume; a $1M Bybit hit (HIGH risk) drowned it
    // out. selectNotableCallouts collects high-tier callouts up front so they
    // never get lost in the noise.
    {
        const cps = (sections.counterparty_triage?.status === 'loaded'
            ? (sections.counterparty_triage.data?.counterparties || sections.counterparty_triage.data?.ranked || [])
            : []) as CounterpartyPrimitive[];
        const sigFamilies = sections.signal_assessment?.data?.signal_families as Record<string, any> | undefined;
        const sigCallouts: NotableCallout[] = [];
        if (sigFamilies) {
            for (const fam of Object.values(sigFamilies)) {
                const callouts = (fam as any)?.notable_callouts || [];
                for (const c of callouts) sigCallouts.push(c as NotableCallout);
            }
        }
        const notable = selectNotableCallouts(cps, sigCallouts, 5);
        if (notable.length > 0) {
            const items = notable.map(n => `<li>${esc(n.reason)}</li>`).join('');
            parts.push(`<div style="margin-top:8px;padding:10px 12px;background:#fef2f2;border:1px solid #fca5a5;border-radius:4px;"><strong style="color:#991b1b;">Notable Findings</strong><ul style="margin:6px 0 0 18px;padding:0;font-size:11px;color:#7f1d1d;">${items}</ul></div>`);
        }
    }

    // Named counterparties from counterparty_triage (M5)
    // D9: Filter out spam token contracts — they have entity_name matching
    // their token symbol (e.g. "xAI", "BUN") but entity_type is "unknown".
    // Only show counterparties with real entity DB attributions.
    const cp = sections.counterparty_triage;
    if (cp.status === 'loaded' && cp.data?.counterparties) {
        const named = (cp.data.counterparties as any[])
            .filter((c: any) => {
                if (isSpamCounterparty(c)) return false;
                if (!c.entity_name || c.entity_name === 'Unknown') return false;
                // D9: Reject spam token contracts — entity_name is their token
                // symbol (e.g. "xAI", "BUN") with no real entity attribution.
                // Use normalizeEntityType to recover "labeled" entities (Arkham
                // returns entity_type="labeled" for ChangeNOW, Shakepay, etc.)
                const rawType = (c.entity_type || '').toLowerCase();
                const etype = normalizeEntityType(rawType, c.entity_name);
                if (!etype || etype === 'unknown') return false;
                return true;
            })
            .sort((a: any, b: any) => (Number(b.total_volume ?? b.volume) || 0) - (Number(a.total_volume ?? a.volume) || 0));
        const totalNamed = named.length;
        const topNamed = named.slice(0, 5);
        if (topNamed.length > 0) {
            const chainLabel = (chain || '').toUpperCase() || 'units';
            const cpDescs = topNamed.map((c: any) => {
                const primaryVol = Number(c.primary_volume) || 0;
                const primaryTok = c.primary_token || chainLabel;
                const vol = primaryVol > 0 ? primaryVol : (Number(c.total_volume ?? c.volume) || 0);
                const label = primaryVol > 0 ? primaryTok : chainLabel;
                const volStr = vol > 0
                    ? ` (${vol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })} ${esc(label)})`
                    : '';
                return `<strong>${esc(cleanEntityName(c.entity_name))}</strong>${volStr}`;
            });
            const moreText = totalNamed > topNamed.length ? ` (showing top ${topNamed.length} of ${totalNamed})` : '';
            parts.push(`Direct counterparty analysis identified ${totalNamed} known entit${totalNamed > 1 ? 'ies' : 'y'}${moreText}: ${cpDescs.join(', ')}.`);
        }
    }

    // Notable indirect exposures from multi_hop_exposure (M6)
    const mhe = sections.multi_hop_exposure;
    if (mhe.status === 'loaded' && mhe.data?.indirect_exposure) {
        const notable = (mhe.data.indirect_exposure as any[])
            .filter((e: any) => e.entity_name && e.entity_name !== 'Unknown')
            .slice(0, 3);
        if (notable.length > 0) {
            const expDescs = notable.map((e: any) =>
                `<strong>${esc(cleanEntityName(e.entity_name))}</strong> (hop ${e.hop ?? '?'}, ${esc(e.category || 'unknown')})`
            );
            parts.push(`Multi-hop exposure analysis found indirect connections to: ${expDescs.join(', ')}.`);
        }
    }

    // Exchange proximity
    const exchange = triage?.summary?.nearest_exchange;
    if (exchange?.name) {
        parts.push(`The nearest identified exchange is <strong>${esc(cleanEntityName(exchange.name))}</strong> (${exchange.hops ?? '?'} hops).`);
    }

    // Backward trace summary
    const bt = sections.backward_trace;
    if (bt.status === 'loaded' && bt.data) {
        const paths = bt.data.paths || bt.data.traces || [];
        const btExp = bt.data.exploration_summary;
        const btEntities = bt.data.source_entities || [];
        if (paths.length > 0) {
            // Compute value coverage: sum of hop-1 edge values per token.
            // Avoids cross-token sums (TRX path value / USDT inflow = meaningless).
            // Sum hop-1 edge values per token (avoid cross-token sums)
            const hop1ByToken: Record<string, number> = {};
            for (const p of paths) {
                const edges = p.edges || [];
                if (edges.length > 0) {
                    const tok = edges[0]?.token_symbol || 'native';
                    const val = Math.abs(Number(edges[0]?.total_value ?? edges[0]?.value) || 0);
                    hop1ByToken[tok] = (hop1ByToken[tok] || 0) + val;
                }
            }
            // Total inflow per token from counterparty data
            const inflowByToken: Record<string, number> = {};
            const cpData = sections.counterparty_triage;
            if (cpData.status === 'loaded' && cpData.data?.counterparties) {
                for (const c of cpData.data.counterparties as any[]) {
                    const tok = (c as any).primary_token || 'native';
                    inflowByToken[tok] = (inflowByToken[tok] || 0) + Number(c.volume_in ?? 0);
                }
            }
            // Compute coverage for the primary token (stablecoin preferred)
            const stables = new Set(['USDT', 'USDC', 'DAI', 'BUSD']);
            const tokens = Object.keys(hop1ByToken);
            const primaryTok = tokens.find(t => stables.has(t)) || tokens[0] || 'native';
            const pathValueSum = hop1ByToken[primaryTok] || 0;
            // SAME-TOKEN only: dividing the primary token's hop-1 value by a
            // cross-token inflow sum (USDT+TRX+ETH) is meaningless (R15). If the
            // primary token has no matching counterparty-inflow figure, we can't
            // compute an honest coverage % — show none rather than a bogus ratio.
            const totalInflow = inflowByToken[primaryTok] ?? 0;
            const coveragePct = totalInflow > 0 ? Math.min(100, Math.round((pathValueSum / totalInflow) * 100)) : null;
            const coverageNote = coveragePct !== null
                ? ` Value coverage: ~${coveragePct}% of total inflow traced to source.`
                : '';
            parts.push(`Backward fund-source tracing identified ${paths.length} path${paths.length > 1 ? 's' : ''} to potential source entities.${coverageNote}`);
        } else if (btEntities.length > 0) {
            // BFS found entities but Neo4j path extraction didn't produce formal paths
            const entityNames = btEntities
                .map((e: any) => e.entity_name || e.entity || e.name)
                .filter(Boolean)
                .slice(0, 3);
            const namesStr = entityNames.length > 0
                ? `: ${entityNames.map((n: string) => `<strong>${esc(n)}</strong>`).join(', ')}`
                : '';
            parts.push(
                `Backward trace discovered ${btEntities.length} source entit${btEntities.length > 1 ? 'ies' : 'y'}${namesStr}` +
                ` across ${btExp?.bfs_depth_reached ?? '?'} hops (${btExp?.total_nodes_explored ?? '?'} addresses explored).`
            );
        } else if (btExp && btExp.total_nodes_explored > 0) {
            if (btExp.stop_reason === 'stop_entity') {
                parts.push(`Backward trace reached known entities at hop ${btExp.bfs_depth_reached}, exploring ${btExp.total_nodes_explored} addresses.`);
            } else {
                const fanOutNote = (btExp.addresses_fetched > 50 && btExp.bfs_depth_reached <= 1)
                    ? ' (high fan-out — address has many counterparties, limiting traversal depth)' : '';
                parts.push(`Backward trace explored ${btExp.total_nodes_explored} addresses across ${btExp.bfs_depth_reached} hops but did not reach a known source entity within the search budget${fanOutNote}.`);
            }
        }
    } else if (bt.status === 'error') {
        parts.push(`Backward fund-source tracing did not complete (${esc(bt.degradedReason || bt.error || 'no reason recorded')}).`);
    }

    // Forward trace summary
    const ft = sections.forward_trace;
    if (ft.status === 'loaded' && ft.data) {
        const ftPaths = ft.data.paths || ft.data.traces || [];
        const ftExp = ft.data.exploration_summary;
        const ftEntities = ft.data.target_entities || [];
        if (ftPaths.length > 0) {
            parts.push(`Forward fund-destination tracing identified ${ftPaths.length} path${ftPaths.length > 1 ? 's' : ''} to known destination entities.`);
        } else if (ftEntities.length > 0) {
            // BFS found entities but Neo4j path extraction didn't produce formal paths
            const entityNames = ftEntities
                .map((e: any) => e.entity_name || e.entity || e.name)
                .filter(Boolean)
                .slice(0, 3);
            const namesStr = entityNames.length > 0
                ? `: ${entityNames.map((n: string) => `<strong>${esc(n)}</strong>`).join(', ')}`
                : '';
            parts.push(
                `Forward trace discovered ${ftEntities.length} destination entit${ftEntities.length > 1 ? 'ies' : 'y'}${namesStr}` +
                ` across ${ftExp?.bfs_depth_reached ?? '?'} hops (${ftExp?.total_nodes_explored ?? '?'} addresses explored).`
            );
        } else if (ftExp && ftExp.total_nodes_explored > 0) {
            if (ftExp.stop_reason === 'stop_entity') {
                parts.push(`Forward trace reached known entities at hop ${ftExp.bfs_depth_reached}, exploring ${ftExp.total_nodes_explored} addresses.`);
            } else {
                const fanOutNote = (ftExp.addresses_fetched > 50 && ftExp.bfs_depth_reached <= 1)
                    ? ' (high fan-out — address has many counterparties, limiting traversal depth)' : '';
                parts.push(`Forward trace explored ${ftExp.total_nodes_explored} addresses across ${ftExp.bfs_depth_reached} hops but did not reach a known destination entity within the search budget${fanOutNote}.`);
            }
        }
    } else if (ft.status === 'error') {
        parts.push(`Forward fund-destination tracing did not complete (${esc(ft.degradedReason || ft.error || 'no reason recorded')}).`);
    }

    // Scan trace paths for high-risk entities (scam, mixer, sanctioned) and add prominent warnings
    const riskEntities: { name: string; category: string; direction: string; hop?: number }[] = [];
    const HIGH_RISK_CATS = ['scam', 'mixer', 'tumbler', 'ransomware', 'sanctioned', 'phishing', 'fraud', 'darknet_market'];
    const SCAM_KEYWORDS = ['chainabuse', 'scam', 'hack', 'phish', 'fraud', 'steal', 'exploit'];

    const _scanPaths = (paths: any[], entities: any[], direction: string) => {
        // Scan path hop_details for scam/risk entities
        for (const path of (paths || [])) {
            for (const hop of (path.hop_details || [])) {
                const eName = (hop.entity_name || hop.entity || '').toString();
                const eCat = (hop.entity_type || '').toLowerCase();
                const isHighRisk = HIGH_RISK_CATS.includes(eCat) ||
                    SCAM_KEYWORDS.some(kw => eName.toLowerCase().includes(kw));
                if (isHighRisk && eName) {
                    riskEntities.push({ name: eName, category: eCat || 'flagged', direction, hop: hop.hop });
                }
            }
            // Also check target_entity on the path itself
            if (path.target_entity) {
                const tName = path.target_entity.name || path.target_entity.entity_name || '';
                const tCat = (path.target_entity.type || path.target_entity.entity_type || '').toLowerCase();
                const isHighRisk = HIGH_RISK_CATS.includes(tCat) ||
                    SCAM_KEYWORDS.some(kw => tName.toLowerCase().includes(kw));
                if (isHighRisk && tName) {
                    riskEntities.push({ name: tName, category: tCat || 'flagged', direction });
                }
            }
        }
        // Scan standalone entities
        for (const ent of (entities || [])) {
            const eName = (ent.entity_name || ent.name || ent.entity || '').toString();
            const eCat = (ent.type || ent.entity_type || ent.category || '').toLowerCase();
            const isHighRisk = HIGH_RISK_CATS.includes(eCat) ||
                SCAM_KEYWORDS.some(kw => eName.toLowerCase().includes(kw));
            if (isHighRisk && eName) {
                riskEntities.push({ name: eName, category: eCat || 'flagged', direction });
            }
        }
    };

    if (bt.status === 'loaded' && bt.data) {
        _scanPaths(bt.data.paths || bt.data.traces || [], bt.data.source_entities || [], 'fund-source');
    }
    if (ft.status === 'loaded' && ft.data) {
        _scanPaths(ft.data.paths || ft.data.traces || [], ft.data.target_entities || [], 'fund-destination');
    }

    // Also scan multi-hop indirect exposure for severe/high risk entities
    if (mhe.status === 'loaded' && mhe.data?.indirect_exposure) {
        for (const exp of (mhe.data.indirect_exposure as any[])) {
            const riskTier = (exp.risk_tier || '').toLowerCase();
            const eCat = (exp.category || '').toLowerCase();
            if (riskTier === 'severe' || riskTier === 'high' || HIGH_RISK_CATS.includes(eCat)) {
                const eName = (exp.entity_name || '').toString();
                if (eName && eName !== 'Unknown') {
                    riskEntities.push({ name: eName, category: eCat || 'flagged', direction: 'indirect exposure', hop: exp.hop });
                }
            }
        }
    }

    // Deduplicate and emit warning
    const uniqueRisk = new Map<string, typeof riskEntities[0]>();
    for (const r of riskEntities) {
        if (!uniqueRisk.has(r.name)) uniqueRisk.set(r.name, r);
    }
    if (uniqueRisk.size > 0) {
        const warnings = Array.from(uniqueRisk.values()).slice(0, 5).map(r =>
            `<strong>${esc(cleanEntityName(r.name))}</strong> (${esc(r.category)}, ${esc(r.direction)}${r.hop ? ` hop ${r.hop}` : ''})`
        );
        parts.push(
            `<strong style="color:#991b1b;">⚠ HIGH-RISK ENTITIES DETECTED IN FUND FLOW:</strong> ` +
            warnings.join(', ') + '. These connections warrant immediate investigative attention.'
        );
    }

    // Coverage: one sentence when any step is missing or degraded.
    const cov = collectCoverageIssues(sections);
    const issueCount = cov.missing.length + cov.degraded.length;
    if (issueCount > 0) {
        const names = [...cov.missing, ...cov.degraded].map((i) => esc(i.label));
        const shown = names.slice(0, 4).join(', ') + (names.length > 4 ? `, +${names.length - 4} more` : '');
        parts.splice(1, 0,
            `<strong style="color:#92400e;">Incomplete coverage:</strong> ${issueCount} investigation step${issueCount === 1 ? ' was' : 's were'} missing or degraded (${shown}), so the findings below cover only what completed; see Coverage and Limitations.`);
    }

    return parts.join(' ');
}

/* ── Address Overview (Tier 1 on-chain facts) ── */

function renderAddressOverview(chain: string | null, address: string | null, triage: any, sections: InvestigationSections): string {
    if (!triage?.summary) return '';

    const s = triage.summary;
    const info = triage.details?.address_info || {};
    const canonicalRisk = resolveCanonicalRisk(sections, triage);

    const rows: [string, string][] = [
        ['Address', monoSpan(address || '—')],
        ['Blockchain', (chain || '—').toUpperCase()],
    ];

    // V4-H3: Gate entity display on confidence — suppress low-confidence labels
    // that contradict the exchange_detection verdict (which uses 30% threshold)
    const entityConfidence = triage.details?.entity?.confidence ?? s.entity_confidence ?? 1.0;
    if (s.entity_name && s.entity_name !== 'Unknown' && entityConfidence >= 0.3) {
        rows.push(['Entity', `<strong>${esc(cleanEntityName(s.entity_name))}</strong> (${esc(s.entity_type || 'unknown')})`]);
    } else if (s.entity_name && s.entity_name !== 'Unknown' && entityConfidence < 0.3) {
        rows.push(['Entity', `<span style="color:#94a3b8;">Possible: ${esc(cleanEntityName(s.entity_name))} — <em>confidence ${(entityConfidence * 100).toFixed(0)}%, unverified</em></span>`]);
    } else {
        rows.push(['Entity', 'Not attributed to a known entity']);
    }

    if (canonicalRisk.score !== null) {
        rows.push(['Risk Score', `<span class="risk-badge" style="${riskBadgeStyle(canonicalRisk.score)}">${(canonicalRisk.score * 100).toFixed(0)}% — ${esc(canonicalRisk.level)}</span>`]);
    } else if (canonicalRisk.level === 'INSUFFICIENT DATA') {
        rows.push(['Risk Score', '<span class="risk-badge" style="background:#f8fafc;color:#64748b;border:1px solid #cbd5e1;">INSUFFICIENT DATA</span>']);
    }

    if (s.sanctioned) {
        rows.push(['Sanctions', `<span class="risk-badge tier-high">OFAC SDN HIT — ${esc(s.sanctions_entity || 'Match found')}</span>`]);
    } else if (s.sanctioned === false) {
        rows.push(['Sanctions', '<span class="risk-badge tier-low">CLEAR</span>']);
    }

    if (typeof s.balance === 'number') {
        rows.push(['Current Balance', `${s.balance.toFixed(6)} ${esc(chain || '')}`]);
    }

    const investigationTxCount = sections?.adaptive_transactions?.data?.transactions?.length
        ?? sections?.counterparty_triage?.data?.analysis_tx_count;
    const effectiveTxCount = info.tx_count ?? s.tx_count ?? investigationTxCount;
    if (typeof effectiveTxCount === 'number' && effectiveTxCount > 0) {
        rows.push(['Total Transactions', String(effectiveTxCount)]);
    }

    if (info.first_seen) rows.push(['First Seen', esc(String(info.first_seen))]);
    if (info.last_seen) rows.push(['Last Seen', esc(String(info.last_seen))]);

    if (s.nearest_exchange?.name) {
        rows.push(['Nearest Exchange', `${esc(cleanEntityName(s.nearest_exchange.name))} (${s.nearest_exchange.hops ?? '?'} hops)`]);
    }

    if (typeof s.p2p_hits === 'number' && s.p2p_hits > 0) {
        rows.push(['P2P Trader Hits', String(s.p2p_hits)]);
    }

    let html = '<table class="meta-table"><tbody>';
    for (const [label, value] of rows) {
        html += `<tr><td class="label">${label}</td><td class="value">${value}</td></tr>`;
    }
    html += '</tbody></table>';
    return html;
}

/* ── Recommended Actions (dynamic, based on findings) ── */

export function buildRecommendedActions(
    triage: any,
    sections: InvestigationSections,
    escalation: string | null,
    chain: string | null,
): string {
    // Architecture (refactored 2026-05-05):
    //   POLICY actions    — non-counterparty rules (sanctions hit, ESCALATE-save)
    //   LEGAL ACTIONS     — Section 91/94 notices, ordered by sortForLegalAction
    //                       (jurisdiction priority first, then magnitude — so
    //                       India-direct exchanges sort above India-via-partner,
    //                       which sort above MLAT-required, regardless of
    //                       absolute volume)
    //   LEADS             — investigative leads (unknown counterparties, scam
    //                       paths) — these are NOT legal-actionable
    //   HOUSEKEEPING      — watchlist, FIR cross-ref, retry trace
    //
    // Within each bucket, the renderer outputs in the order produced. Bucket
    // order is fixed; ordering inside the LEGAL ACTIONS bucket is owned by
    // sortForLegalAction in reportCommon.ts.
    type Action = { bucket: 'policy' | 'legal' | 'leads' | 'housekeeping'; action: string; rationale: string };
    const policy: Action[] = [];
    const legal: Action[] = [];
    const leads: Action[] = [];
    const housekeeping: Action[] = [];

    const sanctioned = triage?.summary?.sanctioned;
    const exchange = triage?.summary?.nearest_exchange;
    const bt = sections.backward_trace;
    const btPaths = bt.status === 'loaded' ? (bt.data?.paths || []).length : 0;
    const cp = sections.counterparty_triage;
    const cpHighRisk = cp.status === 'loaded'
        ? ((cp.data?.counterparties || []) as any[]).filter((c: any) =>
            !isSpamCounterparty(c) && isHighRiskCounterparty(c)).length
        : 0;

    /* ── 1. POLICY actions (always first, regardless of counterparty list) ── */
    if (sanctioned) {
        policy.push({ bucket: 'policy', action: 'Escalate to senior officer — sanctions match', rationale: 'OFAC SDN hit: a high-priority indicator that needs prompt review and corroboration (not an Indian legal bar by itself).' });
    }
    if (escalation === 'ESCALATE') {
        policy.push({ bucket: 'policy', action: 'Preserve graph state and attach to active case', rationale: 'Multi-signal convergence meets escalation threshold. Save investigation for case file.' });
    }

    /* ── 2. Build the actionable counterparty pool ── */
    // Collect every named counterparty (direct + trace-terminal) into a single
    // pool, then let sortForLegalAction order them by jurisdiction + volume.
    // No more separate priority bands per discovery method — discovery method
    // is metadata, not policy.
    type ActionableCp = CounterpartyPrimitive & {
        _source?: 'direct' | 'trace_backward' | 'trace_forward';
        _hops?: number;
        _terminal_tx_hash?: string;
        _terminal_timestamp?: string;
    };
    const pool: ActionableCp[] = [];
    const seen = new Set<string>(); // dedupe by entity name (lowercased)

    // 2a. Direct counterparties from triage. The backend now stamps
    // is_actionable/actionable_jurisdiction on every counterparty so we don't
    // need keyword regex here anymore — sortForLegalAction filters out
    // non-actionable entries.
    if (cp.status === 'loaded' && cp.data?.counterparties) {
        for (const c of cp.data.counterparties as any[]) {
            const name = (c.entity_name || '').toLowerCase();
            if (!name || name === 'unknown') continue;
            if (seen.has(name)) continue;
            seen.add(name);
            pool.push({ ...c, _source: 'direct' });
        }
    }

    // 2b. Trace-discovered terminal exchanges (backward + forward). If the
    // backend flagged them with is_actionable, they enter the pool. If not
    // (e.g. older snapshot, missing fields), we infer actionability from the
    // entity name to avoid losing them.
    for (const [traceKey, sourceTag] of [
        ['backward_trace', 'trace_backward'],
        ['forward_trace', 'trace_forward'],
    ] as const) {
        const traceSection = sections[traceKey as keyof InvestigationSections];
        if (traceSection?.status !== 'loaded' || !traceSection.data) continue;
        const tracePaths = (traceSection.data.paths || traceSection.data.traces || []) as any[];
        for (const path of tracePaths) {
            const ent = path.target_entity || {};
            const entName = ent.name || ent.entity_name || path.entity_name;
            const entType = (ent.type || ent.entity_type || path.entity_type || '').toLowerCase();
            if (!entName || entName === 'Unknown') continue;
            if (entType !== 'exchange' && entType !== 'high_risk_exchange' && entType !== 'vasp') continue;
            const nameKey = entName.toLowerCase();
            if (seen.has(nameKey)) continue;
            seen.add(nameKey);
            const pathAddrs = (path.addresses || path.path || []) as string[];
            const pathEdges = (path.edges || []) as any[];
            const hops = pathAddrs.length - 1;
            const terminalEdge = pathEdges[pathEdges.length - 1] || {};
            pool.push({
                address: ent.address || pathAddrs[pathAddrs.length - 1] || '',
                entity_name: entName,
                entity_type: entType,
                entity_known: true,
                // Backend graph_service should stamp these; if absent we leave
                // sortForLegalAction to fall through with safe defaults.
                is_actionable: ent.is_actionable,
                actionable_jurisdiction: ent.actionable_jurisdiction,
                evidence_strength: hops <= 1 ? 'one_hop' : 'multi_hop',
                primary_volume: terminalEdge.value ?? terminalEdge.total_value ?? undefined,
                primary_token: terminalEdge.token_symbol ?? undefined,
                _source: sourceTag,
                _hops: hops,
                _terminal_tx_hash: terminalEdge.tx_hash,
                _terminal_timestamp: terminalEdge.timestamp,
            });
        }
    }

    // 2c. Triage's "nearest_exchange" hint, if not already covered.
    if (exchange?.name && !seen.has(exchange.name.toLowerCase())) {
        seen.add(exchange.name.toLowerCase());
        pool.push({
            address: '',
            entity_name: exchange.name,
            entity_type: 'exchange',
            entity_known: true,
            is_actionable: true,
            actionable_jurisdiction: undefined,
            evidence_strength: 'multi_hop',
            _source: 'direct',
            _hops: exchange.hops,
        } as ActionableCp);
    }

    /* ── 3. Project pool → legal-action ordering ── */
    // sortForLegalAction filters non-actionable + sorts by jurisdiction + volume.
    // For pool entries that lack actionability fields (older trace snapshots),
    // the filter would drop them; coerce a default so the user still sees
    // something while backend rolls out.
    const coerced = pool.map(c => ({
        ...c,
        is_actionable: c.is_actionable === undefined ? true : c.is_actionable,
    }));
    const sortedActionable = sortForLegalAction(coerced) as ActionableCp[];

    for (const cpa of sortedActionable) {
        const vol = Number(cpa.primary_volume) || Number(cpa.total_volume) || 0;
        const tok = cpa.primary_token || chain || '';
        const volStr = vol > 0 ? ` (${fmtCryptoVal(vol)} ${tok})` : '';
        const jurisLabel = cpa.actionable_jurisdiction
            ? ` — ${formatJurisdiction(cpa.actionable_jurisdiction as string)}`
            : '';
        const sourceDesc = cpa._source === 'direct'
            ? 'Direct counterparty'
            : cpa._source === 'trace_backward'
                ? `Backward-trace endpoint at hop ${cpa._hops ?? '?'}`
                : `Forward-trace endpoint at hop ${cpa._hops ?? '?'}`;
        const evidenceParts: string[] = [`${sourceDesc}${volStr}.`];
        if (cpa.address) evidenceParts.push(`Deposit address: ${cpa.address}.`);
        if (cpa._terminal_tx_hash) evidenceParts.push(`Tx hash: ${cpa._terminal_tx_hash}.`);
        if (cpa._terminal_timestamp) evidenceParts.push(`Date: ${fmtTs(cpa._terminal_timestamp)}.`);
        evidenceParts.push('Request KYC, IP/device logs, and account freeze under ' + NOTICE_STATUTE + '.');
        legal.push({
            bucket: 'legal',
            action: `Issue ${NOTICE_STATUTE} notice to ${cleanEntityName(cpa.entity_name || '')}${jurisLabel}`,
            rationale: evidenceParts.join(' '),
        });
    }

    /* ── 4. LEADS — investigative, not legal-actionable ── */
    const HIGH_RISK_TYPES = new Set(['scam', 'phishing', 'fraud', 'ransomware', 'mixer', 'darknet_market', 'sanctioned', 'stolen_funds', 'terrorism_financing']);
    const seenScams = new Set<string>();
    for (const [traceKey, traceLabel] of [['backward_trace', 'backward trace'], ['forward_trace', 'forward trace']] as const) {
        const traceSection = sections[traceKey as keyof InvestigationSections];
        if (traceSection?.status !== 'loaded' || !traceSection.data) continue;
        for (const path of (traceSection.data.paths || traceSection.data.traces || []) as any[]) {
            const ent = path.target_entity || {};
            const entName = ent.name || ent.entity_name || path.entity_name;
            const entType = (ent.type || ent.entity_type || path.entity_type || '').toLowerCase();
            if (!entName || entName === 'Unknown') continue;
            if (!HIGH_RISK_TYPES.has(entType)) continue;
            const key = entName.toLowerCase();
            if (seenScams.has(key)) continue;
            seenScams.add(key);
            const hops = ((path.addresses || path.path || []) as string[]).length - 1;
            leads.push({
                bucket: 'leads',
                action: `Investigate fund-flow path through ${cleanEntityName(entName)} (${entType})`,
                rationale: `${traceLabel} discovered ${entType} entity at hop ${hops}. Assess whether funds transited through or originated from this entity.`,
            });
            if (leads.length >= 5) break;
        }
    }

    if (cp.status === 'loaded' && cp.data?.counterparties) {
        const unknownCps = (cp.data.counterparties as any[])
            .filter((c: any) => !c.entity_name || c.entity_name === 'Unknown')
            .sort((a: any, b: any) => {
                const va = Number(a.primary_volume) || Number(a.total_volume) || 0;
                const vb = Number(b.primary_volume) || Number(b.total_volume) || 0;
                return vb - va;
            })
            .slice(0, 3);
        for (const uc of unknownCps) {
            const vol = Number(uc.primary_volume) || Number(uc.total_volume) || 0;
            const tok = uc.primary_token || (chain || '');
            const txCount = Number(uc.transaction_count ?? uc.tx_count) || 0;
            const addr = (uc.address || '').slice(0, 14) + '...';
            const volStr = vol > 0 ? `${fmtCryptoVal(vol)} ${tok}` : '';
            const txStr = txCount > 0 ? `, ${txCount} transaction${txCount > 1 ? 's' : ''}` : '';
            leads.push({
                bucket: 'leads',
                action: `Investigate unattributed counterparty ${addr}`,
                rationale: `Top counterparty by volume${volStr ? ` (${volStr}${txStr})` : ''} with no entity attribution. Run separate investigation to identify owner.`,
            });
        }
    }

    if (cpHighRisk > 0) {
        leads.push({ bucket: 'leads', action: `Investigate ${cpHighRisk} high-risk counterpart${cpHighRisk > 1 ? 'ies' : 'y'}`, rationale: 'Counterparty triage identified high-risk addresses requiring individual investigation.' });
    }

    /* ── 5. HOUSEKEEPING ── */
    if (bt.status === 'error') {
        housekeeping.push({ bucket: 'housekeeping', action: 'Re-run backward trace with expanded cache', rationale: `Fund-source tracing failed: ${bt.error || 'connection error'}. Pre-warm transaction cache and retry.` });
    } else if (bt.status === 'loaded' && btPaths === 0) {
        const btExp = bt.data?.exploration_summary;
        const stopReason = btExp?.stop_reason;
        if (stopReason === 'time_budget' || stopReason === 'limit_reached') {
            housekeeping.push({ bucket: 'housekeeping', action: 'Re-run backward trace (uses cached data from this run)', rationale: `Explored ${btExp?.total_nodes_explored || '?'} addresses across ${btExp?.bfs_depth_reached || '?'} hops — search budget exhausted before reaching a known entity. Re-running will extend from cached boundary.` });
        } else {
            housekeeping.push({ bucket: 'housekeeping', action: 'Re-run backward trace with expanded cache', rationale: 'Fund-source tracing returned no paths within hop limit. Consider increasing hop depth.' });
        }
    }
    housekeeping.push({ bucket: 'housekeeping', action: 'Add to watchlist for ongoing monitoring', rationale: 'Continue tracking activity for new transactions, exchange interactions, or dormancy breaks.' });
    housekeeping.push({ bucket: 'housekeeping', action: 'Cross-reference with active cases and FIR database', rationale: 'Check if this address or its counterparties appear in existing investigations.' });

    /* ── 6. Render ── */
    const allActions = [...policy, ...legal, ...leads, ...housekeeping];
    let html = '<table class="data"><thead><tr><th style="width:30px;">#</th><th style="width:90px;">Bucket</th><th>Recommended Action</th><th>Rationale</th></tr></thead><tbody>';
    const bucketLabel: Record<string, string> = {
        policy: 'Policy',
        legal: 'Section 94',
        leads: 'Lead',
        housekeeping: 'Housekeeping',
    };
    for (let i = 0; i < allActions.length; i++) {
        const a = allActions[i];
        html += `<tr><td class="muted">${i + 1}</td><td style="font-size:10px;color:#64748b;text-transform:uppercase;">${esc(bucketLabel[a.bucket] || a.bucket)}</td><td style="font-weight:600;">${esc(a.action)}</td><td style="color:#64748b;">${esc(a.rationale)}</td></tr>`;
    }
    html += '</tbody></table>';
    return html;
}

/* ── Methodology & Data Sources (mostly static) ── */

/** Providers that answered, and those called that never did, from this run's provider health. */
export function sourcesRanHtml(providerHealth: Record<string, any> | null | undefined): string {
    const r = describeSourcesRan(providerHealth);
    if (!r.recorded) return 'Provider status was not recorded for this run, so this report cannot list which external sources answered.';
    const answered = r.answered.map((a) => `${esc(a.provider)} (${a.ok} call${a.ok === 1 ? '' : 's'} answered${a.failed ? `, ${a.failed} failed` : ''})`).join('; ');
    let html = answered ? `Answered: ${answered}.` : 'No external provider answered.';
    if (r.failedOnly.length) html += ` Called but did not answer: ${r.failedOnly.map((f) => `${esc(f.provider)} (${f.failed} failed)`).join('; ')}.`;
    return html;
}

function renderMethodology(
    chain: string | null,
    entityCount?: number,
    providerHealth?: Record<string, any> | null,
    /** Signal-assessment (explain) result — source of the Tier-3 AI disclosure. */
    aiSource?: Record<string, any> | null,
    steps?: StepsRun | null,
): string {
    const chainApi: Record<string, string> = {
        'BTC': 'Mempool.space / Blockstream API',
        'ETH': 'Etherscan V2 API',
        'TRX': 'TronGrid API',
        'BNB': 'Etherscan V2 API (BSC)',
        'MATIC': 'Etherscan V2 API (Polygon)',
    };
    const apiName = chainApi[chain || ''] || 'Chain-specific API';

    return `
    <table class="data">
        <thead><tr><th style="width:180px;">Component</th><th>Description</th></tr></thead>
        <tbody>
            <tr><td style="font-weight:600;">Transaction Data</td><td>${esc(apiName)} — on-chain transactions with full metadata</td></tr>
            <tr><td style="font-weight:600;">Sources That Ran</td><td data-testid="methodology-sources-ran">${sourcesRanHtml(providerHealth)}</td></tr>
            ${steps ? `<tr><td style="font-weight:600;">Investigation Steps</td><td data-testid="methodology-steps-ran">${esc(stepsRunSentence(steps))}</td></tr>` : ''}
            <tr><td style="font-weight:600;">Entity Database</td><td>${entityCount ? entityCount.toLocaleString() : '144,000+'} labelled addresses (exchanges, services, sanctions, scraped intelligence)</td></tr>
            <tr><td style="font-weight:600;">Sanctions Screening</td><td>OFAC SDN list — O(1) hash lookup. ${esc(OFAC_INDICATOR_LINE)}</td></tr>
            <tr><td style="font-weight:600;">Risk Scoring</td><td>8-step composite pipeline: entity match, sanctions, exposure, behavioral patterns, velocity analysis, P2P signals, external intelligence, pattern boost</td></tr>
            <tr><td style="font-weight:600;">Signal Assessment</td><td>8 independent signal families tested for escalation recommendation (ESCALATE / INVESTIGATE / MONITOR). This is separate from the composite risk score — signal families determine investigative priority, the risk score quantifies address-level risk.</td></tr>
            <tr><td style="font-weight:600;">Behavioral Patterns</td><td>18 typologies including peel chain, fan-in/out, relay, shadow bank, hawala-to-crypto, cadence analysis</td></tr>
            <tr><td style="font-weight:600;">Velocity Analysis</td><td>6 metrics: fund-through rate, consolidation ratio, value amplification, dwell time, regularity score, burst score</td></tr>
            <tr><td style="font-weight:600;">Graph Algorithms</td><td>NetworkX — PageRank, betweenness centrality, community detection (Louvain)</td></tr>
            <tr><td style="font-weight:600;">AI Narrative</td><td>LLM-generated dual hypothesis (Theory A: Illicit / Theory B: Legitimate) with signal convergence assessment</td></tr>
        </tbody>
    </table>
    ${buildEvidenceTierLegend((() => {
            // Each AI contribution, with the provenance the backend recorded (ai_meta).
            const meta = aiSource?.ai_meta && typeof aiSource.ai_meta === 'object' ? aiSource.ai_meta : null;
            const genId = meta?.generation_id || aiSource?.generation_id || null;
            if (!meta && !genId) return '';
            const parts: string[] = [];
            if (meta?.model) parts.push(`model ${esc(meta.model)}`);
            if (meta?.provider) parts.push(`provider ${esc(meta.provider)}`);
            if (genId) parts.push(`generation id ${esc(genId)}`);
            if (meta?.generated_at) parts.push(`generated ${esc(meta.generated_at)}`);
            const noText = meta?.llm_used === false || aiSource?.analysis == null;
            const outcome = noText ? ` (no narrative produced${meta?.status ? `: ${esc(meta.status)}` : ''})` : '';
            return `<br/><strong>AI contributions in this report:</strong>`
                + `<ul style="margin:4px 0 0 16px;padding:0;">`
                + `<li>Signal-assessment narrative: ${parts.length ? parts.join(' · ') : 'no model metadata recorded'}${outcome}</li>`
                + `</ul>`;
        })())}
    <div style="margin-top:10px;padding:10px 14px;background:#fffbeb;border:1px solid #fcd34d;border-radius:4px;font-size:11px;line-height:1.6;color:#92400e;">
        <strong>Evidence Preservation Notice:</strong>
        Blockchain data is immutable and publicly verifiable. API responses were cached at the time of analysis.
        All on-chain facts (Tier 1) can be independently reproduced by querying the same blockchain.
        Analytical outputs (Tier 2) are reproducible given the same input data and algorithm version.
        This report should be corroborated with exchange KYC records, IP/device logs, and other investigative
        sources before any legal or enforcement action.
    </div>
    <p style="margin-top:12px;font-size:11px;color:#64748b;">Provider health for this investigation is reported under Coverage and Limitations.</p>
    <h3 style="margin-top:20px;margin-bottom:8px;font-size:13px;color:#1e293b;">Key Terms &amp; Definitions</h3>
    <table class="data" style="font-size:11px;">
        <thead><tr><th style="width:200px;">Term</th><th>Definition</th></tr></thead>
        <tbody>
            <tr><td style="font-weight:600;">Fund-Through Rate</td><td>Percentage of total incoming value that is forwarded outward within the analysis window. A rate near 100% suggests relay or pass-through behavior — funds are not retained.</td></tr>
            <tr><td style="font-weight:600;">Consolidation Ratio</td><td>Ratio of incoming transaction count to outgoing count. High values (many inputs, few outputs) indicate consolidation or sweep activity — multiple deposits being aggregated into a single withdrawal.</td></tr>
            <tr><td style="font-weight:600;">Value Amplification</td><td>Ratio of largest single transaction value to the median transaction value. High amplification may indicate structuring or layering — mixing small routine transactions with large illicit transfers.</td></tr>
            <tr><td style="font-weight:600;">Dwell Time</td><td>Median time (in hours) that funds remain at an address before being moved. Short dwell times (minutes) suggest automated relay; longer dwell times suggest holding or accumulation.</td></tr>
            <tr><td style="font-weight:600;">Regularity Score</td><td>Measure of temporal consistency in transactions (0–1 scale). High regularity (near 1.0) indicates scheduled or automated activity; low regularity indicates human-driven or sporadic usage.</td></tr>
            <tr><td style="font-weight:600;">Burst Score</td><td>Ratio of peak hourly transaction volume to average volume. High burst scores indicate concentrated transaction clusters — common in wash trading, rapid layering, or coordinated fund movements.</td></tr>
            <tr><td style="font-weight:600;">Peel Chain</td><td>A laundering pattern where a large sum is repeatedly "peeled" — sent to a new address with a small amount diverted at each step — to obscure the fund trail. Each hop reduces the remaining amount.</td></tr>
            <tr><td style="font-weight:600;">Fan-In / Fan-Out</td><td>Fan-In: multiple source addresses sending to one target (aggregation). Fan-Out: one source distributing to many targets (distribution). Extreme ratios suggest mixing services or coordinated cash-out.</td></tr>
            <tr><td style="font-weight:600;">Pass-Through Rate</td><td>Percentage of value that transits through an address without accumulation. Near-100% pass-through is a hallmark of relay wallets used to add hops between source and destination.</td></tr>
            <tr><td style="font-weight:600;">Multi-Hop Exposure</td><td>Risk inherited from connected addresses across multiple transaction hops. Uses exponential decay — direct (1-hop) connections carry more weight than indirect (2+ hop) connections.</td></tr>
            <tr><td style="font-weight:600;">Risk Tier</td><td><strong>Critical:</strong> Direct sanctions/darknet entity. <strong>High:</strong> Known fraud/scam/mixer entity. <strong>Medium:</strong> Flagged service or elevated behavioral signals. <strong>Low:</strong> Legitimate exchange or minimal risk indicators.</td></tr>
            <tr><td style="font-weight:600;">BFS (Breadth-First Search)</td><td>Graph traversal algorithm that explores all direct neighbors at each depth level before proceeding deeper. Used for backward/forward tracing to find fund paths within a configurable hop limit.</td></tr>
            <tr><td style="font-weight:600;">PageRank</td><td>Algorithm that ranks addresses by their structural importance in the transaction graph. Originally designed for web search; in blockchain analysis, high-PageRank addresses are major fund transit points.</td></tr>
            <tr><td style="font-weight:600;">Betweenness Centrality</td><td>Measures how often an address lies on the shortest path between other addresses. High betweenness indicates a critical intermediary — removing it would disrupt fund flows in the network.</td></tr>
            <tr><td style="font-weight:600;">Community Detection</td><td>Louvain algorithm that partitions the transaction graph into clusters of densely interconnected addresses. Addresses in the same community likely belong to the same entity or coordinated operation.</td></tr>
            <tr><td style="font-weight:600;">UTXO Consolidation</td><td>In Bitcoin/UTXO chains, combining multiple unspent outputs into a single transaction. Often used to aggregate funds from multiple peel-chain endpoints back into one controlled address.</td></tr>
            <tr><td style="font-weight:600;">Signal Family</td><td>A category of risk indicators (e.g., sanctions, behavioral, entity, exposure). Each family is independently assessed as TRIGGERED or CLEAR. Three or more triggered families → ESCALATE recommendation.</td></tr>
            <tr><td style="font-weight:600;">Velocity Profile</td><td>Behavioral classification derived from the 6 velocity metrics: <strong>Relay</strong> (high pass-through), <strong>Accumulator</strong> (high inflow retention), <strong>Distributor</strong> (high fan-out), <strong>Mixed</strong> (multiple behaviors), <strong>Dormant</strong> (minimal activity).</td></tr>
        </tbody>
    </table>`;
}

/* ── Electronic evidence certificate template (BSA s.63, formerly Evidence Act s.65B; see reportProvenance.ts) ── */

export function renderSection65BCertificate(
    chain: string | null,
    address: string | null,
    reportDate: Date,
    evidenceCount: number,
    providerHealth?: Record<string, any> | null,
    steps?: StepsRun | null,
): string {
    const dateStr = reportDate.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

    return `
    <div class="appendix-divider" id="section-65b">Electronic Evidence Certificate</div>
    <div class="appendix">
        <div class="cert-box">
            <h3>Certificate Under ${esc(CERTIFICATE_STATUTE)}</h3>
            <p style="text-align:center;font-size:10px;color:#64748b;margin-bottom:16px;">(As required for admissibility of electronic records as evidence in Indian courts)</p>

            <p>I, <span class="cert-field">[Officer Name]</span>, holding the designation of
            <span class="cert-field">[Designation]</span>, do hereby certify the following in respect of the
            electronic record produced herewith:</p>

            <p style="margin-top:12px;"><strong>1. Nature of Electronic Record:</strong><br/>
            This document is a computer-generated cryptocurrency investigation report pertaining to blockchain address
            <span class="mono" style="font-size:11px;">${monoWrap(address || '[Address]')}</span> on the
            ${esc((chain || 'UNKNOWN').toUpperCase())} blockchain network. The report contains ${evidenceCount} individually
            numbered evidence items (E-1 through E-${evidenceCount}).</p>

            <p><strong>2. Tool Used for Generation:</strong><br/>
            <strong>CryptoFlow Investigation Platform</strong> (also known as Outris Crypto Investigation Platform) —
            an automated cryptocurrency forensic analysis system designed for law enforcement use.</p>

            <p><strong>3. Data Sources Consulted:</strong></p>
            <ul style="margin:4px 0 8px 24px;font-size:11px;line-height:1.8;" data-testid="cert-sources">
                <li><strong>External providers (from this run's provider health):</strong> ${sourcesRanHtml(providerHealth)}</li>
                <li><strong>Entity Database:</strong> the platform's labelled address database (exchange wallets, known services, community-reported addresses), consulted by the platform's own screening.</li>
                <li><strong>OFAC SDN List:</strong> screened by the platform's own sanctions check (${esc(OFAC_INDICATOR_LINE)}). Whether that screen completed is stated under Coverage and Limitations.</li>
            </ul>

            <p><strong>4. Methodology:</strong><br/>
            The report was generated by the platform's automated investigation pipeline. <span data-testid="cert-steps">${esc(steps ? stepsRunSentence(steps) : 'The investigation steps that ran were not recorded.')}</span>
            Full methodology is documented in the Methodology &amp; Data Sources section of this report.</p>

            <p><strong>5. Computer System:</strong><br/>
            The electronic record was produced by a computer system that was operating properly at the material time,
            and the information contained in the electronic record reproduces or is derived from information fed into
            the computer in the ordinary course of blockchain forensic investigation activities.</p>

            <p><strong>6. Integrity of Data:</strong><br/>
            (a) The blockchain transaction data (Tier 1 — On-chain Facts) is publicly verifiable and immutable on the
            respective blockchain network.<br/>
            (b) Analytical inferences (Tier 2) are algorithmically derived and reproducible given the same input data
            and software version.<br/>
            (c) AI-generated narratives (Tier 3) are indicative assessments and are not presented as primary evidence.<br/>
            (d) API responses were cached at the time of analysis. The original on-chain data can be independently
            verified by any party with blockchain access.</p>

            <p><strong>7. Date of Report Generation:</strong> ${esc(dateStr)}</p>

            <div class="cert-signature-block">
                <div>
                    <div class="sig-line"></div>
                    <div class="sig-label">Name: <span class="cert-field" style="min-width:150px;">[Officer Name]</span></div>
                    <div class="sig-label">Designation: <span class="cert-field" style="min-width:150px;">[Designation]</span></div>
                    <div class="sig-label">Organization: <span class="cert-field" style="min-width:150px;">[Organization]</span></div>
                </div>
                <div>
                    <div class="sig-line"></div>
                    <div class="sig-label">Date: <span class="cert-field" style="min-width:150px;">[DD/MM/YYYY]</span></div>
                    <div class="sig-label">Place: <span class="cert-field" style="min-width:150px;">[City, State]</span></div>
                    <div class="sig-label">Signature &amp; Seal</div>
                </div>
            </div>

            <p style="margin-top:20px;font-size:9px;color:#94a3b8;text-align:center;">
                This certificate is provided as a template in accordance with ${esc(CERTIFICATE_STATUTE)}. The certifying officer must verify all facts stated herein
                and sign in their official capacity. This certificate must accompany the electronic record when presented
                as evidence before any court of law in India.
            </p>
        </div>
    </div>`;
}

/* ── Main builder ── */

/** Circulation & Pass-Through appendix — conserved, time-ordered, single-asset
 * corridors (cycle_detector), grouped by pivot. Renders nothing when empty. */
function renderCirculationReport(circulation?: CirculationResult | null): string {
    if (!circulation || !Array.isArray(circulation.findings) || circulation.findings.length === 0) return '';
    const findings = circulation.findings;
    const groups = new Map<string, CirculationFinding[]>();
    for (const f of findings) {
        const pivot = (f.pivots && f.pivots[0]) || f.terminus || f.origin || 'unknown';
        if (!groups.has(pivot)) groups.set(pivot, []);
        groups.get(pivot)!.push(f);
    }
    const maxUsd = (fs: CirculationFinding[]) => Math.max(0, ...fs.map(f => f.bottleneck_usd || 0));
    const ordered = Array.from(groups.entries()).sort((a, b) => maxUsd(b[1]) - maxUsd(a[1]));
    const s = circulation.summary || {};
    const dwell = (sec?: number | null) => sec == null ? '—' : (sec < 3600 ? `${Math.round(sec / 60)}m` : `${(sec / 3600).toFixed(1)}h`);

    let html = '<div class="appendix-divider" id="circulation">Appendix — Circulation &amp; Pass-Through Corridors</div>';
    html += `<p style="margin:6px 0;color:#475569;">Conserved (same-asset, time-ordered, value-preserving) corridors detected across the investigation graph: <strong>${findings.length}</strong> ${findings.length === 1 ? 'corridor' : 'corridors'}`;
    if (s.passthrough_corridors != null) html += ` · ${esc(String(s.passthrough_corridors))} pass-through`;
    if (s.validated_roundtrips) html += ` · ${esc(String(s.validated_roundtrips))} round-trip`;
    if (s.graph_cycles_examined != null) html += ` (of ${esc(String(s.graph_cycles_examined))} graph cycles examined)`;
    html += '. A corridor is a real fund-conduit signal, not a coincidental bidirectional edge.</p>';

    for (const [pivot, finds] of ordered) {
        html += `<h4 style="margin:10px 0 4px;">Pivot <span style="font-family:monospace;">${esc(pivot)}</span> <span style="color:#64748b;font-weight:normal;">· ${finds.length} ${finds.length === 1 ? 'corridor' : 'corridors'}</span></h4>`;
        html += '<table><thead><tr><th>Origin</th><th>Terminus</th><th>Bottleneck (USD)</th><th>Token</th><th>Dwell</th><th>Typology</th><th>Subject</th><th>Conf.</th></tr></thead><tbody>';
        for (const f of finds.slice().sort((a, b) => (b.bottleneck_usd || 0) - (a.bottleneck_usd || 0))) {
            html += `<tr>
                <td style="font-family:monospace;">${monoWrap(f.origin || '—')}</td>
                <td style="font-family:monospace;">${monoWrap(f.terminus || '—')}</td>
                <td>${f.bottleneck_usd != null ? '$' + Math.round(f.bottleneck_usd).toLocaleString() : '—'}</td>
                <td>${esc(f.token || '—')}</td>
                <td>${dwell(f.total_dwell_s)}</td>
                <td>${esc(String(f.typology || f.class || '—').replace(/_/g, ' '))}</td>
                <td>${f.involves_subject ? 'yes' : ''}</td>
                <td>${f.confidence != null ? esc(String(f.confidence)) : '—'}</td>
            </tr>`;
        }
        html += '</tbody></table>';
    }
    return html;
}

/**
 * §1 Statutory Subpoena & Freezing Matrix — the actionable seizure targets
 * (gas funder, off-ramps, bridge/mixer nexuses) with per-target statutory
 * demands + the jurisdiction-routed notice template. Composed backend-side by
 * analysis/seizure_matrix.py; this renders it.
 */
function renderSeizureMatrix(matrix: any, ev?: EvidenceCounter): string {
    if (!matrix || !Array.isArray(matrix.targets) || matrix.targets.length === 0) return '';
    let html = '';
    if (matrix.summary) html += `<p style="margin:4px 0 10px;color:#475569;">${esc(matrix.summary)}</p>`;
    html += `<table class="data"><thead><tr><th>#</th><th>Priority</th><th>Entity / Nexus</th><th>Role</th><th>Address</th><th>Actionability</th><th>Notice</th></tr></thead><tbody>`;
    const actionabilityLabel = (a: string): string =>
        a === 'issuer_freeze' ? 'Issuer freeze (Tether / Circle)' : formatJurisdiction(a);
    matrix.targets.forEach((t: any, i: number) => {
        const eid = (ev && t.serveable) ? ev.next() : `<span class="muted">${i + 1}</span>`;
        const prCls = t.priority === 'critical' ? 'badge-escalate'
            : t.priority === 'high' ? 'badge-investigate' : 'badge-monitor';
        const freezeBadge = t.issuer_freeze_recommended
            ? ` <span class="risk-badge badge-escalate" style="font-size:9px;">FREEZE PETITION</span>` : '';
        const notice = t.serveable && t.notice_template_key
            ? `<span class="exchange-badge">${esc(t.notice_template_key)}</span>`
            : (t.issuer_freeze_recommended ? '<span class="muted" style="font-size:9px;">issuer petition</span>'
                : (t.nexus_role === 'intel_portal' ? '<span class="muted" style="font-size:9px;">internal query</span>' : '<span class="muted">—</span>'));
        html += `<tr${t.priority === 'critical' ? ' class="highlight"' : ''}>
            <td>${eid}</td>
            <td><span class="risk-badge ${prCls}">${esc(String(t.priority || '').toUpperCase())}</span>${freezeBadge}</td>
            <td>${esc(cleanEntityName(t.entity_name || '—'))}${t.relevance ? `<div class="muted" style="font-size:9px;">${esc(t.relevance)}</div>` : ''}</td>
            <td style="font-size:10px;white-space:nowrap;">${esc(String(t.nexus_role || '').replace(/_/g, ' '))}</td>
            <td>${t.address ? monoSpan(t.address) : '<span class="muted">—</span>'}${t.chain ? ` <span class="muted" style="font-size:9px;">${esc(t.chain)}</span>` : ''}</td>
            <td style="font-size:10px;">${esc(actionabilityLabel(String(t.actionability || '')))}</td>
            <td>${notice}</td>
        </tr>`;
        if (t.serveable && Array.isArray(t.statutory_demands) && t.statutory_demands.length) {
            html += `<tr><td></td><td colspan="6" style="font-size:10px;color:#475569;background:#fafbfc;"><strong>Statutory demands:</strong> ${t.statutory_demands.map((d: string) => esc(d)).join(' &nbsp;·&nbsp; ')}</td></tr>`;
        }
    });
    html += '</tbody></table>';
    if (matrix.estimated_freezable_usd) {
        html += `<p style="margin-top:8px;font-size:11px;color:#475569;"><strong>Estimated freezable at off-ramp exchanges:</strong> ${esc(fmtCryptoVal(matrix.estimated_freezable_usd))} USD</p>`;
    }
    html += `<p style="margin-top:6px;font-size:10px;color:#94a3b8;">Notice templates are jurisdiction-routed (${NOTICE_STATUTE} for India-domiciled; MLAT Letter of Request for foreign). Mixers and unhosted bridge clusters are informational — no operator to compel.</p>`;
    return html;
}

/**
 * §4 Fund-Flow Reconciliation — hop-by-hop USD table + conservation summary
 * (drained-in vs dispersed-out, per-channel totals, unaccounted residue).
 * Composed backend-side by analysis/forensic_flow.py reconciliation().
 */
export function renderValueReconciliation(recon: any): string {
    if (!recon || !Array.isArray(recon.rows) || recon.rows.length === 0) return '';
    const { rows, summary } = recon;
    const anyCapped = rows.some((r: any) => r.amount_capped);
    let html = '';
    // The valuation basis is part of the figure: say what priced the rows and what did not.
    // (The §4 table is built with the recorded edge USD, else stablecoins at par; nothing else is
    // priced, and no price date is recorded, so the report does not invent one.)
    const pricedCount = rows.filter((r: any) => r.usd != null).length;
    html += `<p data-testid="recon-usd-basis" style="margin:4px 0 8px;font-size:11px;color:#475569;"><strong>USD basis:</strong> `
        + `each row uses the USD the trace recorded for that transfer; a stablecoin without one is taken at 1.00 USD par; `
        + `any other asset with no recorded USD is shown as &mdash; and counted as 0 in every total. `
        + `<strong>Price date:</strong> not recorded for these figures (they are not re-priced to the report date; the row timestamp is the transfer time, not a price date). `
        + `${pricedCount} of ${rows.length} rows are priced.</p>`;
    if (summary) {
        html += `<div class="meta-box"><table class="meta-table"><tbody>`;
        const add = (label: string, v: any, note = '') => {
            if (v == null) return;
            html += `<tr><td class="label">${esc(label)}</td><td class="value">${esc(fmtCryptoVal(Number(v)))} USD${note ? ` <span class="muted" style="font-size:10px;">${esc(note)}</span>` : ''}</td></tr>`;
        };
        add('Total traced', summary.total_usd_traced, 'outgoing edges only, edge figure');
        add('Inbound excluded', summary.inbound_excluded_usd, 'deposits into the subject or a victim contract; money of an earlier party, not added to Total traced');
        add('Subject inflow', summary.subject_inflow_usd, 'edge figure');
        add('Subject outflow', summary.subject_outflow_usd, 'edge figure');
        add('Exited to terminals', summary.exited_to_terminals_usd, 'attributable share only');
        add('Unaccounted / parked', summary.unaccounted_usd, 'inflow less exited; mixes the two bases above');
        if (summary.priced_pct != null) html += `<tr><td class="label">Priced coverage</td><td class="value">${esc(String(summary.priced_pct))}% of rows</td></tr>`;
        if (summary.capped_edge_count != null) {
            html += `<tr><td class="label">Amount-capped edges</td><td class="value" data-testid="recon-capped-count">${esc(String(summary.capped_edge_count))}`
                + `${Number(summary.capped_edge_count) > 0 ? ' <span class="muted" style="font-size:10px;">edges that left a pooled wallet: only the attributable share (column below) is tied to the traced source</span>' : ''}</td></tr>`;
        }
        html += `</tbody></table></div>`;
    }
    html += `<table class="data"><thead><tr><th>Hop</th><th>Timestamp (UTC)</th><th>From</th><th>To</th><th>Asset</th><th class="right">Quantity</th><th class="right">USD (edge)</th>`
        + `${anyCapped ? '<th class="right">Attributable USD</th>' : ''}<th>Nature</th></tr></thead><tbody>`;
    const party = (addr: string, label: string) => {
        const full = addr ? `<div>${monoSpan(addr)}</div>` : '';
        const hasLabel = label && label !== String(addr || '').slice(0, 12);
        return `${hasLabel ? `<div>${esc(label)}</div>` : ''}${full}`;
    };
    rows.forEach((r: any) => {
        const capped = !!r.amount_capped;
        html += `<tr>
            <td>${esc(String(r.hop))}</td>
            <td style="font-size:9px;white-space:nowrap;">${esc(fmtTsCompact(r.ts))}</td>
            <td style="font-size:10px;">${party(r.from || '', r.from_label || '')}</td>
            <td style="font-size:10px;">${party(r.to || '', r.to_label || '')}</td>
            <td>${esc(r.asset)}</td>
            <td class="right mono">${r.qty != null ? esc(fmtCryptoVal(Number(r.qty))) : '—'}</td>
            <td class="right mono">${r.usd != null ? esc(fmtCryptoVal(Number(r.usd))) : '<span class="muted">—</span>'}</td>
            ${anyCapped ? `<td class="right mono">${capped ? '<span title="amount capped" data-testid="recon-capped">capped: </span>' : ''}${r.attributable_usd != null ? esc(fmtCryptoVal(Number(r.attributable_usd))) : '<span class="muted">—</span>'}</td>` : ''}
            <td style="font-size:10px;">${esc(String(r.nature || '').replace(/_/g, ' '))}</td>
        </tr>`;
    });
    html += '</tbody></table>';
    return html;
}

/**
 * §3 Flow Diagrams. The report is a self-contained file, so nothing is fetched when it opens:
 * no CDN script, no network. Where the diagram IR is available it is drawn as inline SVG by
 * `components/report/flowDiagram.ts` (the same drawing as the case report). Otherwise the
 * Mermaid source is printed as a collapsed, copyable code block that any flowchart editor redraws.
 * (OD13: Mermaid is not bundled; the bundle would add megabytes to every report.)
 */
export function renderFlowDiagrams(diagrams: any, ir?: Record<string, FlowDiagramIR> | null): string {
    if (!diagrams || (!diagrams.executive && !diagrams.detailed)) return '';
    let html = '';
    html += `<p style="margin:4px 0 10px;color:#475569;font-size:11px;">Fund-flow diagrams generated from the investigation graph. Drawn without any network connection; the Mermaid source is included so a diagram can be reproduced in any flowchart editor.</p>`;
    const block = (key: 'executive' | 'detailed', title: string) => {
        const src = diagrams[key];
        if (!src) return '';
        const drawn = ir && ir[key] && Array.isArray(ir[key].nodes) && ir[key].nodes.length > 0 ? ir[key] : null;
        if (drawn) {
            return renderFlowDiagramFigure({ ...drawn, mermaid: drawn.mermaid || src }, { id: `le-flow-${key}`, title });
        }
        return `<h4 style="margin:12px 0 4px;font-size:12px;font-weight:700;">${esc(title)}</h4>`
            + buildCollapsedCodeBlock(`le-flow-${key}-mermaid`, 'Mermaid source (paste into any Mermaid or flowchart editor to redraw)', String(src));
    };
    html += block('executive', 'Executive Flow');
    html += block('detailed', 'Detailed Forensic Flow');
    return html;
}

export function generateInvestigationReportHtml(
    chain: string | null,
    address: string | null,
    sections: InvestigationSections,
    triage?: any,
    dataMode?: 'mock' | 'cached' | 'live',
    entityCount?: number,
    circulation?: CirculationResult | null,
    assetRecovery?: {
        seizure_matrix?: any;
        value_reconciliation?: any;
        flow_diagrams?: { executive?: string; detailed?: string } | null;
        flow_diagram_ir?: Record<string, FlowDiagramIR> | null;
        funding_sources?: any;
        bridge_settlements?: any[];
        recheck_gates?: any;
    } | null,
): string {
    const now = new Date();
    const reportGenTime = now.getTime();

    // Derive overall assessment
    const sa = sections.signal_assessment;
    const escalation = sa.status === 'loaded' ? sa.data?.escalation_recommendation : null;
    const familiesTriggered = sa.status === 'loaded' ? sa.data?.families_triggered : null;
    const familiesTested = sa.status === 'loaded' ? (sa.data?.families_tested ?? (sa.data?.signal_families ? Object.keys(sa.data.signal_families).length : 8)) : null;
    const vel = sections.velocity;
    const velocityProfile = vel.status === 'loaded' ? vel.data?.velocity_profile : null;
    const velocityRisk = vel.status === 'loaded' ? vel.data?.velocity_risk_score : null;

    const safeFilename = `investigation_${(chain || 'unknown').toUpperCase()}_${(address || 'unknown').slice(0, 12)}_${now.toISOString().slice(0, 10)}`.replace(/[^a-zA-Z0-9_\-]/g, '_');
    const isDemo = dataMode === 'mock' || dataMode === 'cached';
    let preamble = '';
    if (isDemo) {
        preamble += `<div class="demo-watermark-page">THIS REPORT WAS GENERATED FROM ${esc((dataMode || 'mock').toUpperCase())} DATA — NOT SUITABLE FOR LEGAL PROCEEDINGS</div>`;
    }

    // Canonical risk score — prefers investigation-derived over triage default
    const canonicalRisk = resolveCanonicalRisk(sections, triage);

    // Overall Assessment banner
    let assessmentHtml = '';
    if (escalation || velocityProfile) {
        const boxCls = escalation === 'ESCALATE' ? 'escalate'
            : escalation === 'INVESTIGATE' ? 'investigate'
            : escalation === 'INSUFFICIENT_DATA' ? 'monitor' : 'monitor';
        const badgeCls = escalation === 'ESCALATE' ? 'badge-escalate'
            : escalation === 'INVESTIGATE' ? 'badge-investigate'
            : escalation === 'INSUFFICIENT_DATA' ? 'badge-monitor' : 'badge-monitor';

        assessmentHtml += `<div class="assessment-box ${boxCls}">
            <h2 style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;margin-bottom:8px;">Overall Assessment</h2>
            <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">`;

        if (escalation) {
            assessmentHtml += `<span class="badge ${badgeCls}">${esc(escalation.replace(/_/g, ' '))}</span>`;
        }
        if (canonicalRisk.score !== null) {
            assessmentHtml += `<span class="risk-badge" style="${riskBadgeStyle(canonicalRisk.score)}">Risk: ${(canonicalRisk.score * 100).toFixed(0)}% ${esc(canonicalRisk.level)}</span>`;
        } else if (canonicalRisk.level === 'INSUFFICIENT DATA') {
            assessmentHtml += `<span class="risk-badge" style="background:#f8fafc;color:#64748b;border:1px solid #cbd5e1;">Risk: INSUFFICIENT DATA</span>`;
        }
        if (familiesTriggered !== null && familiesTested !== null) {
            assessmentHtml += `<span>${familiesTriggered}/${familiesTested} signal families triggered</span>`;
        }
        if (velocityProfile) {
            assessmentHtml += `<span>Velocity: <strong>${esc(velocityProfile)}</strong></span>`;
        }
        if (typeof velocityRisk === 'number') {
            assessmentHtml += `<span>Velocity Risk: <strong>${pct(velocityRisk)}</strong></span>`;
        }

        assessmentHtml += '</div></div>';
    }

    // Evidence counter for sequential evidence numbering (E-1, E-2, ...)
    const evidenceCounter = new EvidenceCounter();

    let sectionNum = 0;

    // Build table of contents entries (we'll collect them and insert after the header)
    const tocEntries: { num: number; title: string; id: string }[] = [];
    const addTocEntry = (num: number, title: string): string => {
        const id = `section-${num}`;
        tocEntries.push({ num, title, id });
        return id;
    };

    // §1 — Executive Summary
    const execSummary = buildExecutiveSummary(chain, address, triage, escalation, familiesTriggered, familiesTested, velocityProfile, velocityRisk, sections);
    let sectionsBuf = '';
    if (execSummary) {
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Executive Summary');
        sectionsBuf += openSec(sid, `${sectionNum}. Executive Summary`)
            + `<div style="line-height:1.8;font-size:13px;color:#334155;">${execSummary}</div>`
            + closeSec();
    }

    // Coverage and Limitations: right after the Executive Summary, always present.
    sectionNum++;
    {
        const covSid = addTocEntry(sectionNum, 'Coverage and Limitations');
        sectionsBuf += openSec(covSid, `${sectionNum}. Coverage and Limitations`)
            + renderCoverageAndLimitations(sections, triage, assetRecovery?.recheck_gates)
            + closeSec();
    }

    // §2 — Address Overview (from triage)
    if (triage) {
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Address Overview');
        sectionsBuf += openSec(sid, `${sectionNum}. Address Overview`);
        sectionsBuf += renderAddressOverview(chain, address, triage, sections);

        // Surface analysis period (m3/m12 — with tx timestamp fallback)
        const velSection2 = sections.velocity;
        const velData = velSection2?.status === 'loaded' ? velSection2.data : null;
        let addrPeriod = velData?.analysis_period;
        if (!addrPeriod?.start || !addrPeriod?.end) {
            // Fallback: derive from adaptive_transactions timestamps
            const atSection2 = sections.adaptive_transactions;
            const txList2 = atSection2?.status === 'loaded' ? (atSection2.data?.transactions || []) : [];
            if (txList2.length > 0) {
                const ts2 = (txList2 as any[])
                    .map((tx: any) => tx.timestamp || tx.block_timestamp || tx.time)
                    .filter(Boolean)
                    .map((t: any) => typeof t === 'number' ? (t > 1e12 ? t : t * 1000) : new Date(String(t)).getTime())
                    .filter((t: number) => !isNaN(t));
                if (ts2.length > 0) {
                    addrPeriod = {
                        start: new Date(Math.min(...ts2)).toISOString(),
                        end: new Date(Math.max(...ts2)).toISOString(),
                        span_days: Math.ceil((Math.max(...ts2) - Math.min(...ts2)) / 86400000),
                    };
                }
            }
        }
        if (addrPeriod?.start && addrPeriod?.end) {
            const spanText = typeof addrPeriod.span_days === 'number'
                ? ` (${addrPeriod.span_days === 0 ? '1 day' : `${addrPeriod.span_days} days`})`
                : '';
            const txCount = velData?.transaction_count;
            sectionsBuf += `<div style="margin-top:10px;padding:8px 12px;background:#f0f9ff;border:1px solid #bae6fd;border-radius:4px;font-size:12px;color:#0c4a6e;">
                <strong>Analysis Period:</strong> ${esc(fmtTs(addrPeriod.start))} to ${esc(fmtTs(addrPeriod.end))}${spanText}
                ${txCount ? ` &nbsp;&middot;&nbsp; <strong>Transactions Analyzed:</strong> ${txCount}` : ''}
            </div>`;
        }

        sectionsBuf += closeSec();
    } else {
        // Minimal fallback if no triage data
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Target Identification');
        sectionsBuf += openSec(sid, `${sectionNum}. Target Identification`)
            + `<div class="meta-box"><table class="meta-table"><tbody>
                <tr><td class="label">Address</td><td class="value mono">${monoWrap(address || '—')}</td></tr>
                <tr><td class="label">Blockchain</td><td class="value" style="text-transform:uppercase;">${esc(chain || '—')}</td></tr>
            </tbody></table></div>`
            + closeSec();
    }

    // Investigation analysis sections
    for (const { key, title, render } of SECTIONS) {
        const section = sections[key];
        if (section.status === 'idle') continue;

        sectionNum++;
        const sid = addTocEntry(sectionNum, title);
        sectionsBuf += openSec(sid, `${sectionNum}. ${title}`);

        // R-REPORT-03: Data freshness indicator
        if (section.loadedAt) {
            const loadedDate = new Date(section.loadedAt);
            const ageMs = reportGenTime - loadedDate.getTime();
            const ageHours = ageMs / (1000 * 60 * 60);
            const isStale = ageHours > 24;
            const tsStr = loadedDate.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
            sectionsBuf += `<div class="data-freshness${isStale ? ' stale' : ''}">Data retrieved: ${tsStr}${isStale ? ' ⚠ Data is >24h old — consider re-running investigation' : ''}</div>`;
        }

        if (section.status === 'loaded' && section.data) {
            // P2-6: For multi-hop exposure, also pass trace-discovered entities
            if (key === 'multi_hop_exposure') {
                const traceEntities: { name: string; type: string; direction: string; hops?: number }[] = [];
                const _extractTraceEntities = (traceData: any, direction: string) => {
                    if (!traceData) return;
                    // From formal paths
                    for (const path of (traceData.paths || traceData.traces || [])) {
                        if (path.target_entity) {
                            const tName = path.target_entity.name || path.target_entity.entity_name;
                            const tType = path.target_entity.type || path.target_entity.entity_type || 'unknown';
                            if (tName && tName !== 'Unknown') {
                                traceEntities.push({ name: tName, type: tType, direction, hops: path.hop_details?.length });
                            }
                        }
                    }
                    // From source/target_entities array
                    const entityList = direction === 'backward' ? traceData.source_entities : traceData.target_entities;
                    for (const ent of (entityList || [])) {
                        const eName = ent.entity_name || ent.name || ent.entity;
                        const eType = ent.type || ent.entity_type || 'unknown';
                        if (eName && eName !== 'Unknown') {
                            traceEntities.push({ name: eName, type: eType, direction });
                        }
                    }
                };
                if (sections.backward_trace.status === 'loaded') _extractTraceEntities(sections.backward_trace.data, 'backward');
                if (sections.forward_trace.status === 'loaded') _extractTraceEntities(sections.forward_trace.data, 'forward');
                // Deduplicate by name
                const seen = new Set<string>();
                const uniqueTraceEntities = traceEntities.filter(e => {
                    const dedupeKey = e.name.toLowerCase();
                    if (seen.has(dedupeKey)) return false;
                    seen.add(dedupeKey);
                    return true;
                });
                sectionsBuf += renderMultiHopExposure(section.data, uniqueTraceEntities);
            } else if (key === 'adaptive_transactions') {
                // Inject transaction summary from orchestrator for total count display
                const txSummary = sections.signal_assessment?.data?.transaction_summary;
                const enrichedData = txSummary ? { ...section.data, _transaction_summary: txSummary } : section.data;
                sectionsBuf += render(enrichedData, evidenceCounter);
            } else {
                sectionsBuf += render(section.data, evidenceCounter);
            }
        } else if (section.status === 'error') {
            sectionsBuf += `<div class="error-box">Data Unavailable — ${esc(section.error || 'Request failed')}</div>`;
        } else {
            // R10 + the May-2026 NOT TESTED contract: when a section is
            // unloaded, check whether the upstream signal_assessment family
            // recorded *why* (timeout, no_data, fetcher_error, ...) and
            // surface that instead of a generic "Data not available". The
            // map below ties section keys to their corresponding signal
            // family. Sections without a family fall back to the legacy
            // copy so report behaviour is unchanged for those.
            const sectionToFamily: Record<string, string> = {
                multi_hop_exposure: 'exposure_composition',
                velocity: 'behavioral_pattern',
            };
            const famKey = sectionToFamily[key];
            const families = sections.signal_assessment?.data?.signal_families as Record<string, any> | undefined;
            const fam = famKey && families ? families[famKey] : null;
            if (fam && fam.triggered === null && fam.not_tested_reason) {
                const sectionLabel = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                sectionsBuf += `<p style="color:#92400e;font-style:italic;background:#fffbeb;border:1px solid #fcd34d;padding:8px 12px;border-radius:4px;font-size:11px;">${esc(formatNotTestedReason(fam.not_tested_reason, sectionLabel))}</p>`;
            } else {
                sectionsBuf += '<p style="color:#94a3b8;font-style:italic;">Data not available at time of report generation.</p>';
            }
        }

        sectionsBuf += closeSec();
    }

    // Privacy Chain Intelligence (conditional — only for privacy chains)
    const PRIVACY_CHAINS = ['XMR', 'ZEC', 'DASH', 'PIRATE', 'FIRO'];
    if (chain && PRIVACY_CHAINS.includes(chain.toUpperCase())) {
        sectionNum++;
        const privSid = addTocEntry(sectionNum, 'Privacy Chain Intelligence');
        sectionsBuf += openSec(privSid, `${sectionNum}. Privacy Chain Intelligence`);

        const tierMap: Record<string, { tier: number; label: string; desc: string }> = {
            XMR: { tier: 1, label: 'Tier 1 — Fully Opaque', desc: 'Monero hides sender, receiver, and amounts by default. On-chain tracing is not possible without private keys.' },
            ZEC: { tier: 2, label: 'Tier 2 — Partially Traceable', desc: 'Zcash supports both transparent (t-addr, fully visible) and shielded (z-addr, hidden). Most exchange activity uses transparent addresses.' },
            DASH: { tier: 2, label: 'Tier 2 — Partially Traceable', desc: 'Dash PrivateSend is an optional CoinJoin mixing feature. Standard transactions are fully transparent like Bitcoin.' },
            PIRATE: { tier: 1, label: 'Tier 1 — Fully Opaque', desc: 'Pirate Chain enforces mandatory shielding (Sapling). All transactions are private by default.' },
            FIRO: { tier: 2, label: 'Tier 2 — Partially Traceable', desc: 'Firo uses Lelantus Spark for optional privacy. Transparent transactions are fully visible.' },
        };
        const tierInfo = tierMap[chain.toUpperCase()] || { tier: 2, label: 'Tier 2', desc: 'Privacy chain with partial traceability.' };

        sectionsBuf += `<div class="assessment-box" style="background:#1a1033;border:1px solid #7c3aed40;border-radius:6px;padding:12px;margin-bottom:12px;">`;
        sectionsBuf += `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">`;
        sectionsBuf += `<span style="font-size:14px;font-weight:700;color:#c084fc;">${esc(chain.toUpperCase())} — ${esc(tierInfo.label)}</span>`;
        sectionsBuf += `<span class="risk-badge ${tierInfo.tier === 1 ? 'badge-high' : 'badge-medium'}">${tierInfo.tier === 1 ? 'OPAQUE' : 'PARTIAL PRIVACY'}</span>`;
        sectionsBuf += `</div>`;
        sectionsBuf += `<p style="font-size:11px;color:#94a3b8;margin:0 0 8px 0;">${esc(tierInfo.desc)}</p>`;

        // Chain-specific investigation guidance
        if (chain.toUpperCase() === 'XMR') {
            sectionsBuf += `<table class="data"><thead><tr><th>Investigation Pivot</th><th>Priority</th><th>Notes</th></tr></thead><tbody>`;
            sectionsBuf += `<tr><td>Exchange subpoena (deposit/withdrawal records)</td><td><span class="risk-badge badge-high">CRITICAL</span></td><td>Request IP logs + KYC records for deposits within ±2hr window of target timestamp</td></tr>`;
            sectionsBuf += `<tr><td>Swap service records (ChangeNow, FixedFloat, Exch.cx)</td><td><span class="risk-badge badge-high">CRITICAL</span></td><td>No-KYC swap services are primary XMR on/off-ramp. Subpoena IP + order records</td></tr>`;
            sectionsBuf += `<tr><td>TX private key disclosure</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>If suspect cooperates or device seized, tx_private_key proves sender/amount</td></tr>`;
            sectionsBuf += `<tr><td>View key from exchange</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Exchange viewing key reveals all incoming transaction amounts to their wallets</td></tr>`;
            sectionsBuf += `<tr><td>Cross-chain timing correlation</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Match XMR acquisition timestamp with BTC/USDT sale on exchanges</td></tr>`;
            sectionsBuf += `<tr><td>Network-level telemetry</td><td><span class="risk-badge badge-low">MEDIUM</span></td><td>TX broadcast IP correlation (requires multi-vantage node infrastructure)</td></tr>`;
            sectionsBuf += `</tbody></table>`;
        } else if (chain.toUpperCase() === 'ZEC') {
            sectionsBuf += `<table class="data"><thead><tr><th>Investigation Pivot</th><th>Priority</th><th>Notes</th></tr></thead><tbody>`;
            sectionsBuf += `<tr><td>Transparent address analysis</td><td><span class="risk-badge badge-high">CRITICAL</span></td><td>Most ZEC exchange activity uses t-addresses — trace like Bitcoin</td></tr>`;
            sectionsBuf += `<tr><td>Z-to-T unshielding detection</td><td><span class="risk-badge badge-high">CRITICAL</span></td><td>When shielded funds exit to transparent addresses, the exit amount and destination are fully visible</td></tr>`;
            sectionsBuf += `<tr><td>Viewing key court order</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Court order exchanges to disclose viewing keys for shielded addresses under their custody</td></tr>`;
            sectionsBuf += `<tr><td>Exchange subpoena</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Request t-addr deposit/withdrawal records matching transaction timestamp</td></tr>`;
            sectionsBuf += `</tbody></table>`;
        } else if (chain.toUpperCase() === 'DASH') {
            sectionsBuf += `<table class="data"><thead><tr><th>Investigation Pivot</th><th>Priority</th><th>Notes</th></tr></thead><tbody>`;
            sectionsBuf += `<tr><td>Standard transaction tracing</td><td><span class="risk-badge badge-high">CRITICAL</span></td><td>Non-PrivateSend DASH transactions are fully transparent — trace like Bitcoin</td></tr>`;
            sectionsBuf += `<tr><td>CoinJoin round estimation</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Fewer mixing rounds = higher traceability. 1-2 rounds: ~65% addressable via timing</td></tr>`;
            sectionsBuf += `<tr><td>Denomination amount tracking</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Track distinctive non-denomination change amounts across CoinJoin transactions</td></tr>`;
            sectionsBuf += `<tr><td>Exchange subpoena</td><td><span class="risk-badge badge-medium">HIGH</span></td><td>Exchanges record DASH deposits/withdrawals with full address visibility</td></tr>`;
            sectionsBuf += `</tbody></table>`;
        }

        sectionsBuf += `</div>`;

        // Add note about limitations
        sectionsBuf += `<div class="narrative" style="border-color:#7c3aed40;background:#1a103310;">`;
        sectionsBuf += `<p style="font-size:11px;"><strong>Note:</strong> The investigation sections above (exchange detection, velocity, counterparty analysis, etc.) `;
        sectionsBuf += `were executed using available on-chain data. For ${esc(chain.toUpperCase())} privacy chain transactions, some analytical sections may have limited or no data `;
        sectionsBuf += `due to the chain's privacy features. The investigation pivots listed above should be pursued in parallel with on-chain analysis.</p>`;
        sectionsBuf += `</div>`;

        sectionsBuf += closeSec();
    }

    // Seizure & Subpoena Matrix (report §1) — the actionable seizure targets.
    const seizureHtml = renderSeizureMatrix(assetRecovery?.seizure_matrix, evidenceCounter);
    if (seizureHtml) {
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Seizure & Subpoena Matrix');
        sectionsBuf += openSec(sid, `${sectionNum}. Seizure & Subpoena Matrix`) + seizureHtml + closeSec();
    }

    // Fund-Flow Reconciliation (report §4) — hop-by-hop USD table.
    const reconHtml = renderValueReconciliation(assetRecovery?.value_reconciliation);
    if (reconHtml) {
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Fund-Flow Reconciliation');
        sectionsBuf += openSec(sid, `${sectionNum}. Fund-Flow Reconciliation (USD)`) + reconHtml + closeSec();
    }

    // Flow Diagrams (report §3) — generated from the investigation graph.
    const flowHtml = renderFlowDiagrams(assetRecovery?.flow_diagrams, assetRecovery?.flow_diagram_ir);
    if (flowHtml) {
        sectionNum++;
        const sid = addTocEntry(sectionNum, 'Flow Diagrams');
        sectionsBuf += openSec(sid, `${sectionNum}. Flow Diagrams`) + flowHtml + closeSec();
    }

    // Recommended Actions
    sectionNum++;
    const recSid = addTocEntry(sectionNum, 'Recommended Actions');
    sectionsBuf += openSec(recSid, `${sectionNum}. Recommended Actions`);
    sectionsBuf += buildRecommendedActions(triage, sections, escalation, chain);
    sectionsBuf += closeSec();

    // Methodology
    sectionNum++;
    const methSid = addTocEntry(sectionNum, 'Methodology & Data Sources');
    sectionsBuf += openSec(methSid, `${sectionNum}. Methodology & Data Sources`);
    const providerHealth = sections?.signal_assessment?.data?.provider_health || null;
    sectionsBuf += renderMethodology(chain, entityCount, providerHealth, sections?.signal_assessment?.data || null, describeStepsRun(sections as any, sections?.signal_assessment?.data?.coverage));
    sectionsBuf += closeSec();

    // Append all sections into main column (TOC rail built below)
    let mainBuf = sectionsBuf;

    // Appendices — full raw data tables
    const appendixHtml = renderAppendices(sections);
    if (appendixHtml) {
        mainBuf += openSec('appendices', 'Appendices — Complete Investigation Data')
            + appendixHtml
            + closeSec();
    }

    // Circulation & Pass-Through corridors (cycle_detector) — own appendix.
    mainBuf += renderCirculationReport(circulation);

    // Section 65B Certificate Template (Indian Evidence Act)
    mainBuf += `<div id="section-65b">` + renderSection65BCertificate(chain, address, now, evidenceCounter.count, sections?.signal_assessment?.data?.provider_health || null, describeStepsRun(sections as any, sections?.signal_assessment?.data?.coverage)) + `</div>`;

    // Disclaimer
    mainBuf += buildReportDisclaimer();

    const tocRail: ReportTocEntry[] = [
        ...tocEntries.map((e) => ({ id: e.id, label: `${e.num}. ${e.title}` })),
    ];
    if (appendixHtml) tocRail.push({ id: 'appendices', label: 'Appendices' });
    tocRail.push({ id: 'section-65b', label: 'Electronic Evidence Certificate' });

    const pageInner = preamble
        + buildReportMasthead({
            title: 'Cryptocurrency Investigation Report',
            subtitle: 'Confidential — Law Enforcement Use Only',
            dataMode: dataMode || undefined,
        })
        + assessmentHtml
        + mainBuf;

    return wrapReportHtml(
        buildReportToolbar(`Investigation Report — ${(address || '').slice(0, 12)}...`, safeFilename)
        + (isDemo ? `<div class="demo-watermark">DEMO DATA — NOT FOR LEGAL USE (source: ${esc(dataMode || 'mock')})</div>` : '')
        + buildReportShell(pageInner, tocRail),
        `Investigation Report — ${address || 'Unknown'}`,
        INVESTIGATION_EXTRA_CSS,
    );
}

export { openReportHtmlInNewTab };

/* ── Public API ── */

export function openReportInNewTab(
    chain: string | null,
    address: string | null,
    sections: InvestigationSections,
    triage?: any,
    dataMode?: 'mock' | 'cached' | 'live',
    entityCount?: number,
): void {
    const html = generateInvestigationReportHtml(chain, address, sections, triage, dataMode, entityCount);
    openReportHtmlInNewTab(html);
}

/* ── R-REPORT-20: One-Page Summary ── */

/**
 * Generates a standalone one-page summary HTML document.
 * Designed for quick review — contains subject address, risk verdict,
 * key finding, recommended action, case reference, and timestamp.
 * Can be opened in a new tab or saved separately from the full report.
 */
export function generateOnePageSummaryHtml(
    chain: string | null,
    address: string | null,
    sections: InvestigationSections,
    triage?: any,
    caseReference?: string,
): string {
    const now = new Date();

    // Derive key facts
    const canonicalRisk = resolveCanonicalRisk(sections, triage);
    const riskScore = canonicalRisk.score;
    const riskLevel = canonicalRisk.level;
    const sanctioned = triage?.summary?.sanctioned;
    const entityName = triage?.summary?.entity_name && triage.summary.entity_name !== 'Unknown'
        ? cleanEntityName(triage.summary.entity_name) : null;

    const sa = sections.signal_assessment;
    const escalation = sa.status === 'loaded' ? sa.data?.escalation_recommendation : null;

    const vel = sections.velocity;
    const velocityProfile = vel.status === 'loaded' ? vel.data?.velocity_profile : null;

    // Key finding: prioritize sanctions > exchange destination > mixer > velocity
    let keyFinding = 'No critical findings identified.';
    if (sanctioned) {
        keyFinding = `OFAC SDN sanctions match detected — ${esc(triage?.summary?.sanctions_entity || 'sanctioned entity')}.`;
    } else {
        // Check for exchange in backward/forward trace
        const bt = sections.backward_trace;
        const ft = sections.forward_trace;
        const btPaths = bt.status === 'loaded' ? (bt.data?.paths || bt.data?.traces || []) : [];
        const ftPaths = ft.status === 'loaded' ? (ft.data?.paths || ft.data?.traces || []) : [];
        const allPaths = [...btPaths, ...ftPaths];

        let exchangeFound: string | null = null;
        let mixerFound = false;

        for (const path of allPaths) {
            const ent = path.target_entity || {};
            const entName = ent.name || ent.entity_name || path.entity_name || '';
            const entType = (ent.type || ent.entity_type || path.entity_type || '').toLowerCase();
            if (entType === 'exchange' || /exchange|binance|coinbase|kraken|bybit|kucoin|huobi/i.test(entName)) {
                exchangeFound = cleanEntityName(entName);
            }
            if (entType === 'mixer' || /mixer|tornado|wasabi/i.test(entName)) {
                mixerFound = true;
            }
        }

        // Also check triage nearest exchange
        if (!exchangeFound && triage?.summary?.nearest_exchange?.name) {
            exchangeFound = cleanEntityName(triage.summary.nearest_exchange.name);
        }

        if (mixerFound) {
            keyFinding = 'Fund flow trace reveals interaction with cryptocurrency mixing service — potential money laundering indicator.';
        } else if (exchangeFound) {
            keyFinding = `Fund flow trace identifies ${esc(exchangeFound)} as a connected exchange — KYC records obtainable via ${NOTICE_STATUTE} notice.`;
        } else if (velocityProfile === 'Relay') {
            keyFinding = 'Address exhibits relay behavior — funds pass through rapidly with minimal dwell time, consistent with intermediary usage.';
        } else if (escalation === 'ESCALATE') {
            keyFinding = 'Multi-signal convergence analysis triggers escalation recommendation — multiple independent risk indicators detected.';
        }
    }

    // Recommended action
    let recommendedAction = 'Continue monitoring and cross-reference with active case database.';
    if (sanctioned) {
        recommendedAction = 'Escalate immediately to senior officer — sanctions match requires compliance review.';
    } else if (escalation === 'ESCALATE') {
        recommendedAction = 'Preserve graph state, attach to active case file, and escalate for senior review.';
    } else if (triage?.summary?.nearest_exchange?.name) {
        recommendedAction = `Issue ${NOTICE_STATUTE} notice to ${esc(cleanEntityName(triage.summary.nearest_exchange.name))} for KYC and transaction records.`;
    } else if (escalation === 'INVESTIGATE') {
        recommendedAction = 'Conduct further investigation to clarify risk profile before escalation.';
    }

    // Risk badge color
    const riskColor = typeof riskScore === 'number'
        ? (riskScore >= 0.7 ? '#dc2626' : riskScore >= 0.4 ? '#f59e0b' : '#22c55e')
        : '#94a3b8';
    const riskBg = typeof riskScore === 'number'
        ? (riskScore >= 0.7 ? '#fef2f2' : riskScore >= 0.4 ? '#fffbeb' : '#ecfdf5')
        : '#f8fafc';

    const summaryCSS = `
.summary-row { display: flex; margin-bottom: 12px; align-items: baseline; }
.summary-label { font-weight: 700; color: #475569; width: 160px; flex-shrink: 0; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
.summary-value { color: #0f172a; font-size: 13px; flex: 1; }
.risk-verdict {
    display: inline-block; padding: 6px 16px; border-radius: 4px;
    font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;
}
.key-finding {
    background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #2563eb;
    border-radius: 4px; padding: 12px 16px; margin: 16px 0; font-size: 13px; line-height: 1.7; color: #334155;
}
.action-box {
    background: #fffbeb; border: 1px solid #fcd34d; border-radius: 4px;
    padding: 12px 16px; margin: 16px 0; font-size: 12px; color: #92400e;
}
.summary-footer { margin-top: 24px; border-top: 1px solid #e2e8f0; padding-top: 12px; text-align: center; font-size: 10px; color: #94a3b8; }
`;

    const safeFilename = `investigation_summary_${(chain || 'unknown').toUpperCase()}_${(address || 'unknown').slice(0, 12)}_${now.toISOString().slice(0, 10)}`.replace(/[^a-zA-Z0-9_\-]/g, '_');

    let main = buildReportMasthead({
        title: 'Investigation Summary',
        subtitle: 'Confidential — Law Enforcement Use Only',
    });

    main += `
    <div class="summary-row">
        <span class="summary-label">Subject Address</span>
        <span class="summary-value mono">${monoWrap(address || '—')}</span>
    </div>
    <div class="summary-row">
        <span class="summary-label">Blockchain</span>
        <span class="summary-value">${esc((chain || '—').toUpperCase())}</span>
    </div>`;

    if (entityName) {
        main += `
    <div class="summary-row">
        <span class="summary-label">Entity</span>
        <span class="summary-value"><strong>${esc(entityName)}</strong></span>
    </div>`;
    }

    main += `
    <div class="summary-row">
        <span class="summary-label">Risk Verdict</span>
        <span class="summary-value">
            <span class="risk-verdict" style="background:${riskBg};color:${riskColor};border:2px solid ${riskColor};">
                ${typeof riskScore === 'number' ? `${(riskScore * 100).toFixed(0)}% — ${esc(riskLevel)}` : esc(riskLevel)}
            </span>
        </span>
    </div>`;

    if (escalation) {
        const escColor = escalation === 'ESCALATE' ? '#dc2626'
            : escalation === 'INVESTIGATE' ? '#f59e0b'
            : escalation === 'INSUFFICIENT_DATA' ? '#94a3b8' : '#64748b';
        main += `
    <div class="summary-row">
        <span class="summary-label">Escalation</span>
        <span class="summary-value"><strong style="color:${escColor};">${esc(escalation.replace(/_/g, ' '))}</strong></span>
    </div>`;
    }

    if (velocityProfile) {
        main += `
    <div class="summary-row">
        <span class="summary-label">Velocity Profile</span>
        <span class="summary-value"><strong>${esc(velocityProfile)}</strong></span>
    </div>`;
    }

    if (sanctioned) {
        main += `
    <div class="summary-row">
        <span class="summary-label">Sanctions</span>
        <span class="summary-value"><strong style="color:#dc2626;">OFAC SDN HIT — ${esc(triage?.summary?.sanctions_entity || 'Match found')}</strong></span>
    </div>`;
    }

    main += `
    <div class="key-finding">
        <strong>Key Finding:</strong> ${keyFinding}
    </div>
    <div class="action-box">
        <strong>Recommended Action:</strong> ${esc(recommendedAction)}
    </div>`;

    if (caseReference) {
        main += `
    <div class="summary-row">
        <span class="summary-label">Case Reference</span>
        <span class="summary-value mono">${esc(caseReference)}</span>
    </div>`;
    }

    main += `
    <div class="summary-footer" data-testid="outris-report-footer">
        Powered by Outris  ·  © 2026<br/>
        Outris · Crypto Investigation Platform — summary only; see the full report for evidence and methodology.
    </div>`;

    return wrapReportHtml(
        buildReportToolbar(`Investigation Summary — ${(address || '').slice(0, 12)}...`, safeFilename)
        + buildReportShell(main),
        `Investigation Summary — ${address || 'Unknown'}`,
        summaryCSS,
    );
}
